import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { BusinessKind, BusinessStatus } from "@/lib/admin-labels";

export type Lang = "cs" | "pl" | "en" | "es" | "de" | "pt" | "fr" | "hr" | "sk" | "ro" | "it" | "hu" | "bg";
const STORAGE_KEY = "lc_lang";
const BUILD_ID_KEY = "lc_i18n_build";
// Injected by Vite `define` at build time (see vite.config.ts). Falls back to
// "dev" when running under Vitest / SSR nodes that don't inject the constant.
declare const __BUILD_ID__: string;
const CURRENT_BUILD_ID: string =
  typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

type Dict = Record<string, string>;

const cs: Dict = {
  // Nav
  "nav.home": "Domů",
  "nav.back": "Zpět",
  "nav.katalog": "Katalog",
  "nav.mapa": "Mapa",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Můj panel",
  "nav.proMajitele": "Pro majitele",
  "nav.admin": "Admin",
  "nav.cenik": "Ceník",
  "nav.mujUcet": "Můj účet",
  "nav.odhlasit": "Odhlásit",
  "nav.prihlasit": "Přihlásit se",
  "nav.vakanty": "Majitelé Vacantů",

  // Photos
  "photos.title": "Fotky",
  "photos.add": "Přidat fotku",
  "photos.maxSize": "Max. 3 MB / fotka. První 3 fotky zdarma, další: {price} jednorázově.",
  "photos.tooLarge": "Fotka přesahuje 3 MB.",
  "photos.invalidType": "Soubor musí být obrázek.",
  "photos.uploadFailed": "Nahrání se nezdařilo: {msg}",
  "photos.freeAdded": "Fotka byla přidána zdarma.",
  "photos.paidPending": "Fotka vytvořena, čeká na úhradu.",
  "photos.deleted": "Fotka byla odstraněna.",
  "photos.priceLabel": "Cena za další fotku",

  // Index hero
  "home.title.a": "Objevte chutě světa na",
  "home.title.b": "jednom místě",
  "home.subtitle": "Od rodinných bister v Praze po zapadlé bary v Barceloně. Globální průvodce pro milovníky autentické gastronomie.",
  "home.search.what": "Co hledáte? (např. Tapas)",
  "home.search.where": "Kde? (např. Praha)",
  "home.search.submit": "Hledat",

  // Kategorie
  "cat.restaurace": "Restaurace",
  "cat.bary": "Bary",
  "cat.kavarny": "Kavárny",
  "cat.cajovny": "Čajovny",
  "cat.stanky": "Stánky",
  "cat.jidelny": "Jídelny",
  "cat.mlecnyBar": "Mléčné bary",
  "cat.foodTruck": "Food trucky",
  "cat.rychleObcerstveni": "Fast Food",
  "cat.bistra": "Bistra",

  // Mapa CTA
  "home.mapaCta": "Prozkoumat mapu světa",
  "home.latest.title": "Nejnovější podniky",
  "home.latest.sub": "Naposledy přidané schválené podniky.",
  "home.latest.empty": "Zatím žádné schválené podniky.",
  "home.latest.viewAll": "Zobrazit na mapě →",

  // Doporučené
  "home.featured.title": "Doporučené podniky",
  "home.featured.sub": "Ty nejlépe hodnocené skvosty v naší komunitě.",
  "home.featured.all": "Zobrazit vše",

  // Direct Club
  "dc.tag": "Věrnostní program",
  "dc.title": "Staňte se členem Direct Clubu",
  "dc.text": "Získávejte bonusové body za každou návštěvu, využívejte exkluzivní slevy a objevujte speciální menu dostupné pouze pro členy sítě Cerveza.International.",
  "dc.register": "Registrovat se zdarma",
  "dc.how": "Jak to funguje?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Číslo karty",
  "dc.points": "Nasbírané body",

  // CTA majitele
  "owner.title": "Vlastníte gastro podnik?",
  "owner.text": "Přidejte se k tisícům podniků a oslovte hosty z celého světa. Registrace je rychlá a zdarma.",
  "owner.cta": "Připojit svůj podnik",

  // Footer
  "footer.rights": "Všechna práva vyhrazena",
  "footer.about": "O nás",
  "footer.terms": "Podmínky",
  "footer.privacy": "Ochrana údajů",
  "footer.contact": "Kontakt",

  // Auth
  "auth.signIn": "Přihlášení",
  "auth.signUp": "Registrace",
  "auth.tabSignIn": "Přihlásit se",
  "auth.tabSignUp": "Vytvořit účet",
  "auth.google": "Pokračovat přes Google",
  "auth.apple": "Pokračovat přes Apple",
  "auth.or": "nebo",
  "auth.email": "Email",
  "auth.password": "Heslo",
  "auth.passwordMin": "Heslo (min. 6 znaků)",
  "auth.showPassword": "Zobrazit heslo",
  "auth.hidePassword": "Skrýt heslo",
  "auth.name": "Jméno a příjmení",
  "auth.submitSignIn": "Přihlásit se",
  "auth.submitSignInLoading": "Přihlašuji…",
  "auth.submitSignUp": "Vytvořit účet",
  "auth.submitSignUpLoading": "Registruji…",
  "auth.firstAdminNote": "První registrovaný účet se automaticky stane správcem systému.",
  "auth.back": "← Zpět na web",
  "auth.welcomeBack": "Vítejte zpět!",
  "auth.created": "Účet vytvořen.",
  "auth.oauthFailed": "Přihlášení selhalo",

  // Mapa
  "mapa.tag": "Mapa",
  "mapa.loading": "Načítám podniky…",
  "mapa.count": "schválených podniků",
  "mapa.kindHeader": "Typ podniku",
  "mapa.addBusiness": "Přidat podnik",
  "mapa.showUsers": "Členové Direct Clubu",
  "mapa.showAnon": "Hosté",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> přispěvatelé",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "Žádné výsledky",
  "mapa.audit.title": "Audit mapy vs. databáze",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "Vše sedí",
  "mapa.audit.mismatch": "Nesoulad",
  "mapa.audit.rendered": "Zobrazeno na mapě (klient)",
  "mapa.audit.expected": "Mělo by se zobrazit (DB)",
  "mapa.audit.breweries": "Pivovary (varny)",
  "mapa.audit.venues": "Podniky (katalog)",
  "mapa.audit.total": "Celkem",
  "mapa.audit.published": "Zveřejněné",
  "mapa.audit.publishedGps": "Zveřejněné + GPS",
  "mapa.audit.unpublished": "Nezveřejněné",
  "mapa.audit.noGps": "Zveřejněné bez GPS",
  "mapa.audit.missingHeader": "Chybí na mapě",
  "mapa.audit.missingNone": "Žádné — všechny zveřejněné záznamy s GPS jsou v mapovém feedu.",
  "mapa.audit.unnamed": "(bez názvu)",
  "mapa.audit.rowVenue": "podnik",
  "mapa.audit.rowBrewery": "pivovar",
  "mapa.audit.showingFirst": "… zobrazeno prvních 200 z {n}",
  "mapa.coverage.title": "Pokrytí zemí",
  "mapa.coverage.badge": "Země",
  "mapa.coverage.published": "Zveřejněno",
  "mapa.coverage.imported": "Naimportováno, nezveřejněno",
  "mapa.coverage.notImported": "Neimportováno",
  "mapa.coverage.tabAll": "Vše",
  "mapa.coverage.tabPublished": "Zveřejněno",
  "mapa.coverage.tabImported": "Nezveřejněno",
  "mapa.coverage.tabMissing": "Chybí",
  "mapa.coverage.thCountry": "Země",
  "mapa.coverage.thPublic": "Publik.",
  "mapa.coverage.thImported": "Import.",
  "mapa.coverage.thStatus": "Stav",
  "mapa.coverage.statusPublished": "Publikováno",
  "mapa.coverage.statusImported": "Nezveřejněno",
  "mapa.coverage.statusMissing": "Neimportováno",
  "mapa.coverage.empty": "Žádné země v této kategorii.",
  "mapa.coverage.footer": "Publik. = počet zveřejněných provozoven s GPS na mapě · Import. = celkem záznamů v katalogu.",
  "mapa.catalog.venue": "Podnik z katalogu",
  "mapa.catalog.brewery": "Pivovar z katalogu",
  "mapa.catalog.imported": "Importováno z XLSX katalogu Cerveza Data Standard",
  "mapa.catalog.detail": "Detail",
  "mapa.review.discovery": "Neověřeno",
  "mapa.review.pending": "K ověření",
  "mapa.review.verified": "Ověřeno",
  "mapa.review.rejected": "Zamítnuto",
  "mapa.continent.europe": "Evropa",
  "mapa.continent.northAmerica": "Severní Amerika",
  "mapa.continent.southAmerica": "Jižní Amerika",
  "mapa.continent.asia": "Asie",
  "mapa.continent.africa": "Afrika",
  "mapa.continent.oceania": "Oceánie",
  "mapa.continent.other": "Ostatní",
  "mapa.memberBadge": "Člen Direct Clubu",
  "mapa.partnerHint": "Ověřená partnerská firma registrovaná v Cerveza.International",
  "mapa.memberPrivacyNote": "Registrovaný uživatel · přibližná pozice pro ochranu soukromí",
  "mapa.anonPrivacyNote": "Nepřihlášený návštěvník · pozice se zobrazuje 24 h, poté mizí",
  "mapa.legend.title": "Legenda",
  "mapa.userPin.title": "Registrovaný člen Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Přibližná poloha (bez přesné adresy)",
  "mapa.viewProfile": "Profil podniku",
  "mapa.empty.a": "Zatím žádný schválený podnik. Buďte první —",
  "mapa.empty.b": "zaregistrujte podnik",
  "mapa.anonPin.title": "Anonymní návštěvník",
  "mapa.anonPin.subtitle": "Aktivní v posledních 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtry",
  "mapa.filters.search": "Hledat",
  "mapa.filters.searchPlaceholder": "Název, město, popis…",
  "mapa.filters.location": "Lokalita",
  "mapa.filters.continent": "Kontinent",
  "mapa.filters.allContinents": "Všechny kontinenty",
  "mapa.filters.country": "Stát",
  "mapa.filters.allCountries": "Všechny státy",
  "mapa.filters.region": "Region / stát",
  "mapa.filters.regionEmpty": "Region (zatím prázdné)",
  "mapa.filters.allRegions": "Všechny regiony",
  "mapa.filters.city": "Město",
  "mapa.filters.allCities": "Všechna města",
  "mapa.filters.postal": "PSČ",
  "mapa.filters.partnership": "Partnerství",
  "mapa.filters.reset": "Resetovat filtry",
  "mapa.empty.filtered": "Filtru neodpovídá žádný podnik.",
  "mapa.empty.resetFilters": "Resetovat filtry",
  "mapa.location.missing": "Poloha chybí",
  "mapa.location.stale": "Poloha zastaralá",
  "mapa.location.fresh": "Poloha čerstvá",
  "mapa.location.refresh": "Obnovit polohu",
  "mapa.location.lastSeen": "Naposledy",
  "mapa.location.hidden": "skrytá na mapě",
  "mapa.location.never": "nikdy",
  "mapa.location.toastOk": "Poloha aktualizována",
  "mapa.location.toastCached": "Použita čerstvá uložená hodnota.",
  "mapa.location.toastSource": "Zdroj",
  "mapa.location.toastFail": "Polohu se nepodařilo zjistit",
  "mapa.location.toastFailDesc": "Zkuste to prosím znovu později (nedostupné geo informace).",
  "mapa.location.toastError": "Chyba při obnově polohy",
  "mapa.breweries": "Pivovary",
  "mapa.breweriesHint": "Pivovary a výrobci piva",
  "mapa.showMembersNoBusiness": "Členové bez podniku",
  "mapa.memberNoBusiness.badge": "Člen bez podniku",
  "mapa.memberNoBusiness.title": "Člen připravený budovat síť",
  "mapa.memberNoBusiness.subtitle": "Může doporučit nové členy, podniky i hodnotit navštívená místa.",
  "mapa.memberNoBusiness.recommendBusiness": "Doporučit podnik",
  "mapa.memberNoBusiness.inviteMember": "Doporučit člena",
  "mapa.memberNoBusiness.rateVisited": "Ohodnotit navštívený podnik",
  "mapa.memberNoBusiness.privacy": "Přibližná pozice pro ochranu soukromí",
  "mapa.memberNoBusiness.hint": "Registrovaní členové, kteří zatím nevlastní žádný podnik — ideální kandidáti pro doporučení",

  // Moje podniky
  "my.title": "Moje podniky",
  "my.sub": "Spravujte své podniky a jejich odkazy.",
  "my.add": "Přidat podnik",
  "my.loading": "Načítám…",
  "my.empty.a": "Zatím nemáte žádné podniky.",
  "my.empty.b": "Zaregistrovat první",
  "my.links": "Odkazy",
  "my.noLinks": "Žádné odkazy.",
  "my.free": "Zdarma",
  "my.paid": "Placený",
  "my.active": "Aktivní",
  "my.rejected": "Zamítnutý",
  "my.pendingPayment": "Čeká na platbu",
  "my.pay": "Zaplatit",
  "my.payTooltip": "Online platba bude brzy dostupná",
  "my.addLink": "Přidat odkaz",
  "my.linkInfo": "Odkazy na <strong>directclub.digital</strong> / <strong>direct.club</strong> jsou zdarma (max. 3 na podnik). Ostatní externí odkazy: <strong>{price}</strong> jednorázově (trvalý odkaz po úhradě).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Popisek (volitelné)",
  "my.addFree": "Přidat zdarma",
  "my.addPaid": "Přidat za {price}",
  "my.freeAdded": "Odkaz zdarma byl přidán a publikován.",
  "my.paidPending": "Odkaz vytvořen, čeká na úhradu.",
  "my.freeLimit": "Limit 3 bezplatných odkazů na directclub byl vyčerpán.",
  "my.willBeFree": "✓ Odkaz na directclub — bude zdarma a publikován okamžitě.",
  "my.willBePaid": "Externí odkaz — vyžaduje úhradu {price} (publikace po platbě).",
  "my.invalidUrl": "Neplatná URL adresa",

  // Registrace podniku
  "reg.back": "← Zpět na web",
  "reg.moderation": "Hybridní moderace",
  "reg.title": "Registrace podniku",
  "reg.desc": "Po zadání PSČ automaticky doplníme obec a souřadnice z databáze PSČ. Ověřené majitele schvalujeme automaticky, ostatní záznamy projdou moderací.",
  "reg.name": "Název podniku",
  "reg.namePlaceholder": "např. Café Vinohrady",
  "reg.kind": "Typ podniku",
  "reg.kindPlaceholder": "Vyberte typ",
  "reg.country": "Země",
  "reg.psc": "PSČ",
  "reg.pscPlaceholder": "např. 120 00",
  "reg.pscInvalid": "Neplatný formát PSČ (CZ: 5 číslic).",
  "reg.searching": "Hledám obec…",
  "reg.notFound": "Pro PSČ {postal} jsme v databázi nenašli žádnou obec.",
  "reg.foundOne": "Nalezena obec:",
  "reg.foundMany": "Nalezeno {count} obcí — vyberte tu správnou:",
  "reg.coords": "Souřadnice:",
  "reg.mapPreview": "Náhled polohy na mapě",
  "reg.mapHint": "Tažením špendlíku nebo kliknutím do mapy můžete polohu upřesnit.",
  "reg.street": "Ulice a č.p. (volitelné)",
  "reg.streetPlaceholder": "např. Korunní 12",
  "reg.descLabel": "Krátký popis (volitelné)",
  "reg.descMultiHint": "Můžete vyplnit popis v jednom nebo více jazycích. Návštěvníkům se zobrazí ve zvoleném jazyce, pokud chybí, použije se jiný dostupný.",
  "reg.descPlaceholder": "Čím je váš podnik výjimečný?",
  "reg.cancel": "Zrušit",
  "reg.submit": "Odeslat k registraci",
  "reg.submitted": "Podnik byl odeslán k moderaci.",
  "reg.notSignedIn": "Nejste přihlášen",
  "reg.pickCity": "Vyberte platnou obec z výsledků PSČ",
  "reg.invalidData": "Neplatné údaje",
  "reg.gate.title": "Potvrzení vlastnictví podniku",
  "reg.gate.desc": "Registrovat podnik může pouze jeho majitel nebo provozovatel. Vyberte 1–2 kategorie, do kterých váš podnik patří.",
  "reg.gate.confirmBefore": "Potvrzuji, že jsem",
  "reg.gate.confirmStrong": "majitel nebo provozovatel",
  "reg.gate.confirmAfter": "podniku, který chci zaregistrovat, a podnik patří do některé z níže uvedených kategorií.",
  "reg.gate.kindsLabel": "Kategorie podniku (vyberte 1–2)",
  "reg.gate.selected": "Vybráno: {count} / 2",
  "reg.continue": "Pokračovat",

  // Kinds
  "kind.restaurace": "Restaurace",
  "kind.bar": "Bar",
  "kind.kavarna": "Kavárna",
  "kind.cajovna": "Čajovna",
  "kind.stanek": "Stánek",
  "kind.jidelna": "Jídelna",
  "kind.mlecny_bar": "Mléčný bar",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistro",

  // Statuses
  "status.pending": "Čeká na schválení",
  "status.approved": "Schváleno",
  "status.rejected": "Zamítnuto",
  "status.hidden": "Skryto",

  // Language switcher
  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "link.promo": "{months} měs. zdarma do {date}",
  "auth.checkEmail": "Podívejte se do své e-mailové schránky a potvrďte registraci kliknutím na odkaz, který jsme vám poslali.",
  "vakanty.title": "Můj dashboard",
  "vakanty.welcomeWithName": "Vítejte, {name}. Níže vidíte počet Vacantů, které máte ve svém účtu.",
  "vakanty.welcomeNoName": "Vítejte. Níže vidíte počet Vacantů, které máte ve svém účtu.",
  "vakanty.purchasedLabel": "Zakoupené Vacanty",
  "vakanty.emptyHint": "Zatím nemáte žádné Vacanty. O navýšení požádejte administrátora.",
  "vakanty.featuresTitle": "Funkce a informace",
  "vakanty.kindFeature": "Funkce",
  "vakanty.kindInfo": "Informace",
  "vakanty.open": "Otevřít",
 "vakanty.loading": "Načítám…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Interní dokument dostupný výhradně majitelům Vacantů.",
 "vakanty.bp.strictHeader": "Přísně důvěrné (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Tento dokument je přísně důvěrný a představuje obchodní tajemství společnosti For Direct s.r.o.. Dokument nesmí být stahován, přeposílán, kopírován, sdílen, publikován ani jinak distribuován žádné třetí straně — a to ani jeho části, snímky obrazovky, fotografie ani přepisy. Dokument je dostupný pouze k online prohlížení na této stránce a výhradně přihlášenému majiteli Vacantu. Jakékoli neoprávněné použití, reprodukce nebo šíření je porušením smluvních povinností a může vést k občanskoprávnímu i trestněprávnímu postihu. Porušení této dohody o mlčenlivosti je sankcionováno smluvní pokutou ve výši 5 000 000 EUR (slovy: pět milionů eur) za každý jednotlivý případ porušení.",
 "vakanty.bp.paragraph2": "Zaškrtnutím níže potvrzujete, že jste tato pravidla přečetl(a), rozumíte jim a zavazujete se je dodržovat. Váš souhlas bude zaznamenán s časovým razítkem.",
 "vakanty.bp.acceptedOn": "NDA přijato dne {date}.",
 "vakanty.bp.checkboxLabel": "Prohlašuji, že jsem si přečetl(a) podmínky mlčenlivosti výše, souhlasím s nimi a zavazuji se dokument nesdílet ani nedistribuovat.",
 "vakanty.bp.acceptButton": "Přijmout NDA a zobrazit",
 "vakanty.bp.showButton": "Zobrazit Business Plan",
 "vakanty.bp.hideButton": "Skrýt dokument",
 "vakanty.bp.viewerNotice": "Přísně důvěrné — nekopírovat, nesdílet, nestahovat. Prohlížení výhradně na této stránce.",
 "vakanty.bp.mustCheckFirst": "Nejprve zaškrtněte souhlas s NDA.",
 "vakanty.bp.acceptedToast": "NDA přijato. Nyní si můžete dokument prohlédnout.",
  "common.error": "Chyba",
  "common.tryAgain": "Zkusit znovu",
  "common.goHome": "Zpět na hlavní stránku",
  "common.pageNotFound": "Stránka nenalezena",
  "common.pageNotFoundDesc": "Stránku, kterou hledáte, se nepodařilo najít nebo byla přesunuta.",
  "common.errorTitle": "Tato stránka se nenačetla",
  "common.errorDesc": "Něco se nepovedlo. Zkuste stránku obnovit nebo se vraťte na úvod.",
  "auth.enterEmailFirst": "Zadejte nejprve e-mail.",
  "auth.resendResult": "Pokud účet existuje a není potvrzený, odeslali jsme nový odkaz. Zbývá pokusů: {remaining}.",
  "auth.resendFailed": "Nepodařilo se odeslat e-mail.",
  "auth.emailAlreadyRegistered": "Tento e-mail je již zaregistrovaný. Zkontrolujte schránku pro potvrzovací odkaz nebo se přihlaste.",
  "auth.resending": "Odesílám…",
  "auth.resendConfirmation": "Znovu poslat potvrzovací e-mail",
  "auth.signInDesc": "Přihlaste se nebo se zaregistrujte do Cerveza.International.",
  "auth.pageTitle": "Přihlášení — Cerveza.International",
  "authReset.pageTitle": "Nastavení hesla — Cerveza.International",
  "authReset.title": "Nastavení nového hesla",
  "authReset.needLink": "Otevřete prosím tuto stránku z odkazu v e-mailu, který jste obdrželi.",
  "authReset.newPassword": "Nové heslo",
  "authReset.passwordAgain": "Heslo znovu",
  "authReset.saving": "Ukládám…",
  "authReset.save": "Uložit heslo",
  "authReset.tooShort": "Heslo musí mít alespoň 8 znaků.",
  "authReset.mismatch": "Hesla se neshodují.",
  "authReset.saved": "Heslo nastaveno.",
  "unsub.pageTitle": "Odhlášení odběru — Cerveza.International",
  "unsub.title": "Odhlášení odběru",
  "unsub.checking": "Ověřuji odkaz…",
  "unsub.confirmPrompt": "Potvrďte, že se chcete odhlásit z odběru e-mailů od Cerveza.International.",
  "unsub.confirmBtn": "Potvrdit odhlášení",
  "unsub.processing": "Odhlašuji…",
  "unsub.done": "Hotovo. Byli jste úspěšně odhlášeni z odběru.",
  "unsub.already": "Tato adresa je již odhlášena.",
  "unsub.invalid": "Odkaz je neplatný nebo vypršel.",
  "unsub.errorVerify": "Nepodařilo se ověřit odkaz.",
  "unsub.errorGeneric": "Něco se nepovedlo.",
  "unsub.errorNetwork": "Síťová chyba.",
  "pay.successPageTitle": "Platba úspěšná — Cerveza.International",
  "pay.successTitle": "Platba proběhla úspěšně",
  "pay.successDesc": "Děkujeme. Vaše objednávka se zpracovává — odkaz nebo fotografie se aktivuje během několika sekund po obdržení potvrzení.",
  "pay.backToMy": "Zpět na Moje podniky",
  "pay.cancelPageTitle": "Platba zrušena — Cerveza.International",
  "pay.cancelTitle": "Platba zrušena",
  "pay.cancelDesc": "Žádná částka nebyla stržena. Platbu můžete kdykoli zopakovat ze své stránky Moje podniky.",
  "profile.errorTitle": "Chyba",
  "profile.notFound": "Podnik nenalezen",
  "profile.backToMap": "Zpět na mapu",
  "profile.noDescription": "Zatím žádný popis. Přidejte ho v jazycích, ve kterých chcete podnik prezentovat.",
  "profile.editDescription": "Upravit popis",
  "profile.multiLangDesc": "Vícejazyčný popis",
  "profile.maxChars": "Max {n} znaků na jazyk",
  "profile.descriptionsSaved": "Popisy uloženy",
  "profile.saveFailed": "Uložení selhalo",
  "profile.save": "Uložit",
  "profile.cancel": "Zrušit",
  "profile.linksHeader": "Odkazy",
  "profile.viewOnOSM": "Zobrazit na OpenStreetMap",
  "profile.descPlaceholder": "Popis ({lang})",
  "reg.pageTitle": "Registrace podniku — Cerveza.International",
  "reg.pageDesc": "Zaregistrujte svůj podnik do globálního katalogu Cerveza.International.",
  "reg.maxTwoKinds": "Lze vybrat maximálně 2 kategorie.",
  "reg.confirmedKinds": "Potvrzené kategorie: {list}",
  "share.warnFallback": "Pozor: šablona není přeložena do „{lang}\", odesílá se český originál.",
  "share.copiedMsg": "Zpráva zkopírována — vlož ji do požadované aplikace.",
  "share.copyFailed": "Kopírování selhalo.",
  "share.copiedLink": "Odkaz zkopírován.",
  "share.copyLink": "Kopírovat odkaz",
  "share.copyMessage": "Kopírovat zprávu",
  "my.photoUploadedPending": "Fotka nahrána, čeká na schválení adminem.",
  "my.photoPendingApproval": "čeká na schválení",
  "my.rejectReason": "Důvod zamítnutí: {reason}",
  "my.rejectedShort": "zamítnuto",
  "my.freeOutOf": "{free}/3 zdarma · další: {price}",
  "my.photoPack": "Foto balíček:",
  "my.pieces": "ks",
  "majitele.pageTitle": "Majitelé Vacantů — Cerveza.International",

  // Downloads section
  "downloads.title": "Ke stažení",
  "downloads.subtitle": "Manuály a průvodce ke stažení.",
  "downloads.vakantOnly": "Jen pro majitele Vacantů",
  "downloads.signInToView": "Přihlaste se pro zobrazení",
  "downloads.downloadPdf": "Stáhnout PDF",
  "downloads.empty": "Zatím zde nejsou žádné manuály.",

};
const pl: Dict = {
  "nav.home": "Strona główna",
  "nav.back": "Wstecz",
  "nav.katalog": "Katalog",
  "nav.mapa": "Mapa",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Mój panel",
  "nav.proMajitele": "Dla właścicieli",
  "nav.admin": "Admin",
  "nav.cenik": "Cennik",
  "nav.mujUcet": "Moje konto",
  "nav.odhlasit": "Wyloguj",
  "nav.prihlasit": "Zaloguj się",
  "nav.vakanty": "Właściciele Vacantów",

  "photos.title": "Zdjęcia",
  "photos.add": "Dodaj zdjęcie",
  "photos.maxSize": "Maks. 3 MB / zdjęcie. Pierwsze 3 zdjęcia bezpłatnie, kolejne: {price} jednorazowo.",
  "photos.tooLarge": "Zdjęcie przekracza 3 MB.",
  "photos.invalidType": "Plik musi być obrazem.",
  "photos.uploadFailed": "Przesyłanie nie powiodło się: {msg}",
  "photos.freeAdded": "Zdjęcie zostało dodane bezpłatnie.",
  "photos.paidPending": "Zdjęcie utworzone, oczekuje na opłatę.",
  "photos.deleted": "Zdjęcie zostało usunięte.",
  "photos.priceLabel": "Cena za kolejne zdjęcie",

  "home.title.a": "Odkryj smaki świata w",
  "home.title.b": "jednym miejscu",
  "home.subtitle": "Od rodzinnych bistro w Pradze po ukryte bary w Barcelonie. Globalny przewodnik dla miłośników autentycznej gastronomii.",
  "home.search.what": "Czego szukasz? (np. Tapas)",
  "home.search.where": "Gdzie? (np. Warszawa)",
  "home.search.submit": "Szukaj",

  "cat.restaurace": "Restauracje",
  "cat.bary": "Bary",
  "cat.kavarny": "Kawiarnie",
  "cat.cajovny": "Herbaciarnie",
  "cat.stanky": "Stragany",
  "cat.jidelny": "Stołówki",
  "cat.mlecnyBar": "Bary mleczne",
  "cat.foodTruck": "Food trucki",
  "cat.rychleObcerstveni": "Fast foody",
  "cat.bistra": "Bistra",

  "home.mapaCta": "Przeglądaj mapę świata",

  "home.featured.title": "Polecane lokale",
  "home.featured.sub": "Najlepiej oceniane perełki w naszej społeczności.",
  "home.featured.all": "Zobacz wszystkie",

  "dc.tag": "Program lojalnościowy",
  "dc.title": "Zostań członkiem Direct Club",
  "dc.text": "Zbieraj punkty bonusowe za każdą wizytę, korzystaj z ekskluzywnych zniżek i odkrywaj specjalne menu dostępne tylko dla członków sieci Cerveza.International.",
  "dc.register": "Zarejestruj się za darmo",
  "dc.how": "Jak to działa?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Numer karty",
  "dc.points": "Zebrane punkty",

  "owner.title": "Prowadzisz lokal gastronomiczny?",
  "owner.text": "Dołącz do tysięcy lokali i dotrzyj do gości z całego świata. Rejestracja jest szybka i bezpłatna.",
  "owner.cta": "Dodaj swój lokal",

  "footer.rights": "Wszelkie prawa zastrzeżone",
  "footer.about": "O nas",
  "footer.terms": "Regulamin",
  "footer.privacy": "Polityka prywatności",
  "footer.contact": "Kontakt",

  "auth.signIn": "Logowanie",
  "auth.signUp": "Rejestracja",
  "auth.tabSignIn": "Zaloguj się",
  "auth.tabSignUp": "Utwórz konto",
  "auth.google": "Kontynuuj przez Google",
  "auth.apple": "Kontynuuj przez Apple",
  "auth.or": "lub",
  "auth.email": "Email",
  "auth.password": "Hasło",
  "auth.passwordMin": "Hasło (min. 6 znaków)",
  "auth.showPassword": "Pokaż hasło",
  "auth.hidePassword": "Ukryj hasło",
  "auth.name": "Imię i nazwisko",
  "auth.submitSignIn": "Zaloguj się",
  "auth.submitSignInLoading": "Logowanie…",
  "auth.submitSignUp": "Utwórz konto",
  "auth.submitSignUpLoading": "Rejestracja…",
  "auth.firstAdminNote": "Pierwsze zarejestrowane konto automatycznie staje się administratorem systemu.",
  "auth.back": "← Powrót do strony",
  "auth.welcomeBack": "Witamy ponownie!",
  "auth.created": "Konto utworzone.",
  "auth.oauthFailed": "Logowanie nie powiodło się",

  "mapa.tag": "Mapa",
  "mapa.loading": "Ładowanie lokali…",
  "mapa.count": "zatwierdzonych lokali",
  "mapa.kindHeader": "Typ lokalu",
  "mapa.addBusiness": "Dodaj lokal",
  "mapa.showUsers": "Członkowie Direct Club",
  "mapa.showAnon": "Goście",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Lokal z katalogu",
  "mapa.catalog.brewery": "Browar z katalogu",
  "mapa.catalog.imported": "Zaimportowano z katalogu XLSX Cerveza Data Standard",
  "mapa.catalog.detail": "Szczegóły",
  "mapa.review.discovery": "Niezweryfikowane",
  "mapa.review.pending": "Do weryfikacji",
  "mapa.review.verified": "Zweryfikowane",
  "mapa.review.rejected": "Odrzucone",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "Ameryka Północna",
  "mapa.continent.southAmerica": "Ameryka Południowa",
  "mapa.continent.asia": "Azja",
  "mapa.continent.africa": "Afryka",
  "mapa.continent.oceania": "Oceania",
  "mapa.continent.other": "Inne",
  "mapa.memberBadge": "Członek Direct Club",
  "mapa.partnerHint": "Zweryfikowana firma partnerska zarejestrowana w Cerveza.International",
  "mapa.memberPrivacyNote": "Zarejestrowany użytkownik · przybliżona lokalizacja dla ochrony prywatności",
  "mapa.anonPrivacyNote": "Niezalogowany gość · pozycja widoczna przez 24 h, potem znika",
  "mapa.legend.title": "Legenda",
  "mapa.userPin.title": "Zarejestrowany członek Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Przybliżona lokalizacja (bez dokładnego adresu)",
  "mapa.viewProfile": "Profil lokalu",
  "mapa.empty.a": "Brak zatwierdzonych lokali. Bądź pierwszy —",
  "mapa.empty.b": "zarejestruj lokal",
  "mapa.anonPin.title": "Anonimowy odwiedzający",
  "mapa.anonPin.subtitle": "Aktywny w ciągu ostatnich 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtry",
  "mapa.filters.search": "Szukaj",
  "mapa.filters.searchPlaceholder": "Nazwa, miasto, opis…",
  "mapa.filters.location": "Lokalizacja",
  "mapa.filters.continent": "Kontynent",
  "mapa.filters.allContinents": "Wszystkie kontynenty",
  "mapa.filters.country": "Kraj",
  "mapa.filters.allCountries": "Wszystkie kraje",
  "mapa.filters.region": "Region",
  "mapa.filters.regionEmpty": "Region (brak)",
  "mapa.filters.allRegions": "Wszystkie regiony",
  "mapa.filters.city": "Miasto",
  "mapa.filters.allCities": "Wszystkie miasta",
  "mapa.filters.postal": "Kod pocztowy",
  "mapa.filters.partnership": "Partnerstwo",
  "mapa.filters.reset": "Resetuj filtry",
  "mapa.empty.filtered": "Żaden lokal nie pasuje do filtra.",
  "mapa.empty.resetFilters": "Resetuj filtry",
  "mapa.location.missing": "Brak lokalizacji",
  "mapa.location.stale": "Lokalizacja nieaktualna",
  "mapa.location.fresh": "Lokalizacja aktualna",
  "mapa.location.refresh": "Odśwież lokalizację",
  "mapa.location.lastSeen": "Ostatnio",
  "mapa.location.hidden": "ukryta na mapie",
  "mapa.location.never": "nigdy",
  "mapa.location.toastOk": "Lokalizacja zaktualizowana",
  "mapa.location.toastCached": "Użyto świeżej wartości z pamięci.",
  "mapa.location.toastSource": "Źródło",
  "mapa.location.toastFail": "Nie udało się ustalić lokalizacji",
  "mapa.location.toastFailDesc": "Spróbuj później (brak danych geo).",
  "mapa.location.toastError": "Błąd odświeżania lokalizacji",
  "mapa.breweries": "Browary",
  "mapa.breweriesHint": "Browary i producenci piwa",
  "mapa.showMembersNoBusiness": "Członkowie bez lokalu",
  "mapa.memberNoBusiness.badge": "Członek bez lokalu",
  "mapa.memberNoBusiness.title": "Członek gotowy budować sieć",
  "mapa.memberNoBusiness.subtitle": "Może polecać nowych członków, lokale i oceniać odwiedzane miejsca.",
  "mapa.memberNoBusiness.recommendBusiness": "Poleć lokal",
  "mapa.memberNoBusiness.inviteMember": "Poleć członka",
  "mapa.memberNoBusiness.rateVisited": "Oceń odwiedzony lokal",
  "mapa.memberNoBusiness.privacy": "Przybliżona lokalizacja dla ochrony prywatności",
  "mapa.memberNoBusiness.hint": "Zarejestrowani członkowie, którzy nie posiadają lokalu — idealni kandydaci do polecania",

  "my.title": "Moje lokale",
  "my.sub": "Zarządzaj swoimi lokalami i ich linkami.",
  "my.add": "Dodaj lokal",
  "my.loading": "Ładowanie…",
  "my.empty.a": "Nie masz jeszcze żadnych lokali.",
  "my.empty.b": "Zarejestruj pierwszy",
  "my.links": "Linki",
  "my.noLinks": "Brak linków.",
  "my.free": "Bezpłatny",
  "my.paid": "Płatny",
  "my.active": "Aktywny",
  "my.rejected": "Odrzucony",
  "my.pendingPayment": "Oczekuje na płatność",
  "my.pay": "Zapłać",
  "my.payTooltip": "Płatność online będzie wkrótce dostępna",
  "my.addLink": "Dodaj link",
  "my.linkInfo": "Linki do <strong>directclub.digital</strong> / <strong>direct.club</strong> są bezpłatne (maks. 3 na lokal). Pozostałe linki zewnętrzne: <strong>{price}</strong> jednorazowo (link na stałe po opłaceniu).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Etykieta (opcjonalnie)",
  "my.addFree": "Dodaj bezpłatnie",
  "my.addPaid": "Dodaj za {price}",
  "my.freeAdded": "Bezpłatny link został dodany i opublikowany.",
  "my.paidPending": "Link utworzony, oczekuje na opłatę.",
  "my.freeLimit": "Limit 3 bezpłatnych linków do directclub został wyczerpany.",
  "my.willBeFree": "✓ Link do directclub — będzie bezpłatny i opublikowany natychmiast.",
  "my.willBePaid": "Link zewnętrzny — wymaga opłaty {price} (publikacja po płatności).",
  "my.invalidUrl": "Nieprawidłowy adres URL",

  "reg.back": "← Powrót do strony",
  "reg.moderation": "Moderacja hybrydowa",
  "reg.title": "Rejestracja lokalu",
  "reg.desc": "Po wpisaniu kodu pocztowego automatycznie uzupełnimy miejscowość i współrzędne z bazy kodów pocztowych. Zweryfikowanych właścicieli zatwierdzamy automatycznie, pozostałe wpisy przechodzą moderację.",
  "reg.name": "Nazwa lokalu",
  "reg.namePlaceholder": "np. Café Vinohrady",
  "reg.kind": "Typ lokalu",
  "reg.kindPlaceholder": "Wybierz typ",
  "reg.country": "Kraj",
  "reg.psc": "Kod pocztowy",
  "reg.pscPlaceholder": "np. 120 00",
  "reg.pscInvalid": "Nieprawidłowy format kodu pocztowego (CZ: 5 cyfr).",
  "reg.searching": "Wyszukiwanie miejscowości…",
  "reg.notFound": "Dla kodu {postal} nie znaleźliśmy w bazie żadnej miejscowości.",
  "reg.foundOne": "Znaleziono miejscowość:",
  "reg.foundMany": "Znaleziono {count} miejscowości — wybierz właściwą:",
  "reg.coords": "Współrzędne:",
  "reg.mapPreview": "Podgląd lokalizacji na mapie",
  "reg.mapHint": "Przeciągnij pinezkę lub kliknij na mapie, aby doprecyzować lokalizację.",
  "reg.street": "Ulica i numer (opcjonalnie)",
  "reg.streetPlaceholder": "np. Korunní 12",
  "reg.descLabel": "Krótki opis (opcjonalnie)",
  "reg.descPlaceholder": "Czym wyróżnia się Twój lokal?",
  "reg.cancel": "Anuluj",
  "reg.submit": "Wyślij do rejestracji",
  "reg.submitted": "Lokal został wysłany do moderacji.",
  "reg.notSignedIn": "Nie jesteś zalogowany",
  "reg.pickCity": "Wybierz prawidłową miejscowość z wyników kodu pocztowego",
  "reg.invalidData": "Nieprawidłowe dane",
  "reg.gate.title": "Potwierdzenie własności lokalu",
  "reg.gate.desc": "Lokal może zarejestrować wyłącznie jego właściciel lub operator. Wybierz 1–2 kategorie, do których należy Twój lokal.",
  "reg.gate.confirmBefore": "Potwierdzam, że jestem",
  "reg.gate.confirmStrong": "właścicielem lub operatorem",
  "reg.gate.confirmAfter": "lokalu, który chcę zarejestrować, i lokal należy do jednej z poniższych kategorii.",
  "reg.gate.kindsLabel": "Kategoria lokalu (wybierz 1–2)",
  "reg.gate.selected": "Wybrano: {count} / 2",
  "reg.continue": "Kontynuuj",

  "kind.restaurace": "Restauracja",
  "kind.bar": "Bar",
  "kind.kavarna": "Kawiarnia",
  "kind.cajovna": "Herbaciarnia",
  "kind.stanek": "Stragan",
  "kind.jidelna": "Stołówka",
  "kind.mlecny_bar": "Bar mleczny",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast food",
  "kind.bistro": "Bistro",

  "status.pending": "Oczekuje na zatwierdzenie",
  "status.approved": "Zatwierdzony",
  "status.rejected": "Odrzucony",
  "status.hidden": "Ukryty",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "link.promo": "{months} mies. gratis do {date}",
  "auth.checkEmail": "Sprawdź swoją skrzynkę e-mail i potwierdź rejestrację, klikając w link, który do Ciebie wysłaliśmy.",
  "home.latest.title": "Najnowsze lokale",
  "home.latest.sub": "Ostatnio dodane zatwierdzone lokale.",
  "home.latest.empty": "Brak zatwierdzonych lokali.",
  "home.latest.viewAll": "Pokaż na mapie →",
  "reg.descMultiHint": "Możesz wypełnić opis w jednym lub kilku językach. Odwiedzający zobaczą go w swoim języku; w razie braku użyty zostanie inny dostępny.",
  "vakanty.title": "Mój dashboard",
  "vakanty.welcomeWithName": "Witaj, {name}. Poniżej widzisz liczbę Vacantów przypisanych do Twojego konta.",
  "vakanty.welcomeNoName": "Witaj. Poniżej widzisz liczbę Vacantów przypisanych do Twojego konta.",
  "vakanty.purchasedLabel": "Zakupione Vacanty",
  "vakanty.emptyHint": "Nie masz jeszcze żadnych Vacantów. O zwiększenie liczby poproś administratora.",
  "vakanty.featuresTitle": "Funkcje i informacje",
  "vakanty.kindFeature": "Funkcja",
  "vakanty.kindInfo": "Informacja",
  "vakanty.open": "Otwórz",
 "vakanty.loading": "Ładowanie…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Dokument wewnętrzny dostępny wyłącznie dla właścicieli Vacantów.",
 "vakanty.bp.strictHeader": "Ściśle poufne (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Ten dokument jest ściśle poufny i stanowi tajemnicę handlową spółki For Direct s.r.o.. Dokumentu nie wolno pobierać, przesyłać, kopiować, udostępniać, publikować ani w żaden inny sposób rozpowszechniać osobom trzecim — dotyczy to również jego fragmentów, zrzutów ekranu, zdjęć oraz transkrypcji. Dokument jest udostępniany wyłącznie do przeglądania online na tej stronie i tylko zalogowanemu właścicielowi Vacanta. Wszelkie nieuprawnione użycie, powielanie lub rozpowszechnianie stanowi naruszenie zobowiązań umownych i może skutkować odpowiedzialnością cywilną oraz karną. Naruszenie niniejszej umowy o zachowaniu poufności podlega karze umownej w wysokości 5 000 000 EUR (słownie: pięć milionów euro) za każdy pojedynczy przypadek naruszenia.",
 "vakanty.bp.paragraph2": "Zaznaczając poniższe pole potwierdzasz, że przeczytałeś(-aś) te zasady, rozumiesz je i zobowiązujesz się ich przestrzegać. Twoja zgoda zostanie zapisana wraz ze znacznikiem czasu.",
 "vakanty.bp.acceptedOn": "NDA zaakceptowane dnia {date}.",
 "vakanty.bp.checkboxLabel": "Oświadczam, że zapoznałem(-am) się z powyższymi warunkami poufności, akceptuję je i zobowiązuję się nie udostępniać ani nie rozpowszechniać dokumentu.",
 "vakanty.bp.acceptButton": "Zaakceptuj NDA i wyświetl",
 "vakanty.bp.showButton": "Pokaż Business Plan",
 "vakanty.bp.hideButton": "Ukryj dokument",
 "vakanty.bp.viewerNotice": "Ściśle poufne — nie kopiować, nie udostępniać, nie pobierać. Przeglądanie wyłącznie na tej stronie.",
 "vakanty.bp.mustCheckFirst": "Najpierw zaznacz zgodę na NDA.",
 "vakanty.bp.acceptedToast": "NDA zaakceptowane. Możesz teraz zobaczyć dokument.",
  "common.error": "Błąd",
  "common.tryAgain": "Spróbuj ponownie",
  "common.goHome": "Wróć do strony głównej",
  "common.pageNotFound": "Strona nie znaleziona",
  "common.pageNotFoundDesc": "Strona, której szukasz, nie istnieje lub została przeniesiona.",
  "common.errorTitle": "Nie udało się załadować strony",
  "common.errorDesc": "Coś poszło nie tak. Spróbuj odświeżyć lub wrócić na stronę główną.",
  "auth.enterEmailFirst": "Najpierw wpisz e-mail.",
  "auth.resendResult": "Jeśli konto istnieje i nie jest potwierdzone, wysłaliśmy nowy link. Pozostało prób: {remaining}.",
  "auth.resendFailed": "Nie udało się wysłać e-maila.",
  "auth.emailAlreadyRegistered": "Ten e-mail jest już zarejestrowany. Sprawdź skrzynkę lub zaloguj się.",
  "auth.resending": "Wysyłam…",
  "auth.resendConfirmation": "Wyślij ponownie e-mail potwierdzający",
  "auth.signInDesc": "Zaloguj się lub zarejestruj w Cerveza.International.",
  "auth.pageTitle": "Logowanie — Cerveza.International",
  "authReset.pageTitle": "Ustawienie hasła — Cerveza.International",
  "authReset.title": "Ustaw nowe hasło",
  "authReset.needLink": "Otwórz tę stronę z linku w otrzymanym e-mailu.",
  "authReset.newPassword": "Nowe hasło",
  "authReset.passwordAgain": "Powtórz hasło",
  "authReset.saving": "Zapisuję…",
  "authReset.save": "Zapisz hasło",
  "authReset.tooShort": "Hasło musi mieć co najmniej 8 znaków.",
  "authReset.mismatch": "Hasła nie są zgodne.",
  "authReset.saved": "Hasło ustawione.",
  "unsub.pageTitle": "Anulowanie subskrypcji — Cerveza.International",
  "unsub.title": "Anulowanie subskrypcji",
  "unsub.checking": "Weryfikuję link…",
  "unsub.confirmPrompt": "Potwierdź, że chcesz zrezygnować z otrzymywania e-maili od Cerveza.International.",
  "unsub.confirmBtn": "Potwierdź rezygnację",
  "unsub.processing": "Wypisywanie…",
  "unsub.done": "Gotowe. Zostałeś wypisany.",
  "unsub.already": "Ten adres został już wypisany.",
  "unsub.invalid": "Link jest nieprawidłowy lub wygasł.",
  "unsub.errorVerify": "Nie udało się zweryfikować linku.",
  "unsub.errorGeneric": "Coś poszło nie tak.",
  "unsub.errorNetwork": "Błąd sieci.",
  "pay.successPageTitle": "Płatność zakończona — Cerveza.International",
  "pay.successTitle": "Płatność zakończona pomyślnie",
  "pay.successDesc": "Dziękujemy. Zamówienie jest przetwarzane — link lub zdjęcie zostanie aktywowane w kilka sekund po potwierdzeniu.",
  "pay.backToMy": "Wróć do Moje firmy",
  "pay.cancelPageTitle": "Płatność anulowana — Cerveza.International",
  "pay.cancelTitle": "Płatność anulowana",
  "pay.cancelDesc": "Nie pobrano żadnej kwoty. Płatność możesz powtórzyć na stronie Moje firmy.",
  "profile.errorTitle": "Błąd",
  "profile.notFound": "Nie znaleziono lokalu",
  "profile.backToMap": "Wróć do mapy",
  "profile.noDescription": "Brak opisu. Dodaj go w językach, w których chcesz prezentować lokal.",
  "profile.editDescription": "Edytuj opis",
  "profile.multiLangDesc": "Opis wielojęzyczny",
  "profile.maxChars": "Maks. {n} znaków na język",
  "profile.descriptionsSaved": "Opisy zapisane",
  "profile.saveFailed": "Zapis nie powiódł się",
  "profile.save": "Zapisz",
  "profile.cancel": "Anuluj",
  "profile.linksHeader": "Linki",
  "profile.viewOnOSM": "Zobacz na OpenStreetMap",
  "profile.descPlaceholder": "Opis ({lang})",
  "reg.pageTitle": "Rejestracja lokalu — Cerveza.International",
  "reg.pageDesc": "Zarejestruj swój lokal w globalnym katalogu Cerveza.International.",
  "reg.maxTwoKinds": "Można wybrać maksymalnie 2 kategorie.",
  "reg.confirmedKinds": "Potwierdzone kategorie: {list}",
  "share.warnFallback": "Uwaga: szablon nie jest przetłumaczony na „{lang}”, wysyłamy oryginał czeski.",
  "share.copiedMsg": "Wiadomość skopiowana — wklej do wybranej aplikacji.",
  "share.copyFailed": "Kopiowanie nie powiodło się.",
  "share.copiedLink": "Link skopiowany.",
  "share.copyLink": "Kopiuj link",
  "share.copyMessage": "Kopiuj wiadomość",
  "my.photoUploadedPending": "Zdjęcie wgrane, oczekuje na akceptację admina.",
  "my.photoPendingApproval": "oczekuje na akceptację",
  "my.rejectReason": "Powód odrzucenia: {reason}",
  "my.rejectedShort": "odrzucone",
  "my.freeOutOf": "{free}/3 gratis · następne: {price}",
  "my.photoPack": "Pakiet zdjęć:",
  "my.pieces": "szt.",
  "majitele.pageTitle": "Właściciele Vacantów — Cerveza.International",

  // Downloads section
  "downloads.title": "Do pobrania",
  "downloads.subtitle": "Instrukcje i przewodniki do pobrania.",
  "downloads.vakantOnly": "Tylko dla właścicieli Vacantów",
  "downloads.signInToView": "Zaloguj się, aby zobaczyć",
  "downloads.downloadPdf": "Pobierz PDF",
  "downloads.empty": "Nie ma jeszcze żadnych instrukcji.",

};
const en: Dict = {
  "nav.home": "Home",
  "nav.back": "Back",
  "nav.katalog": "Catalogue",
  "nav.mapa": "Map",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "My panel",
  "nav.proMajitele": "For owners",
  "nav.admin": "Admin",
  "nav.cenik": "Pricing",
  "nav.mujUcet": "My account",
  "nav.odhlasit": "Sign out",
  "nav.prihlasit": "Sign in",
  "nav.vakanty": "Vacant owners",

  "photos.title": "Photos",
  "photos.add": "Add photo",
  "photos.maxSize": "Max 3 MB / photo. First 3 photos free, additional: {price} one-time.",
  "photos.tooLarge": "Photo exceeds 3 MB.",
  "photos.invalidType": "File must be an image.",
  "photos.uploadFailed": "Upload failed: {msg}",
  "photos.freeAdded": "Photo added for free.",
  "photos.paidPending": "Photo created, awaiting payment.",
  "photos.deleted": "Photo deleted.",
  "photos.priceLabel": "Price for each additional photo",

  "home.title.a": "Discover the flavours of the world in",
  "home.title.b": "one place",
  "home.subtitle": "From family bistros in Prague to hidden bars in Barcelona. A global guide for lovers of authentic gastronomy.",
  "home.search.what": "What are you looking for? (e.g. Tapas)",
  "home.search.where": "Where? (e.g. Prague)",
  "home.search.submit": "Search",

  "cat.restaurace": "Restaurants",
  "cat.bary": "Bars",
  "cat.kavarny": "Cafés",
  "cat.cajovny": "Tea houses",
  "cat.stanky": "Stalls",
  "cat.jidelny": "Canteens",
  "cat.mlecnyBar": "Milk bars",
  "cat.foodTruck": "Food trucks",
  "cat.rychleObcerstveni": "Fast Foods",
  "cat.bistra": "Bistros",

  "home.mapaCta": "Explore the world map",

  "home.featured.title": "Featured venues",
  "home.featured.sub": "The top-rated gems in our community.",
  "home.featured.all": "View all",

  "dc.tag": "Loyalty programme",
  "dc.title": "Become a Direct Club member",
  "dc.text": "Earn bonus points with every visit, enjoy exclusive discounts and discover special menus available only to members of the Cerveza.International network.",
  "dc.register": "Sign up for free",
  "dc.how": "How does it work?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Card number",
  "dc.points": "Points collected",

  "owner.title": "Do you own a gastro venue?",
  "owner.text": "Join thousands of venues and reach guests from around the world. Registration is fast and free.",
  "owner.cta": "Add your venue",

  "footer.rights": "All rights reserved",
  "footer.about": "About",
  "footer.terms": "Terms",
  "footer.privacy": "Privacy",
  "footer.contact": "Contact",

  "auth.signIn": "Sign in",
  "auth.signUp": "Sign up",
  "auth.tabSignIn": "Sign in",
  "auth.tabSignUp": "Create account",
  "auth.google": "Continue with Google",
  "auth.apple": "Continue with Apple",
  "auth.or": "or",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.passwordMin": "Password (min. 6 characters)",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "auth.name": "Full name",
  "auth.submitSignIn": "Sign in",
  "auth.submitSignInLoading": "Signing in…",
  "auth.submitSignUp": "Create account",
  "auth.submitSignUpLoading": "Registering…",
  "auth.firstAdminNote": "The first registered account automatically becomes the system administrator.",
  "auth.back": "← Back to site",
  "auth.welcomeBack": "Welcome back!",
  "auth.created": "Account created.",
  "auth.oauthFailed": "Sign-in failed",

  "mapa.tag": "Map",
  "mapa.loading": "Loading venues…",
  "mapa.count": "approved venues",
  "mapa.kindHeader": "Venue type",
  "mapa.addBusiness": "Add venue",
  "mapa.showUsers": "Direct Club members",
  "mapa.showAnon": "Guests",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Venue from catalog",
  "mapa.catalog.brewery": "Brewery from catalog",
  "mapa.catalog.imported": "Imported from Cerveza Data Standard XLSX catalog",
  "mapa.catalog.detail": "Detail",
  "mapa.review.discovery": "Unverified",
  "mapa.review.pending": "Pending review",
  "mapa.review.verified": "Verified",
  "mapa.review.rejected": "Rejected",
  "mapa.continent.europe": "Europe",
  "mapa.continent.northAmerica": "North America",
  "mapa.continent.southAmerica": "South America",
  "mapa.continent.asia": "Asia",
  "mapa.continent.africa": "Africa",
  "mapa.continent.oceania": "Oceania",
  "mapa.continent.other": "Other",
  "mapa.memberBadge": "Direct Club member",
  "mapa.partnerHint": "Verified partner business registered on Cerveza.International",
  "mapa.memberPrivacyNote": "Registered user · approximate location for privacy",
  "mapa.anonPrivacyNote": "Anonymous visitor · location shown for 24 h, then removed",
  "mapa.legend.title": "Legend",
  "mapa.userPin.title": "Registered Cerveza.International member — Direct Club",
  "mapa.userPin.subtitle": "Approximate location (no precise address)",
  "mapa.viewProfile": "Venue profile",
  "mapa.empty.a": "No approved venues yet. Be the first —",
  "mapa.empty.b": "register a venue",
  "mapa.anonPin.title": "Anonymous visitor",
  "mapa.anonPin.subtitle": "Active in the last 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filters",
  "mapa.filters.search": "Search",
  "mapa.filters.searchPlaceholder": "Name, city, description…",
  "mapa.filters.location": "Location",
  "mapa.filters.continent": "Continent",
  "mapa.filters.allContinents": "All continents",
  "mapa.filters.country": "Country",
  "mapa.filters.allCountries": "All countries",
  "mapa.filters.region": "Region / state",
  "mapa.filters.regionEmpty": "Region (none yet)",
  "mapa.filters.allRegions": "All regions",
  "mapa.filters.city": "City",
  "mapa.filters.allCities": "All cities",
  "mapa.filters.postal": "Postal code",
  "mapa.filters.partnership": "Partnership",
  "mapa.filters.reset": "Reset filters",
  "mapa.empty.filtered": "No venue matches the filter.",
  "mapa.empty.resetFilters": "Reset filters",
  "mapa.location.missing": "Location missing",
  "mapa.location.stale": "Location stale",
  "mapa.location.fresh": "Location fresh",
  "mapa.location.refresh": "Refresh location",
  "mapa.location.lastSeen": "Last seen",
  "mapa.location.hidden": "hidden from map",
  "mapa.location.never": "never",
  "mapa.location.toastOk": "Location updated",
  "mapa.location.toastCached": "Fresh cached value used.",
  "mapa.location.toastSource": "Source",
  "mapa.location.toastFail": "Could not determine your location",
  "mapa.location.toastFailDesc": "Please try again later (geo info unavailable).",
  "mapa.location.toastError": "Error refreshing location",
  "mapa.breweries": "Breweries",
  "mapa.breweriesHint": "Breweries and beer producers",
  "mapa.showMembersNoBusiness": "Members without a business",
  "mapa.memberNoBusiness.badge": "Member without a business",
  "mapa.memberNoBusiness.title": "Member ready to build the network",
  "mapa.memberNoBusiness.subtitle": "Can recommend new members, businesses and rate visited places.",
  "mapa.memberNoBusiness.recommendBusiness": "Recommend a business",
  "mapa.memberNoBusiness.inviteMember": "Recommend a member",
  "mapa.memberNoBusiness.rateVisited": "Rate visited business",
  "mapa.memberNoBusiness.privacy": "Approximate location for privacy protection",
  "mapa.memberNoBusiness.hint": "Registered members who do not own a business — ideal candidates for recommendations",

  "my.title": "My venues",
  "my.sub": "Manage your venues and their links.",
  "my.add": "Add venue",
  "my.loading": "Loading…",
  "my.empty.a": "You don't have any venues yet.",
  "my.empty.b": "Register the first one",
  "my.links": "Links",
  "my.noLinks": "No links.",
  "my.free": "Free",
  "my.paid": "Paid",
  "my.active": "Active",
  "my.rejected": "Rejected",
  "my.pendingPayment": "Awaiting payment",
  "my.pay": "Pay",
  "my.payTooltip": "Online payment will be available soon",
  "my.addLink": "Add link",
  "my.linkInfo": "Links to <strong>directclub.digital</strong> / <strong>direct.club</strong> are free (max 3 per venue). Other external links: <strong>{price}</strong> one-time (permanent link after payment).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Label (optional)",
  "my.addFree": "Add for free",
  "my.addPaid": "Add for {price}",
  "my.freeAdded": "Free link was added and published.",
  "my.paidPending": "Link created, awaiting payment.",
  "my.freeLimit": "Limit of 3 free directclub links has been reached.",
  "my.willBeFree": "✓ directclub link — will be free and published immediately.",
  "my.willBePaid": "External link — requires payment of {price} (published after payment).",
  "my.invalidUrl": "Invalid URL",

  "reg.back": "← Back to site",
  "reg.moderation": "Hybrid moderation",
  "reg.title": "Venue registration",
  "reg.desc": "After entering the postal code we'll automatically fill in the city and coordinates from the postal code database. Verified owners are approved automatically, other entries go through moderation.",
  "reg.name": "Venue name",
  "reg.namePlaceholder": "e.g. Café Vinohrady",
  "reg.kind": "Venue type",
  "reg.kindPlaceholder": "Select type",
  "reg.country": "Country",
  "reg.psc": "Postal code",
  "reg.pscPlaceholder": "e.g. 120 00",
  "reg.pscInvalid": "Invalid postal code format (CZ: 5 digits).",
  "reg.searching": "Searching for city…",
  "reg.notFound": "We didn't find any city in the database for postal code {postal}.",
  "reg.foundOne": "City found:",
  "reg.foundMany": "Found {count} cities — pick the right one:",
  "reg.coords": "Coordinates:",
  "reg.mapPreview": "Location preview on the map",
  "reg.mapHint": "Drag the pin or click on the map to refine the location.",
  "reg.street": "Street and number (optional)",
  "reg.streetPlaceholder": "e.g. Korunní 12",
  "reg.descLabel": "Short description (optional)",
  "reg.descMultiHint": "You may fill in the description in one or more languages. Visitors see it in their language; if missing, another available language is used.",
  "reg.descPlaceholder": "What makes your venue special?",
  "reg.cancel": "Cancel",
  "reg.submit": "Submit for registration",
  "reg.submitted": "Venue submitted for moderation.",
  "reg.notSignedIn": "You are not signed in",
  "reg.pickCity": "Select a valid city from the postal code results",
  "reg.invalidData": "Invalid data",
  "reg.gate.title": "Confirm business ownership",
  "reg.gate.desc": "Only the owner or operator may register a business. Select 1–2 categories that your business belongs to.",
  "reg.gate.confirmBefore": "I confirm that I am the",
  "reg.gate.confirmStrong": "owner or operator",
  "reg.gate.confirmAfter": "of the business I want to register, and that it belongs to one of the categories listed below.",
  "reg.gate.kindsLabel": "Business category (select 1–2)",
  "reg.gate.selected": "Selected: {count} / 2",
  "reg.continue": "Continue",

  "kind.restaurace": "Restaurant",
  "kind.bar": "Bar",
  "kind.kavarna": "Café",
  "kind.cajovna": "Tea house",
  "kind.stanek": "Stall",
  "kind.jidelna": "Canteen",
  "kind.mlecny_bar": "Milk bar",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistro",

  "status.pending": "Awaiting approval",
  "status.approved": "Approved",
  "status.rejected": "Rejected",
  "status.hidden": "Hidden",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "link.promo": "{months} mo. free until {date}",
  "auth.checkEmail": "Please check your inbox and confirm your registration by clicking the link we sent you.",
  "home.latest.title": "Latest Businesses",
  "home.latest.sub": "Recently added approved businesses.",
  "home.latest.empty": "No approved businesses yet.",
  "home.latest.viewAll": "Show on map →",
  "vakanty.title": "My dashboard",
  "vakanty.welcomeWithName": "Welcome, {name}. Below you can see the number of Vacants on your account.",
  "vakanty.welcomeNoName": "Welcome. Below you can see the number of Vacants on your account.",
  "vakanty.purchasedLabel": "Purchased Vacants",
  "vakanty.emptyHint": "You don't have any Vacants yet. Ask the administrator to add more.",
  "vakanty.featuresTitle": "Features and information",
  "vakanty.kindFeature": "Feature",
  "vakanty.kindInfo": "Information",
  "vakanty.open": "Open",
 "vakanty.loading": "Loading…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Internal document available exclusively to Vacant owners.",
 "vakanty.bp.strictHeader": "Strictly Confidential",
 "vakanty.bp.paragraph1": "This document is strictly confidential and constitutes a trade secret of For Direct s.r.o.. The document must not be downloaded, forwarded, copied, shared, published or otherwise distributed to any third party — including any parts of it, screenshots, photographs or transcripts. The document is available for online viewing on this page only, and exclusively to the signed-in Vacant owner. Any unauthorized use, reproduction or distribution constitutes a breach of contractual obligations and may lead to civil and criminal liability. Any breach of this non-disclosure agreement is subject to a contractual penalty of EUR 5,000,000 (five million euros) for each individual breach.",
 "vakanty.bp.paragraph2": "By ticking the box below you confirm that you have read these rules, understand them and undertake to comply with them. Your consent will be recorded with a timestamp.",
 "vakanty.bp.acceptedOn": "NDA accepted on {date}.",
 "vakanty.bp.checkboxLabel": "I declare that I have read the confidentiality terms above, agree to them and undertake not to share or distribute this document.",
 "vakanty.bp.acceptButton": "Accept NDA and view",
 "vakanty.bp.showButton": "Show Business Plan",
 "vakanty.bp.hideButton": "Hide document",
 "vakanty.bp.viewerNotice": "Strictly confidential — do not copy, share or download. Viewing on this page only.",
 "vakanty.bp.mustCheckFirst": "Please tick the NDA consent first.",
 "vakanty.bp.acceptedToast": "NDA accepted. You can now view the document.",
  "common.error": "Error",
  "common.tryAgain": "Try again",
  "common.goHome": "Go home",
  "common.pageNotFound": "Page not found",
  "common.pageNotFoundDesc": "The page you're looking for doesn't exist or has been moved.",
  "common.errorTitle": "This page didn't load",
  "common.errorDesc": "Something went wrong on our end. You can try refreshing or head back home.",
  "auth.enterEmailFirst": "Please enter your email first.",
  "auth.resendResult": "If an account exists and is unconfirmed, we sent a new link. Attempts left: {remaining}.",
  "auth.resendFailed": "Failed to send email.",
  "auth.emailAlreadyRegistered": "This email is already registered. Check your inbox for the confirmation link or sign in.",
  "auth.resending": "Sending…",
  "auth.resendConfirmation": "Resend confirmation email",
  "auth.signInDesc": "Sign in or sign up to Cerveza.International.",
  "auth.pageTitle": "Sign in — Cerveza.International",
  "authReset.pageTitle": "Set password — Cerveza.International",
  "authReset.title": "Set a new password",
  "authReset.needLink": "Please open this page from the link in the email you received.",
  "authReset.newPassword": "New password",
  "authReset.passwordAgain": "Password again",
  "authReset.saving": "Saving…",
  "authReset.save": "Save password",
  "authReset.tooShort": "Password must be at least 8 characters.",
  "authReset.mismatch": "Passwords do not match.",
  "authReset.saved": "Password set.",
  "unsub.pageTitle": "Unsubscribe — Cerveza.International",
  "unsub.title": "Unsubscribe",
  "unsub.checking": "Verifying link…",
  "unsub.confirmPrompt": "Please confirm that you want to unsubscribe from Cerveza.International emails.",
  "unsub.confirmBtn": "Confirm unsubscribe",
  "unsub.processing": "Unsubscribing…",
  "unsub.done": "Done. You have been unsubscribed.",
  "unsub.already": "This address is already unsubscribed.",
  "unsub.invalid": "The link is invalid or has expired.",
  "unsub.errorVerify": "Could not verify the link.",
  "unsub.errorGeneric": "Something went wrong.",
  "unsub.errorNetwork": "Network error.",
  "pay.successPageTitle": "Payment successful — Cerveza.International",
  "pay.successTitle": "Payment successful",
  "pay.successDesc": "Thank you. Your order is being processed — the link or photo will activate within seconds of the payment confirmation.",
  "pay.backToMy": "Back to My businesses",
  "pay.cancelPageTitle": "Payment cancelled — Cerveza.International",
  "pay.cancelTitle": "Payment cancelled",
  "pay.cancelDesc": "No amount was charged. You can retry the payment anytime from your My businesses page.",
  "profile.errorTitle": "Error",
  "profile.notFound": "Business not found",
  "profile.backToMap": "Back to map",
  "profile.noDescription": "No description yet. Add it in the languages you want to present the business in.",
  "profile.editDescription": "Edit description",
  "profile.multiLangDesc": "Multilingual description",
  "profile.maxChars": "Max {n} characters per language",
  "profile.descriptionsSaved": "Descriptions saved",
  "profile.saveFailed": "Save failed",
  "profile.save": "Save",
  "profile.cancel": "Cancel",
  "profile.linksHeader": "Links",
  "profile.viewOnOSM": "View on OpenStreetMap",
  "profile.descPlaceholder": "Description ({lang})",
  "reg.pageTitle": "Register a business — Cerveza.International",
  "reg.pageDesc": "Register your business in the global Cerveza.International catalog.",
  "reg.maxTwoKinds": "You can select at most 2 categories.",
  "reg.confirmedKinds": "Confirmed categories: {list}",
  "share.warnFallback": "Note: template is not translated to “{lang}”, sending the Czech original.",
  "share.copiedMsg": "Message copied — paste it into the desired app.",
  "share.copyFailed": "Copy failed.",
  "share.copiedLink": "Link copied.",
  "share.copyLink": "Copy link",
  "share.copyMessage": "Copy message",
  "my.photoUploadedPending": "Photo uploaded, awaiting admin approval.",
  "my.photoPendingApproval": "awaiting approval",
  "my.rejectReason": "Reason for rejection: {reason}",
  "my.rejectedShort": "rejected",
  "my.freeOutOf": "{free}/3 free · next: {price}",
  "my.photoPack": "Photo pack:",
  "my.pieces": "pcs",
  "majitele.pageTitle": "Vacant Owners — Cerveza.International",

  // Downloads section
  "downloads.title": "Downloads",
  "downloads.subtitle": "Manuals and guides to download.",
  "downloads.vakantOnly": "Vacant owners only",
  "downloads.signInToView": "Sign in to view",
  "downloads.downloadPdf": "Download PDF",
  "downloads.empty": "No manuals available yet.",

};
const es: Dict = {
  "nav.home": "Inicio",
  "nav.back": "Atrás",
  "nav.katalog": "Catálogo",
  "nav.mapa": "Mapa",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Mi panel",
  "nav.proMajitele": "Para propietarios",
  "nav.admin": "Admin",
  "nav.cenik": "Tarifas",
  "nav.mujUcet": "Mi cuenta",
  "nav.odhlasit": "Cerrar sesión",
  "nav.prihlasit": "Iniciar sesión",
  "nav.vakanty": "Propietarios de Vacants",

  "photos.title": "Fotos",
  "photos.add": "Añadir foto",
  "photos.maxSize": "Máx. 3 MB / foto. Las primeras 3 fotos gratis, las siguientes: {price} pago único.",
  "photos.tooLarge": "La foto supera 3 MB.",
  "photos.invalidType": "El archivo debe ser una imagen.",
  "photos.uploadFailed": "Error al subir: {msg}",
  "photos.freeAdded": "Foto añadida gratuitamente.",
  "photos.paidPending": "Foto creada, pendiente de pago.",
  "photos.deleted": "Foto eliminada.",
  "photos.priceLabel": "Precio por cada foto adicional",

  "home.title.a": "Descubre los sabores del mundo en",
  "home.title.b": "un solo lugar",
  "home.subtitle": "Desde bistrós familiares en Praga hasta bares escondidos en Barcelona. Una guía global para los amantes de la gastronomía auténtica.",
  "home.search.what": "¿Qué buscas? (p. ej. Tapas)",
  "home.search.where": "¿Dónde? (p. ej. Madrid)",
  "home.search.submit": "Buscar",

  "cat.restaurace": "Restaurantes",
  "cat.bary": "Bares",
  "cat.kavarny": "Cafeterías",
  "cat.cajovny": "Teterías",
  "cat.stanky": "Puestos",
  "cat.jidelny": "Comedores",
  "cat.mlecnyBar": "Bares lácteos",
  "cat.foodTruck": "Food trucks",
  "cat.rychleObcerstveni": "Fast Food",
  "cat.bistra": "Bistrós",

  "home.mapaCta": "Explorar el mapa del mundo",

  "home.featured.title": "Locales destacados",
  "home.featured.sub": "Las joyas mejor valoradas de nuestra comunidad.",
  "home.featured.all": "Ver todos",

  "dc.tag": "Programa de fidelidad",
  "dc.title": "Hazte miembro de Direct Club",
  "dc.text": "Gana puntos bonus por cada visita, aprovecha descuentos exclusivos y descubre menús especiales disponibles solo para miembros de la red Cerveza.International.",
  "dc.register": "Regístrate gratis",
  "dc.how": "¿Cómo funciona?",
  "dc.status": "Estado",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Número de tarjeta",
  "dc.points": "Puntos acumulados",

  "owner.title": "¿Eres dueño de un local gastronómico?",
  "owner.text": "Únete a miles de locales y llega a clientes de todo el mundo. El registro es rápido y gratuito.",
  "owner.cta": "Añadir mi local",

  "footer.rights": "Todos los derechos reservados",
  "footer.about": "Sobre nosotros",
  "footer.terms": "Términos",
  "footer.privacy": "Privacidad",
  "footer.contact": "Contacto",

  "auth.signIn": "Iniciar sesión",
  "auth.signUp": "Registro",
  "auth.tabSignIn": "Iniciar sesión",
  "auth.tabSignUp": "Crear cuenta",
  "auth.google": "Continuar con Google",
  "auth.apple": "Continuar con Apple",
  "auth.or": "o",
  "auth.email": "Email",
  "auth.password": "Contraseña",
  "auth.passwordMin": "Contraseña (mín. 6 caracteres)",
  "auth.showPassword": "Mostrar contraseña",
  "auth.hidePassword": "Ocultar contraseña",
  "auth.name": "Nombre y apellidos",
  "auth.submitSignIn": "Iniciar sesión",
  "auth.submitSignInLoading": "Iniciando sesión…",
  "auth.submitSignUp": "Crear cuenta",
  "auth.submitSignUpLoading": "Registrando…",
  "auth.firstAdminNote": "La primera cuenta registrada se convierte automáticamente en administrador del sistema.",
  "auth.back": "← Volver al sitio",
  "auth.welcomeBack": "¡Bienvenido de vuelta!",
  "auth.created": "Cuenta creada.",
  "auth.oauthFailed": "Error al iniciar sesión",

  "mapa.tag": "Mapa",
  "mapa.loading": "Cargando locales…",
  "mapa.count": "locales aprobados",
  "mapa.kindHeader": "Tipo de local",
  "mapa.addBusiness": "Añadir local",
  "mapa.showUsers": "Miembros de Direct Club",
  "mapa.showAnon": "Invitados",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Local del catálogo",
  "mapa.catalog.brewery": "Cervecería del catálogo",
  "mapa.catalog.imported": "Importado del catálogo XLSX Cerveza Data Standard",
  "mapa.catalog.detail": "Detalle",
  "mapa.review.discovery": "Sin verificar",
  "mapa.review.pending": "Pendiente de revisión",
  "mapa.review.verified": "Verificado",
  "mapa.review.rejected": "Rechazado",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "América del Norte",
  "mapa.continent.southAmerica": "América del Sur",
  "mapa.continent.asia": "Asia",
  "mapa.continent.africa": "África",
  "mapa.continent.oceania": "Oceanía",
  "mapa.continent.other": "Otros",
  "mapa.memberBadge": "Miembro Direct Club",
  "mapa.partnerHint": "Empresa asociada verificada registrada en Cerveza.International",
  "mapa.memberPrivacyNote": "Usuario registrado · ubicación aproximada por privacidad",
  "mapa.anonPrivacyNote": "Visitante anónimo · ubicación visible 24 h, luego se elimina",
  "mapa.legend.title": "Leyenda",
  "mapa.userPin.title": "Miembro registrado de Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Ubicación aproximada (sin dirección exacta)",
  "mapa.viewProfile": "Perfil del local",
  "mapa.empty.a": "Aún no hay locales aprobados. Sé el primero —",
  "mapa.empty.b": "registra un local",
  "mapa.anonPin.title": "Visitante anónimo",
  "mapa.anonPin.subtitle": "Activo en las últimas 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtros",
  "mapa.filters.search": "Buscar",
  "mapa.filters.searchPlaceholder": "Nombre, ciudad, descripción…",
  "mapa.filters.location": "Ubicación",
  "mapa.filters.continent": "Continente",
  "mapa.filters.allContinents": "Todos los continentes",
  "mapa.filters.country": "País",
  "mapa.filters.allCountries": "Todos los países",
  "mapa.filters.region": "Región / estado",
  "mapa.filters.regionEmpty": "Región (vacío)",
  "mapa.filters.allRegions": "Todas las regiones",
  "mapa.filters.city": "Ciudad",
  "mapa.filters.allCities": "Todas las ciudades",
  "mapa.filters.postal": "Código postal",
  "mapa.filters.partnership": "Asociación",
  "mapa.filters.reset": "Restablecer filtros",
  "mapa.empty.filtered": "Ningún local coincide con el filtro.",
  "mapa.empty.resetFilters": "Restablecer filtros",
  "mapa.location.missing": "Ubicación ausente",
  "mapa.location.stale": "Ubicación obsoleta",
  "mapa.location.fresh": "Ubicación reciente",
  "mapa.location.refresh": "Actualizar ubicación",
  "mapa.location.lastSeen": "Última vez",
  "mapa.location.hidden": "oculta en el mapa",
  "mapa.location.never": "nunca",
  "mapa.location.toastOk": "Ubicación actualizada",
  "mapa.location.toastCached": "Se usó un valor reciente en caché.",
  "mapa.location.toastSource": "Fuente",
  "mapa.location.toastFail": "No se pudo determinar la ubicación",
  "mapa.location.toastFailDesc": "Inténtelo más tarde (geo no disponible).",
  "mapa.location.toastError": "Error al actualizar la ubicación",
  "mapa.breweries": "Cervecerías",
  "mapa.breweriesHint": "Cervecerías y productores de cerveza",
  "mapa.showMembersNoBusiness": "Miembros sin negocio",
  "mapa.memberNoBusiness.badge": "Miembro sin negocio",
  "mapa.memberNoBusiness.title": "Miembro listo para construir la red",
  "mapa.memberNoBusiness.subtitle": "Puede recomendar nuevos miembros, negocios y valorar lugares visitados.",
  "mapa.memberNoBusiness.recommendBusiness": "Recomendar negocio",
  "mapa.memberNoBusiness.inviteMember": "Recomendar miembro",
  "mapa.memberNoBusiness.rateVisited": "Valorar negocio visitado",
  "mapa.memberNoBusiness.privacy": "Ubicación aproximada para proteger la privacidad",
  "mapa.memberNoBusiness.hint": "Miembros registrados que no tienen un negocio — candidatos ideales para recomendaciones",

  "my.title": "Mis locales",
  "my.sub": "Gestiona tus locales y sus enlaces.",
  "my.add": "Añadir local",
  "my.loading": "Cargando…",
  "my.empty.a": "Aún no tienes locales.",
  "my.empty.b": "Registrar el primero",
  "my.links": "Enlaces",
  "my.noLinks": "Sin enlaces.",
  "my.free": "Gratis",
  "my.paid": "De pago",
  "my.active": "Activo",
  "my.rejected": "Rechazado",
  "my.pendingPayment": "Pendiente de pago",
  "my.pay": "Pagar",
  "my.payTooltip": "El pago en línea estará disponible pronto",
  "my.addLink": "Añadir enlace",
  "my.linkInfo": "Los enlaces a <strong>directclub.digital</strong> / <strong>direct.club</strong> son gratis (máx. 3 por local). Otros enlaces externos: <strong>{price}</strong> pago único (enlace permanente tras el pago).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Etiqueta (opcional)",
  "my.addFree": "Añadir gratis",
  "my.addPaid": "Añadir por {price}",
  "my.freeAdded": "Enlace gratuito añadido y publicado.",
  "my.paidPending": "Enlace creado, pendiente de pago.",
  "my.freeLimit": "Se ha alcanzado el límite de 3 enlaces gratuitos de directclub.",
  "my.willBeFree": "✓ Enlace de directclub — será gratuito y se publicará inmediatamente.",
  "my.willBePaid": "Enlace externo — requiere pago de {price} (publicación tras el pago).",
  "my.invalidUrl": "URL no válida",

  "reg.back": "← Volver al sitio",
  "reg.moderation": "Moderación híbrida",
  "reg.title": "Registro de local",
  "reg.desc": "Tras introducir el código postal completaremos automáticamente la ciudad y las coordenadas desde la base de códigos postales. Los propietarios verificados se aprueban automáticamente, el resto pasa por moderación.",
  "reg.name": "Nombre del local",
  "reg.namePlaceholder": "p. ej. Café Vinohrady",
  "reg.kind": "Tipo de local",
  "reg.kindPlaceholder": "Selecciona tipo",
  "reg.country": "País",
  "reg.psc": "Código postal",
  "reg.pscPlaceholder": "p. ej. 28013",
  "reg.pscInvalid": "Formato de código postal no válido (CZ: 5 dígitos).",
  "reg.searching": "Buscando ciudad…",
  "reg.notFound": "No encontramos ninguna ciudad en la base para el código postal {postal}.",
  "reg.foundOne": "Ciudad encontrada:",
  "reg.foundMany": "Se encontraron {count} ciudades — elige la correcta:",
  "reg.coords": "Coordenadas:",
  "reg.mapPreview": "Vista previa de la ubicación en el mapa",
  "reg.mapHint": "Arrastra el marcador o haz clic en el mapa para ajustar la ubicación.",
  "reg.street": "Calle y número (opcional)",
  "reg.streetPlaceholder": "p. ej. Gran Vía 12",
  "reg.descLabel": "Descripción breve (opcional)",
  "reg.descPlaceholder": "¿Qué hace especial a tu local?",
  "reg.cancel": "Cancelar",
  "reg.submit": "Enviar para registro",
  "reg.submitted": "Local enviado a moderación.",
  "reg.notSignedIn": "No has iniciado sesión",
  "reg.pickCity": "Selecciona una ciudad válida de los resultados del código postal",
  "reg.invalidData": "Datos no válidos",
  "reg.gate.title": "Confirmación de titularidad del establecimiento",
  "reg.gate.desc": "Solo el propietario o el responsable puede registrar un establecimiento. Seleccione 1–2 categorías a las que pertenece su establecimiento.",
  "reg.gate.confirmBefore": "Confirmo que soy",
  "reg.gate.confirmStrong": "propietario o responsable",
  "reg.gate.confirmAfter": "del establecimiento que quiero registrar y que pertenece a alguna de las categorías indicadas a continuación.",
  "reg.gate.kindsLabel": "Categoría del establecimiento (elija 1–2)",
  "reg.gate.selected": "Seleccionadas: {count} / 2",
  "reg.continue": "Continuar",

  "kind.restaurace": "Restaurante",
  "kind.bar": "Bar",
  "kind.kavarna": "Cafetería",
  "kind.cajovna": "Tetería",
  "kind.stanek": "Puesto",
  "kind.jidelna": "Comedor",
  "kind.mlecny_bar": "Bar lácteo",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistró",

  "status.pending": "Pendiente de aprobación",
  "status.approved": "Aprobado",
  "status.rejected": "Rechazado",
  "status.hidden": "Oculto",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "link.promo": "{months} meses gratis hasta {date}",
  "auth.checkEmail": "Revise su bandeja de entrada y confirme su registro haciendo clic en el enlace que le enviamos.",
  "home.latest.title": "Negocios recientes",
  "home.latest.sub": "Negocios aprobados añadidos recientemente.",
  "home.latest.empty": "Aún no hay negocios aprobados.",
  "home.latest.viewAll": "Ver en el mapa →",
  "reg.descMultiHint": "Puedes rellenar la descripción en uno o varios idiomas. Los visitantes la verán en su idioma; si falta, se usará otro disponible.",
  "vakanty.title": "Mi panel",
  "vakanty.welcomeWithName": "Bienvenido, {name}. A continuación verás el número de Vacants en tu cuenta.",
  "vakanty.welcomeNoName": "Bienvenido. A continuación verás el número de Vacants en tu cuenta.",
  "vakanty.purchasedLabel": "Vacants adquiridos",
  "vakanty.emptyHint": "Aún no tienes Vacants. Pide al administrador que los amplíe.",
  "vakanty.featuresTitle": "Funciones e información",
  "vakanty.kindFeature": "Función",
  "vakanty.kindInfo": "Información",
  "vakanty.open": "Abrir",
 "vakanty.loading": "Cargando…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Documento interno disponible exclusivamente para los propietarios de Vacants.",
 "vakanty.bp.strictHeader": "Estrictamente confidencial (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Este documento es estrictamente confidencial y constituye un secreto comercial de For Direct s.r.o.. El documento no puede ser descargado, reenviado, copiado, compartido, publicado ni distribuido de ninguna otra manera a terceros, incluidas partes del mismo, capturas de pantalla, fotografías o transcripciones. El documento solo está disponible para su consulta en línea en esta página y exclusivamente para el propietario de Vacant identificado. Cualquier uso, reproducción o distribución no autorizados constituye un incumplimiento de las obligaciones contractuales y puede dar lugar a responsabilidad civil y penal. El incumplimiento de este acuerdo de confidencialidad está sujeto a una penalización contractual de 5.000.000 EUR (cinco millones de euros) por cada infracción individual.",
 "vakanty.bp.paragraph2": "Al marcar la casilla siguiente confirmas que has leído estas reglas, las comprendes y te comprometes a cumplirlas. Tu consentimiento se registrará con marca de tiempo.",
 "vakanty.bp.acceptedOn": "NDA aceptado el {date}.",
 "vakanty.bp.checkboxLabel": "Declaro que he leído los términos de confidencialidad anteriores, los acepto y me comprometo a no compartir ni distribuir este documento.",
 "vakanty.bp.acceptButton": "Aceptar NDA y ver",
 "vakanty.bp.showButton": "Mostrar Business Plan",
 "vakanty.bp.hideButton": "Ocultar documento",
 "vakanty.bp.viewerNotice": "Estrictamente confidencial — no copiar, compartir ni descargar. Consulta únicamente en esta página.",
 "vakanty.bp.mustCheckFirst": "Primero marca la aceptación del NDA.",
 "vakanty.bp.acceptedToast": "NDA aceptado. Ahora puedes ver el documento.",
  "common.error": "Error",
  "common.tryAgain": "Reintentar",
  "common.goHome": "Ir al inicio",
  "common.pageNotFound": "Página no encontrada",
  "common.pageNotFoundDesc": "La página que buscas no existe o ha sido movida.",
  "common.errorTitle": "La página no se cargó",
  "common.errorDesc": "Algo salió mal. Puedes intentar recargar o volver al inicio.",
  "auth.enterEmailFirst": "Introduce primero el correo.",
  "auth.resendResult": "Si la cuenta existe y no está confirmada, hemos enviado un nuevo enlace. Intentos restantes: {remaining}.",
  "auth.resendFailed": "No se pudo enviar el correo.",
  "auth.emailAlreadyRegistered": "Este correo ya está registrado. Revisa tu bandeja o inicia sesión.",
  "auth.resending": "Enviando…",
  "auth.resendConfirmation": "Reenviar correo de confirmación",
  "auth.signInDesc": "Inicia sesión o regístrate en Cerveza.International.",
  "auth.pageTitle": "Iniciar sesión — Cerveza.International",
  "authReset.pageTitle": "Establecer contraseña — Cerveza.International",
  "authReset.title": "Establecer nueva contraseña",
  "authReset.needLink": "Abre esta página desde el enlace del correo recibido.",
  "authReset.newPassword": "Nueva contraseña",
  "authReset.passwordAgain": "Repite la contraseña",
  "authReset.saving": "Guardando…",
  "authReset.save": "Guardar contraseña",
  "authReset.tooShort": "La contraseña debe tener al menos 8 caracteres.",
  "authReset.mismatch": "Las contraseñas no coinciden.",
  "authReset.saved": "Contraseña establecida.",
  "unsub.pageTitle": "Cancelar suscripción — Cerveza.International",
  "unsub.title": "Cancelar suscripción",
  "unsub.checking": "Verificando enlace…",
  "unsub.confirmPrompt": "Confirma que deseas darte de baja de los correos de Cerveza.International.",
  "unsub.confirmBtn": "Confirmar baja",
  "unsub.processing": "Dando de baja…",
  "unsub.done": "Hecho. Has sido dado de baja.",
  "unsub.already": "Esta dirección ya está dada de baja.",
  "unsub.invalid": "El enlace no es válido o ha caducado.",
  "unsub.errorVerify": "No se pudo verificar el enlace.",
  "unsub.errorGeneric": "Algo salió mal.",
  "unsub.errorNetwork": "Error de red.",
  "pay.successPageTitle": "Pago realizado — Cerveza.International",
  "pay.successTitle": "Pago realizado con éxito",
  "pay.successDesc": "Gracias. Tu pedido se está procesando — el enlace o la foto se activará en segundos tras la confirmación.",
  "pay.backToMy": "Volver a Mis negocios",
  "pay.cancelPageTitle": "Pago cancelado — Cerveza.International",
  "pay.cancelTitle": "Pago cancelado",
  "pay.cancelDesc": "No se ha cobrado ninguna cantidad. Puedes reintentar el pago desde tu página Mis negocios.",
  "profile.errorTitle": "Error",
  "profile.notFound": "Negocio no encontrado",
  "profile.backToMap": "Volver al mapa",
  "profile.noDescription": "Sin descripción. Añádela en los idiomas en los que quieras presentar el negocio.",
  "profile.editDescription": "Editar descripción",
  "profile.multiLangDesc": "Descripción multilingüe",
  "profile.maxChars": "Máx. {n} caracteres por idioma",
  "profile.descriptionsSaved": "Descripciones guardadas",
  "profile.saveFailed": "Error al guardar",
  "profile.save": "Guardar",
  "profile.cancel": "Cancelar",
  "profile.linksHeader": "Enlaces",
  "profile.viewOnOSM": "Ver en OpenStreetMap",
  "profile.descPlaceholder": "Descripción ({lang})",
  "reg.pageTitle": "Registrar un negocio — Cerveza.International",
  "reg.pageDesc": "Registra tu negocio en el catálogo global Cerveza.International.",
  "reg.maxTwoKinds": "Se pueden seleccionar máximo 2 categorías.",
  "reg.confirmedKinds": "Categorías confirmadas: {list}",
  "share.warnFallback": "Aviso: la plantilla no está traducida a «{lang}», se envía el original en checo.",
  "share.copiedMsg": "Mensaje copiado — pégalo en la app deseada.",
  "share.copyFailed": "Error al copiar.",
  "share.copiedLink": "Enlace copiado.",
  "share.copyLink": "Copiar enlace",
  "share.copyMessage": "Copiar mensaje",
  "my.photoUploadedPending": "Foto subida, pendiente de aprobación por el administrador.",
  "my.photoPendingApproval": "pendiente de aprobación",
  "my.rejectReason": "Motivo del rechazo: {reason}",
  "my.rejectedShort": "rechazada",
  "my.freeOutOf": "{free}/3 gratis · siguiente: {price}",
  "my.photoPack": "Paquete de fotos:",
  "my.pieces": "uds",
  "majitele.pageTitle": "Propietarios de Vacants — Cerveza.International",

  // Downloads section
  "downloads.title": "Descargas",
  "downloads.subtitle": "Manuales y guías para descargar.",
  "downloads.vakantOnly": "Solo para propietarios de Vacants",
  "downloads.signInToView": "Inicia sesión para ver",
  "downloads.downloadPdf": "Descargar PDF",
  "downloads.empty": "Aún no hay manuales disponibles.",

};
const de: Dict = {
  "nav.home": "Startseite",
  "nav.back": "Zurück",
  "nav.katalog": "Katalog",
  "nav.mapa": "Karte",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Mein Panel",
  "nav.proMajitele": "Für Inhaber",
  "nav.admin": "Admin",
  "nav.cenik": "Preisliste",
  "nav.mujUcet": "Mein Konto",
  "nav.odhlasit": "Abmelden",
  "nav.prihlasit": "Anmelden",
  "nav.vakanty": "Vacant-Inhaber",

  "photos.title": "Fotos",
  "photos.add": "Foto hinzufügen",
  "photos.maxSize": "Max. 3 MB / Foto. Die ersten 3 Fotos kostenlos, weitere: {price} einmalig.",
  "photos.tooLarge": "Foto überschreitet 3 MB.",
  "photos.invalidType": "Datei muss ein Bild sein.",
  "photos.uploadFailed": "Hochladen fehlgeschlagen: {msg}",
  "photos.freeAdded": "Foto wurde kostenlos hinzugefügt.",
  "photos.paidPending": "Foto erstellt, wartet auf Zahlung.",
  "photos.deleted": "Foto wurde gelöscht.",
  "photos.priceLabel": "Preis pro weiteres Foto",

  "home.title.a": "Entdecken Sie die Aromen der Welt an",
  "home.title.b": "einem Ort",
  "home.subtitle": "Von Familienbistros in Prag bis zu versteckten Bars in Barcelona. Ein globaler Führer für Liebhaber authentischer Gastronomie.",
  "home.search.what": "Was suchen Sie? (z. B. Tapas)",
  "home.search.where": "Wo? (z. B. Berlin)",
  "home.search.submit": "Suchen",

  "cat.restaurace": "Restaurants",
  "cat.bary": "Bars",
  "cat.kavarny": "Cafés",
  "cat.cajovny": "Teestuben",
  "cat.stanky": "Stände",
  "cat.jidelny": "Kantinen",
  "cat.mlecnyBar": "Milchbars",
  "cat.foodTruck": "Food Trucks",
  "cat.rychleObcerstveni": "Fast Foods",
  "cat.bistra": "Bistros",

  "home.mapaCta": "Weltkarte erkunden",

  "home.featured.title": "Empfohlene Lokale",
  "home.featured.sub": "Die bestbewerteten Perlen unserer Community.",
  "home.featured.all": "Alle anzeigen",

  "dc.tag": "Treueprogramm",
  "dc.title": "Werden Sie Mitglied im Direct Club",
  "dc.text": "Sammeln Sie Bonuspunkte bei jedem Besuch, nutzen Sie exklusive Rabatte und entdecken Sie spezielle Menüs nur für Mitglieder des Cerveza.International-Netzwerks.",
  "dc.register": "Kostenlos registrieren",
  "dc.how": "Wie funktioniert es?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Kartennummer",
  "dc.points": "Gesammelte Punkte",

  "owner.title": "Betreiben Sie ein Gastro-Lokal?",
  "owner.text": "Schließen Sie sich tausenden Lokalen an und erreichen Sie Gäste aus aller Welt. Die Registrierung ist schnell und kostenlos.",
  "owner.cta": "Lokal hinzufügen",

  "footer.rights": "Alle Rechte vorbehalten",
  "footer.about": "Über uns",
  "footer.terms": "AGB",
  "footer.privacy": "Datenschutz",
  "footer.contact": "Kontakt",

  "auth.signIn": "Anmeldung",
  "auth.signUp": "Registrierung",
  "auth.tabSignIn": "Anmelden",
  "auth.tabSignUp": "Konto erstellen",
  "auth.google": "Mit Google fortfahren",
  "auth.apple": "Mit Apple fortfahren",
  "auth.or": "oder",
  "auth.email": "E-Mail",
  "auth.password": "Passwort",
  "auth.passwordMin": "Passwort (min. 6 Zeichen)",
  "auth.showPassword": "Passwort anzeigen",
  "auth.hidePassword": "Passwort ausblenden",
  "auth.name": "Vor- und Nachname",
  "auth.submitSignIn": "Anmelden",
  "auth.submitSignInLoading": "Anmeldung läuft…",
  "auth.submitSignUp": "Konto erstellen",
  "auth.submitSignUpLoading": "Registrierung läuft…",
  "auth.firstAdminNote": "Das erste registrierte Konto wird automatisch zum Systemadministrator.",
  "auth.back": "← Zurück zur Website",
  "auth.welcomeBack": "Willkommen zurück!",
  "auth.created": "Konto erstellt.",
  "auth.oauthFailed": "Anmeldung fehlgeschlagen",

  "mapa.tag": "Karte",
  "mapa.loading": "Lokale werden geladen…",
  "mapa.count": "genehmigte Lokale",
  "mapa.kindHeader": "Lokaltyp",
  "mapa.addBusiness": "Lokal hinzufügen",
  "mapa.showUsers": "Direct Club Mitglieder",
  "mapa.showAnon": "Gäste",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Betrieb aus dem Katalog",
  "mapa.catalog.brewery": "Brauerei aus dem Katalog",
  "mapa.catalog.imported": "Importiert aus dem Cerveza-Data-Standard-XLSX-Katalog",
  "mapa.catalog.detail": "Details",
  "mapa.review.discovery": "Nicht verifiziert",
  "mapa.review.pending": "Zur Prüfung",
  "mapa.review.verified": "Verifiziert",
  "mapa.review.rejected": "Abgelehnt",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "Nordamerika",
  "mapa.continent.southAmerica": "Südamerika",
  "mapa.continent.asia": "Asien",
  "mapa.continent.africa": "Afrika",
  "mapa.continent.oceania": "Ozeanien",
  "mapa.continent.other": "Sonstige",
  "mapa.memberBadge": "Direct-Club-Mitglied",
  "mapa.partnerHint": "Verifiziertes Partnerunternehmen, registriert bei Cerveza.International",
  "mapa.memberPrivacyNote": "Registrierter Nutzer · ungefährer Standort zum Schutz der Privatsphäre",
  "mapa.anonPrivacyNote": "Anonymer Besucher · Standort 24 Std. sichtbar, danach entfernt",
  "mapa.legend.title": "Legende",
  "mapa.userPin.title": "Registriertes Mitglied von Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Ungefährer Standort (ohne genaue Adresse)",
  "mapa.viewProfile": "Lokalprofil",
  "mapa.empty.a": "Noch keine genehmigten Lokale. Seien Sie der Erste —",
  "mapa.empty.b": "Lokal registrieren",
  "mapa.anonPin.title": "Anonymer Besucher",
  "mapa.anonPin.subtitle": "Aktiv in den letzten 24 Std.",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filter",
  "mapa.filters.search": "Suchen",
  "mapa.filters.searchPlaceholder": "Name, Stadt, Beschreibung…",
  "mapa.filters.location": "Standort",
  "mapa.filters.continent": "Kontinent",
  "mapa.filters.allContinents": "Alle Kontinente",
  "mapa.filters.country": "Land",
  "mapa.filters.allCountries": "Alle Länder",
  "mapa.filters.region": "Region",
  "mapa.filters.regionEmpty": "Region (leer)",
  "mapa.filters.allRegions": "Alle Regionen",
  "mapa.filters.city": "Stadt",
  "mapa.filters.allCities": "Alle Städte",
  "mapa.filters.postal": "PLZ",
  "mapa.filters.partnership": "Partnerschaft",
  "mapa.filters.reset": "Filter zurücksetzen",
  "mapa.empty.filtered": "Kein Lokal entspricht dem Filter.",
  "mapa.empty.resetFilters": "Filter zurücksetzen",
  "mapa.location.missing": "Standort fehlt",
  "mapa.location.stale": "Standort veraltet",
  "mapa.location.fresh": "Standort aktuell",
  "mapa.location.refresh": "Standort aktualisieren",
  "mapa.location.lastSeen": "Zuletzt",
  "mapa.location.hidden": "auf Karte ausgeblendet",
  "mapa.location.never": "nie",
  "mapa.location.toastOk": "Standort aktualisiert",
  "mapa.location.toastCached": "Frischer gespeicherter Wert verwendet.",
  "mapa.location.toastSource": "Quelle",
  "mapa.location.toastFail": "Standort konnte nicht ermittelt werden",
  "mapa.location.toastFailDesc": "Bitte später erneut versuchen (Geo nicht verfügbar).",
  "mapa.location.toastError": "Fehler beim Aktualisieren",
  "mapa.breweries": "Brauereien",
  "mapa.breweriesHint": "Brauereien und Bierhersteller",
  "mapa.showMembersNoBusiness": "Mitglieder ohne Betrieb",
  "mapa.memberNoBusiness.badge": "Mitglied ohne Betrieb",
  "mapa.memberNoBusiness.title": "Mitglied bereit, Netzwerk aufzubauen",
  "mapa.memberNoBusiness.subtitle": "Kann neue Mitglieder, Betriebe empfehlen und besuchte Orte bewerten.",
  "mapa.memberNoBusiness.recommendBusiness": "Betrieb empfehlen",
  "mapa.memberNoBusiness.inviteMember": "Mitglied empfehlen",
  "mapa.memberNoBusiness.rateVisited": "Besuchten Betrieb bewerten",
  "mapa.memberNoBusiness.privacy": "Ungefährer Standort zum Schutz der Privatsphäre",
  "mapa.memberNoBusiness.hint": "Registrierte Mitglieder ohne eigenen Betrieb — ideale Kandidaten für Empfehlungen",

  "my.title": "Meine Lokale",
  "my.sub": "Verwalten Sie Ihre Lokale und deren Links.",
  "my.add": "Lokal hinzufügen",
  "my.loading": "Laden…",
  "my.empty.a": "Sie haben noch keine Lokale.",
  "my.empty.b": "Erstes registrieren",
  "my.links": "Links",
  "my.noLinks": "Keine Links.",
  "my.free": "Kostenlos",
  "my.paid": "Kostenpflichtig",
  "my.active": "Aktiv",
  "my.rejected": "Abgelehnt",
  "my.pendingPayment": "Wartet auf Zahlung",
  "my.pay": "Bezahlen",
  "my.payTooltip": "Online-Zahlung bald verfügbar",
  "my.addLink": "Link hinzufügen",
  "my.linkInfo": "Links zu <strong>directclub.digital</strong> / <strong>direct.club</strong> sind kostenlos (max. 3 pro Lokal). Andere externe Links: <strong>{price}</strong> einmalig (permanenter Link nach Zahlung).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Bezeichnung (optional)",
  "my.addFree": "Kostenlos hinzufügen",
  "my.addPaid": "Für {price} hinzufügen",
  "my.freeAdded": "Kostenloser Link wurde hinzugefügt und veröffentlicht.",
  "my.paidPending": "Link erstellt, wartet auf Zahlung.",
  "my.freeLimit": "Limit von 3 kostenlosen directclub-Links erreicht.",
  "my.willBeFree": "✓ directclub-Link — kostenlos und sofort veröffentlicht.",
  "my.willBePaid": "Externer Link — Zahlung von {price} erforderlich (Veröffentlichung nach Zahlung).",
  "my.invalidUrl": "Ungültige URL",

  "reg.back": "← Zurück zur Website",
  "reg.moderation": "Hybride Moderation",
  "reg.title": "Lokalregistrierung",
  "reg.desc": "Nach Eingabe der Postleitzahl ergänzen wir automatisch Stadt und Koordinaten aus der PLZ-Datenbank. Verifizierte Inhaber werden automatisch genehmigt, andere Einträge durchlaufen die Moderation.",
  "reg.name": "Name des Lokals",
  "reg.namePlaceholder": "z. B. Café Vinohrady",
  "reg.kind": "Lokaltyp",
  "reg.kindPlaceholder": "Typ wählen",
  "reg.country": "Land",
  "reg.psc": "Postleitzahl",
  "reg.pscPlaceholder": "z. B. 10115",
  "reg.pscInvalid": "Ungültiges PLZ-Format (CZ: 5 Ziffern).",
  "reg.searching": "Stadt wird gesucht…",
  "reg.notFound": "Für PLZ {postal} wurde keine Stadt in der Datenbank gefunden.",
  "reg.foundOne": "Stadt gefunden:",
  "reg.foundMany": "{count} Städte gefunden — wählen Sie die richtige:",
  "reg.coords": "Koordinaten:",
  "reg.mapPreview": "Standortvorschau auf der Karte",
  "reg.mapHint": "Pin ziehen oder auf die Karte klicken, um den Standort anzupassen.",
  "reg.street": "Straße und Hausnummer (optional)",
  "reg.streetPlaceholder": "z. B. Korunní 12",
  "reg.descLabel": "Kurze Beschreibung (optional)",
  "reg.descPlaceholder": "Was macht Ihr Lokal besonders?",
  "reg.cancel": "Abbrechen",
  "reg.submit": "Zur Registrierung senden",
  "reg.submitted": "Lokal zur Moderation gesendet.",
  "reg.notSignedIn": "Sie sind nicht angemeldet",
  "reg.pickCity": "Wählen Sie eine gültige Stadt aus den PLZ-Ergebnissen",
  "reg.invalidData": "Ungültige Daten",
  "reg.gate.title": "Bestätigung der Inhaberschaft",
  "reg.gate.desc": "Einen Betrieb kann nur sein Inhaber oder Betreiber registrieren. Wählen Sie 1–2 Kategorien, zu denen Ihr Betrieb gehört.",
  "reg.gate.confirmBefore": "Ich bestätige, dass ich",
  "reg.gate.confirmStrong": "Inhaber oder Betreiber",
  "reg.gate.confirmAfter": "des Betriebs bin, den ich registrieren möchte, und dass er zu einer der unten genannten Kategorien gehört.",
  "reg.gate.kindsLabel": "Kategorie des Betriebs (1–2 wählen)",
  "reg.gate.selected": "Ausgewählt: {count} / 2",
  "reg.continue": "Weiter",

  "kind.restaurace": "Restaurant",
  "kind.bar": "Bar",
  "kind.kavarna": "Café",
  "kind.cajovna": "Teestube",
  "kind.stanek": "Stand",
  "kind.jidelna": "Kantine",
  "kind.mlecny_bar": "Milchbar",
  "kind.food_truck": "Food Truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistro",

  "status.pending": "Wartet auf Genehmigung",
  "status.approved": "Genehmigt",
  "status.rejected": "Abgelehnt",
  "status.hidden": "Versteckt",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} Mon. gratis bis {date}",
  "auth.checkEmail": "Bitte überprüfen Sie Ihren Posteingang und bestätigen Sie Ihre Registrierung, indem Sie auf den Link klicken, den wir Ihnen gesendet haben.",
  "home.latest.title": "Neueste Betriebe",
  "home.latest.sub": "Zuletzt hinzugefügte genehmigte Betriebe.",
  "home.latest.empty": "Noch keine genehmigten Betriebe.",
  "home.latest.viewAll": "Auf der Karte anzeigen →",
  "reg.descMultiHint": "Sie können die Beschreibung in einer oder mehreren Sprachen ausfüllen. Besucher sehen sie in ihrer Sprache; fehlt sie, wird eine andere verfügbare verwendet.",
  "vakanty.title": "Mein Dashboard",
  "vakanty.welcomeWithName": "Willkommen, {name}. Unten siehst du die Anzahl der Vacants in deinem Konto.",
  "vakanty.welcomeNoName": "Willkommen. Unten siehst du die Anzahl der Vacants in deinem Konto.",
  "vakanty.purchasedLabel": "Erworbene Vacants",
  "vakanty.emptyHint": "Du hast noch keine Vacants. Bitte den Administrator um eine Erhöhung.",
  "vakanty.featuresTitle": "Funktionen und Informationen",
  "vakanty.kindFeature": "Funktion",
  "vakanty.kindInfo": "Information",
  "vakanty.open": "Öffnen",
 "vakanty.loading": "Lädt…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Internes Dokument, ausschließlich für Vacant-Eigentümer verfügbar.",
 "vakanty.bp.strictHeader": "Streng vertraulich (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Dieses Dokument ist streng vertraulich und stellt ein Geschäftsgeheimnis der For Direct s.r.o. dar. Das Dokument darf nicht heruntergeladen, weitergeleitet, kopiert, geteilt, veröffentlicht oder auf andere Weise an Dritte weitergegeben werden — auch nicht in Teilen, als Screenshot, Foto oder Transkript. Das Dokument steht ausschließlich zur Online-Ansicht auf dieser Seite und nur dem angemeldeten Vacant-Eigentümer zur Verfügung. Jede unbefugte Nutzung, Vervielfältigung oder Weitergabe stellt einen Verstoß gegen vertragliche Pflichten dar und kann zivil- und strafrechtliche Folgen haben. Ein Verstoß gegen diese Vertraulichkeitsvereinbarung wird mit einer Vertragsstrafe in Höhe von 5.000.000 EUR (in Worten: fünf Millionen Euro) pro Einzelverstoß geahndet.",
 "vakanty.bp.paragraph2": "Mit dem Ankreuzen des Kästchens unten bestätigst du, dass du diese Regeln gelesen und verstanden hast und dich zu deren Einhaltung verpflichtest. Deine Zustimmung wird mit einem Zeitstempel gespeichert.",
 "vakanty.bp.acceptedOn": "NDA angenommen am {date}.",
 "vakanty.bp.checkboxLabel": "Ich erkläre, dass ich die oben stehenden Vertraulichkeitsbedingungen gelesen habe, ihnen zustimme und mich verpflichte, dieses Dokument weder zu teilen noch zu verbreiten.",
 "vakanty.bp.acceptButton": "NDA akzeptieren und anzeigen",
 "vakanty.bp.showButton": "Business Plan anzeigen",
 "vakanty.bp.hideButton": "Dokument ausblenden",
 "vakanty.bp.viewerNotice": "Streng vertraulich — nicht kopieren, teilen oder herunterladen. Ansicht nur auf dieser Seite.",
 "vakanty.bp.mustCheckFirst": "Bitte zuerst die NDA-Zustimmung ankreuzen.",
 "vakanty.bp.acceptedToast": "NDA angenommen. Du kannst das Dokument jetzt ansehen.",
  "common.error": "Fehler",
  "common.tryAgain": "Erneut versuchen",
  "common.goHome": "Zur Startseite",
  "common.pageNotFound": "Seite nicht gefunden",
  "common.pageNotFoundDesc": "Die gesuchte Seite existiert nicht oder wurde verschoben.",
  "common.errorTitle": "Diese Seite konnte nicht geladen werden",
  "common.errorDesc": "Etwas ist schiefgelaufen. Bitte aktualisiere oder gehe zur Startseite.",
  "auth.enterEmailFirst": "Bitte zuerst E-Mail eingeben.",
  "auth.resendResult": "Falls das Konto existiert und unbestätigt ist, haben wir einen neuen Link gesendet. Verbleibende Versuche: {remaining}.",
  "auth.resendFailed": "E-Mail konnte nicht gesendet werden.",
  "auth.emailAlreadyRegistered": "Diese E-Mail ist bereits registriert. Prüfe deinen Posteingang oder melde dich an.",
  "auth.resending": "Sende…",
  "auth.resendConfirmation": "Bestätigungs-E-Mail erneut senden",
  "auth.signInDesc": "Melde dich bei Cerveza.International an oder registriere dich.",
  "auth.pageTitle": "Anmelden — Cerveza.International",
  "authReset.pageTitle": "Passwort setzen — Cerveza.International",
  "authReset.title": "Neues Passwort setzen",
  "authReset.needLink": "Bitte öffne diese Seite über den Link aus der erhaltenen E-Mail.",
  "authReset.newPassword": "Neues Passwort",
  "authReset.passwordAgain": "Passwort wiederholen",
  "authReset.saving": "Speichere…",
  "authReset.save": "Passwort speichern",
  "authReset.tooShort": "Passwort muss mindestens 8 Zeichen haben.",
  "authReset.mismatch": "Passwörter stimmen nicht überein.",
  "authReset.saved": "Passwort gesetzt.",
  "unsub.pageTitle": "Abmelden — Cerveza.International",
  "unsub.title": "Newsletter abbestellen",
  "unsub.checking": "Link wird überprüft…",
  "unsub.confirmPrompt": "Bestätige, dass du dich von den E-Mails von Cerveza.International abmelden möchtest.",
  "unsub.confirmBtn": "Abmeldung bestätigen",
  "unsub.processing": "Abmeldung läuft…",
  "unsub.done": "Erledigt. Du wurdest abgemeldet.",
  "unsub.already": "Diese Adresse ist bereits abgemeldet.",
  "unsub.invalid": "Der Link ist ungültig oder abgelaufen.",
  "unsub.errorVerify": "Link konnte nicht überprüft werden.",
  "unsub.errorGeneric": "Etwas ist schiefgelaufen.",
  "unsub.errorNetwork": "Netzwerkfehler.",
  "pay.successPageTitle": "Zahlung erfolgreich — Cerveza.International",
  "pay.successTitle": "Zahlung erfolgreich",
  "pay.successDesc": "Danke. Deine Bestellung wird verarbeitet — Link oder Foto wird innerhalb weniger Sekunden nach der Bestätigung aktiv.",
  "pay.backToMy": "Zurück zu Meine Betriebe",
  "pay.cancelPageTitle": "Zahlung abgebrochen — Cerveza.International",
  "pay.cancelTitle": "Zahlung abgebrochen",
  "pay.cancelDesc": "Es wurde nichts abgebucht. Du kannst die Zahlung jederzeit von Meine Betriebe wiederholen.",
  "profile.errorTitle": "Fehler",
  "profile.notFound": "Betrieb nicht gefunden",
  "profile.backToMap": "Zurück zur Karte",
  "profile.noDescription": "Noch keine Beschreibung. Füge sie in den Sprachen hinzu, in denen du den Betrieb präsentieren möchtest.",
  "profile.editDescription": "Beschreibung bearbeiten",
  "profile.multiLangDesc": "Mehrsprachige Beschreibung",
  "profile.maxChars": "Max. {n} Zeichen pro Sprache",
  "profile.descriptionsSaved": "Beschreibungen gespeichert",
  "profile.saveFailed": "Speichern fehlgeschlagen",
  "profile.save": "Speichern",
  "profile.cancel": "Abbrechen",
  "profile.linksHeader": "Links",
  "profile.viewOnOSM": "Auf OpenStreetMap anzeigen",
  "profile.descPlaceholder": "Beschreibung ({lang})",
  "reg.pageTitle": "Betrieb registrieren — Cerveza.International",
  "reg.pageDesc": "Registriere deinen Betrieb im globalen Katalog Cerveza.International.",
  "reg.maxTwoKinds": "Maximal 2 Kategorien wählbar.",
  "reg.confirmedKinds": "Bestätigte Kategorien: {list}",
  "share.warnFallback": "Hinweis: Vorlage ist nicht in „{lang}“ übersetzt, das tschechische Original wird gesendet.",
  "share.copiedMsg": "Nachricht kopiert — füge sie in die gewünschte App ein.",
  "share.copyFailed": "Kopieren fehlgeschlagen.",
  "share.copiedLink": "Link kopiert.",
  "share.copyLink": "Link kopieren",
  "share.copyMessage": "Nachricht kopieren",
  "my.photoUploadedPending": "Foto hochgeladen, wartet auf Admin-Freigabe.",
  "my.photoPendingApproval": "wartet auf Freigabe",
  "my.rejectReason": "Ablehnungsgrund: {reason}",
  "my.rejectedShort": "abgelehnt",
  "my.freeOutOf": "{free}/3 gratis · weitere: {price}",
  "my.photoPack": "Foto-Paket:",
  "my.pieces": "Stk",
  "majitele.pageTitle": "Vacant-Besitzer — Cerveza.International",

  // Downloads section
  "downloads.title": "Downloads",
  "downloads.subtitle": "Handbücher und Anleitungen zum Herunterladen.",
  "downloads.vakantOnly": "Nur für Vacant-Besitzer",
  "downloads.signInToView": "Zum Anzeigen anmelden",
  "downloads.downloadPdf": "PDF herunterladen",
  "downloads.empty": "Noch keine Handbücher verfügbar.",

};
const pt: Dict = {
  "nav.home": "Início",
  "nav.back": "Voltar",
  "nav.katalog": "Catálogo",
  "nav.mapa": "Mapa",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Meu painel",
  "nav.proMajitele": "Para proprietários",
  "nav.admin": "Admin",
  "nav.cenik": "Preços",
  "nav.mujUcet": "Minha conta",
  "nav.odhlasit": "Sair",
  "nav.prihlasit": "Entrar",
  "nav.vakanty": "Proprietários de Vacants",

  "photos.title": "Fotos",
  "photos.add": "Adicionar foto",
  "photos.maxSize": "Máx. 3 MB / foto. As primeiras 3 fotos grátis, adicionais: {price} pagamento único.",
  "photos.tooLarge": "A foto excede 3 MB.",
  "photos.invalidType": "O ficheiro deve ser uma imagem.",
  "photos.uploadFailed": "Falha no envio: {msg}",
  "photos.freeAdded": "Foto adicionada gratuitamente.",
  "photos.paidPending": "Foto criada, aguarda pagamento.",
  "photos.deleted": "Foto eliminada.",
  "photos.priceLabel": "Preço por cada foto adicional",

  "home.title.a": "Descubra os sabores do mundo num",
  "home.title.b": "só lugar",
  "home.subtitle": "Desde bistrôs familiares em Praga a bares escondidos em Barcelona. Um guia global para amantes da gastronomia autêntica.",
  "home.search.what": "O que procura? (ex. Tapas)",
  "home.search.where": "Onde? (ex. Lisboa)",
  "home.search.submit": "Procurar",

  "cat.restaurace": "Restaurantes",
  "cat.bary": "Bares",
  "cat.kavarny": "Cafés",
  "cat.cajovny": "Casas de chá",
  "cat.stanky": "Bancas",
  "cat.jidelny": "Cantinas",
  "cat.mlecnyBar": "Bares de leite",
  "cat.foodTruck": "Food trucks",
  "cat.rychleObcerstveni": "Fast Foods",
  "cat.bistra": "Bistrôs",

  "home.mapaCta": "Explorar o mapa do mundo",

  "home.featured.title": "Locais em destaque",
  "home.featured.sub": "As joias mais bem avaliadas da nossa comunidade.",
  "home.featured.all": "Ver todos",

  "dc.tag": "Programa de fidelidade",
  "dc.title": "Torne-se membro do Direct Club",
  "dc.text": "Ganhe pontos bónus em cada visita, aproveite descontos exclusivos e descubra menus especiais disponíveis apenas para membros da rede Cerveza.International.",
  "dc.register": "Registar gratuitamente",
  "dc.how": "Como funciona?",
  "dc.status": "Estado",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Número do cartão",
  "dc.points": "Pontos acumulados",

  "owner.title": "É proprietário de um local gastronómico?",
  "owner.text": "Junte-se a milhares de locais e chegue a clientes de todo o mundo. O registo é rápido e gratuito.",
  "owner.cta": "Adicionar o meu local",

  "footer.rights": "Todos os direitos reservados",
  "footer.about": "Sobre nós",
  "footer.terms": "Termos",
  "footer.privacy": "Privacidade",
  "footer.contact": "Contacto",

  "auth.signIn": "Entrar",
  "auth.signUp": "Registo",
  "auth.tabSignIn": "Entrar",
  "auth.tabSignUp": "Criar conta",
  "auth.google": "Continuar com Google",
  "auth.apple": "Continuar com Apple",
  "auth.or": "ou",
  "auth.email": "Email",
  "auth.password": "Palavra-passe",
  "auth.passwordMin": "Palavra-passe (mín. 6 caracteres)",
  "auth.name": "Nome completo",
  "auth.submitSignIn": "Entrar",
  "auth.submitSignInLoading": "A entrar…",
  "auth.submitSignUp": "Criar conta",
  "auth.submitSignUpLoading": "A registar…",
  "auth.firstAdminNote": "A primeira conta registada torna-se automaticamente administrador do sistema.",
  "auth.back": "← Voltar ao site",
  "auth.welcomeBack": "Bem-vindo de volta!",
  "auth.created": "Conta criada.",
  "auth.oauthFailed": "Falha ao entrar",

  "mapa.tag": "Mapa",
  "mapa.loading": "A carregar locais…",
  "mapa.count": "locais aprovados",
  "mapa.kindHeader": "Tipo de local",
  "mapa.addBusiness": "Adicionar local",
  "mapa.showUsers": "Membros do Direct Club",
  "mapa.showAnon": "Visitantes",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Estabelecimento do catálogo",
  "mapa.catalog.brewery": "Cervejaria do catálogo",
  "mapa.catalog.imported": "Importado do catálogo XLSX Cerveza Data Standard",
  "mapa.catalog.detail": "Detalhe",
  "mapa.review.discovery": "Não verificado",
  "mapa.review.pending": "Aguarda revisão",
  "mapa.review.verified": "Verificado",
  "mapa.review.rejected": "Rejeitado",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "América do Norte",
  "mapa.continent.southAmerica": "América do Sul",
  "mapa.continent.asia": "Ásia",
  "mapa.continent.africa": "África",
  "mapa.continent.oceania": "Oceania",
  "mapa.continent.other": "Outros",
  "mapa.memberBadge": "Membro Direct Club",
  "mapa.partnerHint": "Empresa parceira verificada registada na Cerveza.International",
  "mapa.memberPrivacyNote": "Utilizador registado · localização aproximada por privacidade",
  "mapa.anonPrivacyNote": "Visitante anónimo · localização visível 24 h, depois removida",
  "mapa.legend.title": "Legenda",
  "mapa.userPin.title": "Membro registado da Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Localização aproximada (sem morada exata)",
  "mapa.viewProfile": "Perfil do local",
  "mapa.empty.a": "Ainda sem locais aprovados. Seja o primeiro —",
  "mapa.empty.b": "registar um local",
  "mapa.anonPin.title": "Visitante anónimo",
  "mapa.anonPin.subtitle": "Ativo nas últimas 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtros",
  "mapa.filters.search": "Pesquisar",
  "mapa.filters.searchPlaceholder": "Nome, cidade, descrição…",
  "mapa.filters.location": "Localização",
  "mapa.filters.continent": "Continente",
  "mapa.filters.allContinents": "Todos os continentes",
  "mapa.filters.country": "País",
  "mapa.filters.allCountries": "Todos os países",
  "mapa.filters.region": "Região",
  "mapa.filters.regionEmpty": "Região (vazio)",
  "mapa.filters.allRegions": "Todas as regiões",
  "mapa.filters.city": "Cidade",
  "mapa.filters.allCities": "Todas as cidades",
  "mapa.filters.postal": "Código postal",
  "mapa.filters.partnership": "Parceria",
  "mapa.filters.reset": "Repor filtros",
  "mapa.empty.filtered": "Nenhum local corresponde ao filtro.",
  "mapa.empty.resetFilters": "Repor filtros",
  "mapa.location.missing": "Localização em falta",
  "mapa.location.stale": "Localização desatualizada",
  "mapa.location.fresh": "Localização recente",
  "mapa.location.refresh": "Atualizar localização",
  "mapa.location.lastSeen": "Última vez",
  "mapa.location.hidden": "oculto no mapa",
  "mapa.location.never": "nunca",
  "mapa.location.toastOk": "Localização atualizada",
  "mapa.location.toastCached": "Valor recente em cache utilizado.",
  "mapa.location.toastSource": "Fonte",
  "mapa.location.toastFail": "Não foi possível determinar a localização",
  "mapa.location.toastFailDesc": "Tente novamente mais tarde (geo indisponível).",
  "mapa.location.toastError": "Erro ao atualizar localização",
  "mapa.breweries": "Cervejarias",
  "mapa.breweriesHint": "Cervejarias e produtores de cerveja",
  "mapa.showMembersNoBusiness": "Membros sem negócio",
  "mapa.memberNoBusiness.badge": "Membro sem negócio",
  "mapa.memberNoBusiness.title": "Membro pronto para construir a rede",
  "mapa.memberNoBusiness.subtitle": "Pode recomendar novos membros, negócios e avaliar locais visitados.",
  "mapa.memberNoBusiness.recommendBusiness": "Recomendar negócio",
  "mapa.memberNoBusiness.inviteMember": "Recomendar membro",
  "mapa.memberNoBusiness.rateVisited": "Avaliar negócio visitado",
  "mapa.memberNoBusiness.privacy": "Localização aproximada para proteção de privacidade",
  "mapa.memberNoBusiness.hint": "Membros registados que não têm negócio — candidatos ideais para recomendações",

  "my.title": "Meus locais",
  "my.sub": "Gira os seus locais e respetivos links.",
  "my.add": "Adicionar local",
  "my.loading": "A carregar…",
  "my.empty.a": "Ainda não tem locais.",
  "my.empty.b": "Registar o primeiro",
  "my.links": "Links",
  "my.noLinks": "Sem links.",
  "my.free": "Grátis",
  "my.paid": "Pago",
  "my.active": "Ativo",
  "my.rejected": "Rejeitado",
  "my.pendingPayment": "Aguarda pagamento",
  "my.pay": "Pagar",
  "my.payTooltip": "Pagamento online em breve disponível",
  "my.addLink": "Adicionar link",
  "my.linkInfo": "Os links para <strong>directclub.digital</strong> / <strong>direct.club</strong> são grátis (máx. 3 por local). Outros links externos: <strong>{price}</strong> pagamento único (link permanente após pagamento).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Etiqueta (opcional)",
  "my.addFree": "Adicionar grátis",
  "my.addPaid": "Adicionar por {price}",
  "my.freeAdded": "Link gratuito adicionado e publicado.",
  "my.paidPending": "Link criado, aguarda pagamento.",
  "my.freeLimit": "Limite de 3 links gratuitos do directclub atingido.",
  "my.willBeFree": "✓ Link do directclub — será gratuito e publicado imediatamente.",
  "my.willBePaid": "Link externo — requer pagamento de {price} (publicado após pagamento).",
  "my.invalidUrl": "URL inválido",

  "reg.back": "← Voltar ao site",
  "reg.moderation": "Moderação híbrida",
  "reg.title": "Registo de local",
  "reg.desc": "Após introduzir o código postal, preenchemos automaticamente a cidade e as coordenadas a partir da base de dados de códigos postais. Proprietários verificados são aprovados automaticamente, outros registos passam por moderação.",
  "reg.name": "Nome do local",
  "reg.namePlaceholder": "ex. Café Vinohrady",
  "reg.kind": "Tipo de local",
  "reg.kindPlaceholder": "Selecione o tipo",
  "reg.country": "País",
  "reg.psc": "Código postal",
  "reg.pscPlaceholder": "ex. 1000-001",
  "reg.pscInvalid": "Formato de código postal inválido (CZ: 5 dígitos).",
  "reg.searching": "A procurar cidade…",
  "reg.notFound": "Não encontrámos nenhuma cidade na base para o código postal {postal}.",
  "reg.foundOne": "Cidade encontrada:",
  "reg.foundMany": "Encontradas {count} cidades — escolha a correta:",
  "reg.coords": "Coordenadas:",
  "reg.mapPreview": "Pré-visualização da localização no mapa",
  "reg.mapHint": "Arraste o pin ou clique no mapa para ajustar a localização.",
  "reg.street": "Rua e número (opcional)",
  "reg.streetPlaceholder": "ex. Korunní 12",
  "reg.descLabel": "Descrição breve (opcional)",
  "reg.descPlaceholder": "O que torna o seu local especial?",
  "reg.cancel": "Cancelar",
  "reg.submit": "Enviar para registo",
  "reg.submitted": "Local enviado para moderação.",
  "reg.notSignedIn": "Não está autenticado",
  "reg.pickCity": "Selecione uma cidade válida dos resultados do código postal",
  "reg.invalidData": "Dados inválidos",
  "reg.gate.title": "Confirmação de titularidade do estabelecimento",
  "reg.gate.desc": "Só o proprietário ou o responsável pode registar um estabelecimento. Selecione 1–2 categorias a que o seu estabelecimento pertence.",
  "reg.gate.confirmBefore": "Confirmo que sou",
  "reg.gate.confirmStrong": "proprietário ou responsável",
  "reg.gate.confirmAfter": "do estabelecimento que quero registar e que pertence a uma das categorias indicadas abaixo.",
  "reg.gate.kindsLabel": "Categoria do estabelecimento (escolha 1–2)",
  "reg.gate.selected": "Selecionadas: {count} / 2",
  "reg.continue": "Continuar",

  "kind.restaurace": "Restaurante",
  "kind.bar": "Bar",
  "kind.kavarna": "Café",
  "kind.cajovna": "Casa de chá",
  "kind.stanek": "Banca",
  "kind.jidelna": "Cantina",
  "kind.mlecny_bar": "Bar de leite",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistrô",

  "status.pending": "Aguarda aprovação",
  "status.approved": "Aprovado",
  "status.rejected": "Rejeitado",
  "status.hidden": "Oculto",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} meses grátis até {date}",
  "auth.checkEmail": "Por favor, verifique sua caixa de entrada e confirme seu cadastro clicando no link que enviamos.",
  "home.latest.title": "Negócios recentes",
  "home.latest.sub": "Negócios aprovados adicionados recentemente.",
  "home.latest.empty": "Ainda não há negócios aprovados.",
  "home.latest.viewAll": "Ver no mapa →",
  "reg.descMultiHint": "Pode preencher a descrição num ou mais idiomas. Os visitantes verão no seu idioma; se faltar, será usado outro disponível.",
  "vakanty.title": "Meu painel",
  "vakanty.welcomeWithName": "Bem-vindo, {name}. Abaixo vê o número de Vacants na sua conta.",
  "vakanty.welcomeNoName": "Bem-vindo. Abaixo vê o número de Vacants na sua conta.",
  "vakanty.purchasedLabel": "Vacants adquiridos",
  "vakanty.emptyHint": "Ainda não tem Vacants. Peça ao administrador para aumentar.",
  "vakanty.featuresTitle": "Funções e informações",
  "vakanty.kindFeature": "Função",
  "vakanty.kindInfo": "Informação",
  "vakanty.open": "Abrir",
 "vakanty.loading": "A carregar…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Documento interno disponível exclusivamente para os proprietários de Vacants.",
 "vakanty.bp.strictHeader": "Estritamente confidencial (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Este documento é estritamente confidencial e constitui um segredo comercial da For Direct s.r.o.. O documento não pode ser descarregado, reencaminhado, copiado, partilhado, publicado ou de qualquer outra forma distribuído a terceiros — incluindo partes do mesmo, capturas de ecrã, fotografias ou transcrições. O documento está disponível apenas para consulta online nesta página e exclusivamente ao proprietário de Vacant autenticado. Qualquer utilização, reprodução ou distribuição não autorizada constitui uma violação das obrigações contratuais e pode dar origem a responsabilidade civil e criminal. A violação do presente acordo de confidencialidade está sujeita a uma penalidade contratual no valor de 5.000.000 EUR (cinco milhões de euros) por cada infração individual.",
 "vakanty.bp.paragraph2": "Ao assinalar a caixa abaixo, confirma que leu estas regras, que as compreende e que se compromete a cumpri-las. O seu consentimento será registado com data e hora.",
 "vakanty.bp.acceptedOn": "NDA aceite em {date}.",
 "vakanty.bp.checkboxLabel": "Declaro que li os termos de confidencialidade acima, que os aceito e que me comprometo a não partilhar nem distribuir este documento.",
 "vakanty.bp.acceptButton": "Aceitar NDA e visualizar",
 "vakanty.bp.showButton": "Mostrar Business Plan",
 "vakanty.bp.hideButton": "Ocultar documento",
 "vakanty.bp.viewerNotice": "Estritamente confidencial — não copiar, partilhar nem descarregar. Consulta apenas nesta página.",
 "vakanty.bp.mustCheckFirst": "Assinale primeiro a aceitação do NDA.",
 "vakanty.bp.acceptedToast": "NDA aceite. Já pode visualizar o documento.",
  "common.error": "Erro",
  "common.tryAgain": "Tentar novamente",
  "common.goHome": "Ir para o início",
  "common.pageNotFound": "Página não encontrada",
  "common.pageNotFoundDesc": "A página que procura não existe ou foi movida.",
  "common.errorTitle": "Esta página não carregou",
  "common.errorDesc": "Ocorreu um erro. Tente atualizar ou voltar ao início.",
  "auth.enterEmailFirst": "Introduza primeiro o e-mail.",
  "auth.resendResult": "Se a conta existir e não estiver confirmada, enviámos um novo link. Tentativas restantes: {remaining}.",
  "auth.resendFailed": "Não foi possível enviar o e-mail.",
  "auth.emailAlreadyRegistered": "Este e-mail já está registado. Verifique a sua caixa ou inicie sessão.",
  "auth.resending": "A enviar…",
  "auth.resendConfirmation": "Reenviar e-mail de confirmação",
  "auth.signInDesc": "Inicie sessão ou registe-se em Cerveza.International.",
  "auth.pageTitle": "Iniciar sessão — Cerveza.International",
  "authReset.pageTitle": "Definir palavra-passe — Cerveza.International",
  "authReset.title": "Definir nova palavra-passe",
  "authReset.needLink": "Abra esta página a partir do link no e-mail recebido.",
  "authReset.newPassword": "Nova palavra-passe",
  "authReset.passwordAgain": "Repita a palavra-passe",
  "authReset.saving": "A guardar…",
  "authReset.save": "Guardar palavra-passe",
  "authReset.tooShort": "A palavra-passe deve ter no mínimo 8 caracteres.",
  "authReset.mismatch": "As palavras-passe não coincidem.",
  "authReset.saved": "Palavra-passe definida.",
  "unsub.pageTitle": "Cancelar subscrição — Cerveza.International",
  "unsub.title": "Cancelar subscrição",
  "unsub.checking": "A verificar link…",
  "unsub.confirmPrompt": "Confirme que pretende cancelar a subscrição de e-mails da Cerveza.International.",
  "unsub.confirmBtn": "Confirmar cancelamento",
  "unsub.processing": "A cancelar…",
  "unsub.done": "Feito. Foi removido com sucesso.",
  "unsub.already": "Este endereço já foi removido.",
  "unsub.invalid": "O link é inválido ou expirou.",
  "unsub.errorVerify": "Não foi possível verificar o link.",
  "unsub.errorGeneric": "Algo correu mal.",
  "unsub.errorNetwork": "Erro de rede.",
  "pay.successPageTitle": "Pagamento efetuado — Cerveza.International",
  "pay.successTitle": "Pagamento efetuado com sucesso",
  "pay.successDesc": "Obrigado. A sua encomenda está a ser processada — o link ou foto será ativado em segundos após a confirmação.",
  "pay.backToMy": "Voltar a Os meus negócios",
  "pay.cancelPageTitle": "Pagamento cancelado — Cerveza.International",
  "pay.cancelTitle": "Pagamento cancelado",
  "pay.cancelDesc": "Nenhum valor foi cobrado. Pode repetir o pagamento na página Os meus negócios.",
  "profile.errorTitle": "Erro",
  "profile.notFound": "Negócio não encontrado",
  "profile.backToMap": "Voltar ao mapa",
  "profile.noDescription": "Ainda sem descrição. Adicione-a nos idiomas em que quer apresentar o negócio.",
  "profile.editDescription": "Editar descrição",
  "profile.multiLangDesc": "Descrição multilingue",
  "profile.maxChars": "Máx. {n} caracteres por idioma",
  "profile.descriptionsSaved": "Descrições guardadas",
  "profile.saveFailed": "Falha ao guardar",
  "profile.save": "Guardar",
  "profile.cancel": "Cancelar",
  "profile.linksHeader": "Ligações",
  "profile.viewOnOSM": "Ver no OpenStreetMap",
  "profile.descPlaceholder": "Descrição ({lang})",
  "reg.pageTitle": "Registar negócio — Cerveza.International",
  "reg.pageDesc": "Registe o seu negócio no catálogo global Cerveza.International.",
  "reg.maxTwoKinds": "Pode selecionar no máximo 2 categorias.",
  "reg.confirmedKinds": "Categorias confirmadas: {list}",
  "share.warnFallback": "Nota: o modelo não está traduzido para «{lang}», será enviado o original em checo.",
  "share.copiedMsg": "Mensagem copiada — cole na aplicação pretendida.",
  "share.copyFailed": "Falha ao copiar.",
  "share.copiedLink": "Ligação copiada.",
  "share.copyLink": "Copiar ligação",
  "share.copyMessage": "Copiar mensagem",
  "my.photoUploadedPending": "Foto carregada, a aguardar aprovação do admin.",
  "my.photoPendingApproval": "a aguardar aprovação",
  "my.rejectReason": "Motivo da rejeição: {reason}",
  "my.rejectedShort": "rejeitada",
  "my.freeOutOf": "{free}/3 grátis · próxima: {price}",
  "my.photoPack": "Pacote de fotos:",
  "my.pieces": "un",
  "majitele.pageTitle": "Proprietários de Vacants — Cerveza.International",

  // Downloads section
  "downloads.title": "Downloads",
  "downloads.subtitle": "Manuais e guias para baixar.",
  "downloads.vakantOnly": "Apenas para proprietários de Vacants",
  "downloads.signInToView": "Entre para visualizar",
  "downloads.downloadPdf": "Baixar PDF",
  "downloads.empty": "Ainda não há manuais disponíveis.",

};
const fr: Dict = {
  "nav.home": "Accueil",
  "nav.back": "Retour",
  "nav.katalog": "Catalogue",
  "nav.mapa": "Carte",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Mon tableau",
  "nav.proMajitele": "Pour les propriétaires",
  "nav.admin": "Admin",
  "nav.cenik": "Tarifs",
  "nav.mujUcet": "Mon compte",
  "nav.odhlasit": "Déconnexion",
  "nav.prihlasit": "Se connecter",
  "nav.vakanty": "Propriétaires de Vacants",

  "photos.title": "Photos",
  "photos.add": "Ajouter une photo",
  "photos.maxSize": "Max. 3 Mo / photo. 3 premières photos gratuites, suivantes : {price} paiement unique.",
  "photos.tooLarge": "La photo dépasse 3 Mo.",
  "photos.invalidType": "Le fichier doit être une image.",
  "photos.uploadFailed": "Échec de l'envoi : {msg}",
  "photos.freeAdded": "Photo ajoutée gratuitement.",
  "photos.paidPending": "Photo créée, en attente de paiement.",
  "photos.deleted": "Photo supprimée.",
  "photos.priceLabel": "Prix par photo supplémentaire",

  "home.title.a": "Découvrez les saveurs du monde en",
  "home.title.b": "un seul lieu",
  "home.subtitle": "Des bistrots familiaux de Prague aux bars cachés de Barcelone. Un guide mondial pour les amateurs de gastronomie authentique.",
  "home.search.what": "Que cherchez-vous ? (ex. Tapas)",
  "home.search.where": "Où ? (ex. Paris)",
  "home.search.submit": "Rechercher",

  "cat.restaurace": "Restaurants",
  "cat.bary": "Bars",
  "cat.kavarny": "Cafés",
  "cat.cajovny": "Salons de thé",
  "cat.stanky": "Stands",
  "cat.jidelny": "Cantines",
  "cat.mlecnyBar": "Bars laitiers",
  "cat.foodTruck": "Food trucks",
  "cat.rychleObcerstveni": "Fast-foods",
  "cat.bistra": "Bistrots",

  "home.mapaCta": "Explorer la carte du monde",

  "home.featured.title": "Établissements en vedette",
  "home.featured.sub": "Les pépites les mieux notées de notre communauté.",
  "home.featured.all": "Tout voir",

  "dc.tag": "Programme de fidélité",
  "dc.title": "Devenez membre du Direct Club",
  "dc.text": "Gagnez des points bonus à chaque visite, profitez de remises exclusives et découvrez des menus spéciaux réservés aux membres du réseau Cerveza.International.",
  "dc.register": "S'inscrire gratuitement",
  "dc.how": "Comment ça marche ?",
  "dc.status": "Statut",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Numéro de carte",
  "dc.points": "Points cumulés",

  "owner.title": "Vous tenez un établissement gastronomique ?",
  "owner.text": "Rejoignez des milliers d'établissements et touchez des clients du monde entier. L'inscription est rapide et gratuite.",
  "owner.cta": "Ajouter mon établissement",

  "footer.rights": "Tous droits réservés",
  "footer.about": "À propos",
  "footer.terms": "Conditions",
  "footer.privacy": "Confidentialité",
  "footer.contact": "Contact",

  "auth.signIn": "Connexion",
  "auth.signUp": "Inscription",
  "auth.tabSignIn": "Se connecter",
  "auth.tabSignUp": "Créer un compte",
  "auth.google": "Continuer avec Google",
  "auth.apple": "Continuer avec Apple",
  "auth.or": "ou",
  "auth.email": "Email",
  "auth.password": "Mot de passe",
  "auth.passwordMin": "Mot de passe (min. 6 caractères)",
  "auth.name": "Nom et prénom",
  "auth.submitSignIn": "Se connecter",
  "auth.submitSignInLoading": "Connexion…",
  "auth.submitSignUp": "Créer un compte",
  "auth.submitSignUpLoading": "Inscription…",
  "auth.firstAdminNote": "Le premier compte enregistré devient automatiquement administrateur du système.",
  "auth.back": "← Retour au site",
  "auth.welcomeBack": "Bon retour !",
  "auth.created": "Compte créé.",
  "auth.oauthFailed": "Échec de la connexion",

  "mapa.tag": "Carte",
  "mapa.loading": "Chargement des établissements…",
  "mapa.count": "établissements approuvés",
  "mapa.kindHeader": "Type d'établissement",
  "mapa.addBusiness": "Ajouter un établissement",
  "mapa.showUsers": "Membres du Direct Club",
  "mapa.showAnon": "Invités",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Établissement du catalogue",
  "mapa.catalog.brewery": "Brasserie du catalogue",
  "mapa.catalog.imported": "Importé du catalogue XLSX Cerveza Data Standard",
  "mapa.catalog.detail": "Détail",
  "mapa.review.discovery": "Non vérifié",
  "mapa.review.pending": "À vérifier",
  "mapa.review.verified": "Vérifié",
  "mapa.review.rejected": "Rejeté",
  "mapa.continent.europe": "Europe",
  "mapa.continent.northAmerica": "Amérique du Nord",
  "mapa.continent.southAmerica": "Amérique du Sud",
  "mapa.continent.asia": "Asie",
  "mapa.continent.africa": "Afrique",
  "mapa.continent.oceania": "Océanie",
  "mapa.continent.other": "Autres",
  "mapa.memberBadge": "Membre Direct Club",
  "mapa.partnerHint": "Entreprise partenaire vérifiée enregistrée sur Cerveza.International",
  "mapa.memberPrivacyNote": "Utilisateur enregistré · position approximative pour la confidentialité",
  "mapa.anonPrivacyNote": "Visiteur anonyme · position visible 24 h, puis supprimée",
  "mapa.legend.title": "Légende",
  "mapa.userPin.title": "Membre inscrit de Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Localisation approximative (sans adresse précise)",
  "mapa.viewProfile": "Profil de l'établissement",
  "mapa.empty.a": "Aucun établissement approuvé. Soyez le premier —",
  "mapa.empty.b": "enregistrer un établissement",
  "mapa.anonPin.title": "Visiteur anonyme",
  "mapa.anonPin.subtitle": "Actif depuis 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtres",
  "mapa.filters.search": "Rechercher",
  "mapa.filters.searchPlaceholder": "Nom, ville, description…",
  "mapa.filters.location": "Localisation",
  "mapa.filters.continent": "Continent",
  "mapa.filters.allContinents": "Tous les continents",
  "mapa.filters.country": "Pays",
  "mapa.filters.allCountries": "Tous les pays",
  "mapa.filters.region": "Région",
  "mapa.filters.regionEmpty": "Région (vide)",
  "mapa.filters.allRegions": "Toutes les régions",
  "mapa.filters.city": "Ville",
  "mapa.filters.allCities": "Toutes les villes",
  "mapa.filters.postal": "Code postal",
  "mapa.filters.partnership": "Partenariat",
  "mapa.filters.reset": "Réinitialiser les filtres",
  "mapa.empty.filtered": "Aucun établissement ne correspond au filtre.",
  "mapa.empty.resetFilters": "Réinitialiser les filtres",
  "mapa.location.missing": "Localisation manquante",
  "mapa.location.stale": "Localisation obsolète",
  "mapa.location.fresh": "Localisation récente",
  "mapa.location.refresh": "Actualiser la localisation",
  "mapa.location.lastSeen": "Dernière fois",
  "mapa.location.hidden": "masqué sur la carte",
  "mapa.location.never": "jamais",
  "mapa.location.toastOk": "Localisation mise à jour",
  "mapa.location.toastCached": "Valeur récente en cache utilisée.",
  "mapa.location.toastSource": "Source",
  "mapa.location.toastFail": "Impossible de déterminer la localisation",
  "mapa.location.toastFailDesc": "Réessayez plus tard (géo indisponible).",
  "mapa.location.toastError": "Erreur d'actualisation de la localisation",
  "mapa.breweries": "Brasseries",
  "mapa.breweriesHint": "Brasseries et producteurs de bière",
  "mapa.showMembersNoBusiness": "Membres sans établissement",
  "mapa.memberNoBusiness.badge": "Membre sans établissement",
  "mapa.memberNoBusiness.title": "Membre prêt à construire le réseau",
  "mapa.memberNoBusiness.subtitle": "Peut recommander de nouveaux membres, établissements et noter les lieux visités.",
  "mapa.memberNoBusiness.recommendBusiness": "Recommander un établissement",
  "mapa.memberNoBusiness.inviteMember": "Recommander un membre",
  "mapa.memberNoBusiness.rateVisited": "Noter l'établissement visité",
  "mapa.memberNoBusiness.privacy": "Position approximative pour protéger la vie privée",
  "mapa.memberNoBusiness.hint": "Membres inscrits qui ne possèdent pas d'établissement — candidats idéaux pour les recommandations",

  "my.title": "Mes établissements",
  "my.sub": "Gérez vos établissements et leurs liens.",
  "my.add": "Ajouter un établissement",
  "my.loading": "Chargement…",
  "my.empty.a": "Vous n'avez encore aucun établissement.",
  "my.empty.b": "Enregistrer le premier",
  "my.links": "Liens",
  "my.noLinks": "Aucun lien.",
  "my.free": "Gratuit",
  "my.paid": "Payant",
  "my.active": "Actif",
  "my.rejected": "Rejeté",
  "my.pendingPayment": "En attente de paiement",
  "my.pay": "Payer",
  "my.payTooltip": "Le paiement en ligne sera bientôt disponible",
  "my.addLink": "Ajouter un lien",
  "my.linkInfo": "Les liens vers <strong>directclub.digital</strong> / <strong>direct.club</strong> sont gratuits (max. 3 par établissement). Autres liens externes : <strong>{price}</strong> paiement unique (lien permanent après paiement).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Libellé (facultatif)",
  "my.addFree": "Ajouter gratuitement",
  "my.addPaid": "Ajouter pour {price}",
  "my.freeAdded": "Lien gratuit ajouté et publié.",
  "my.paidPending": "Lien créé, en attente de paiement.",
  "my.freeLimit": "Limite de 3 liens gratuits directclub atteinte.",
  "my.willBeFree": "✓ Lien directclub — sera gratuit et publié immédiatement.",
  "my.willBePaid": "Lien externe — paiement de {price} requis (publication après paiement).",
  "my.invalidUrl": "URL invalide",

  "reg.back": "← Retour au site",
  "reg.moderation": "Modération hybride",
  "reg.title": "Inscription d'un établissement",
  "reg.desc": "Après la saisie du code postal, nous remplissons automatiquement la ville et les coordonnées depuis la base de données des codes postaux. Les propriétaires vérifiés sont approuvés automatiquement, les autres entrées passent par la modération.",
  "reg.name": "Nom de l'établissement",
  "reg.namePlaceholder": "ex. Café Vinohrady",
  "reg.kind": "Type d'établissement",
  "reg.kindPlaceholder": "Sélectionner le type",
  "reg.country": "Pays",
  "reg.psc": "Code postal",
  "reg.pscPlaceholder": "ex. 75001",
  "reg.pscInvalid": "Format de code postal invalide (CZ : 5 chiffres).",
  "reg.searching": "Recherche de la ville…",
  "reg.notFound": "Aucune ville trouvée dans la base pour le code postal {postal}.",
  "reg.foundOne": "Ville trouvée :",
  "reg.foundMany": "{count} villes trouvées — choisissez la bonne :",
  "reg.coords": "Coordonnées :",
  "reg.mapPreview": "Aperçu de l'emplacement sur la carte",
  "reg.mapHint": "Faites glisser le marqueur ou cliquez sur la carte pour ajuster l'emplacement.",
  "reg.street": "Rue et numéro (facultatif)",
  "reg.streetPlaceholder": "ex. Korunní 12",
  "reg.descLabel": "Brève description (facultative)",
  "reg.descPlaceholder": "Qu'est-ce qui rend votre établissement spécial ?",
  "reg.cancel": "Annuler",
  "reg.submit": "Envoyer pour inscription",
  "reg.submitted": "Établissement envoyé en modération.",
  "reg.notSignedIn": "Vous n'êtes pas connecté",
  "reg.pickCity": "Sélectionnez une ville valide parmi les résultats du code postal",
  "reg.invalidData": "Données invalides",
  "reg.gate.title": "Confirmation de propriété de l'établissement",
  "reg.gate.desc": "Seul le propriétaire ou l'exploitant peut enregistrer un établissement. Sélectionnez 1 à 2 catégories auxquelles appartient votre établissement.",
  "reg.gate.confirmBefore": "Je confirme être le",
  "reg.gate.confirmStrong": "propriétaire ou l'exploitant",
  "reg.gate.confirmAfter": "de l'établissement que je souhaite enregistrer, et qu'il appartient à l'une des catégories indiquées ci-dessous.",
  "reg.gate.kindsLabel": "Catégorie de l'établissement (choisir 1 à 2)",
  "reg.gate.selected": "Sélectionnées : {count} / 2",
  "reg.continue": "Continuer",

  "kind.restaurace": "Restaurant",
  "kind.bar": "Bar",
  "kind.kavarna": "Café",
  "kind.cajovna": "Salon de thé",
  "kind.stanek": "Stand",
  "kind.jidelna": "Cantine",
  "kind.mlecny_bar": "Bar laitier",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast-food",
  "kind.bistro": "Bistrot",

  "status.pending": "En attente d'approbation",
  "status.approved": "Approuvé",
  "status.rejected": "Rejeté",
  "status.hidden": "Masqué",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} mois gratuits jusqu'au {date}",
  "auth.checkEmail": "Veuillez vérifier votre boîte de réception et confirmer votre inscription en cliquant sur le lien que nous vous avons envoyé.",
  "home.latest.title": "Établissements récents",
  "home.latest.sub": "Établissements approuvés récemment ajoutés.",
  "home.latest.empty": "Aucun établissement approuvé pour le moment.",
  "home.latest.viewAll": "Voir sur la carte →",
  "reg.descMultiHint": "Vous pouvez remplir la description dans une ou plusieurs langues. Les visiteurs la verront dans leur langue ; si elle manque, une autre disponible sera utilisée.",
  "vakanty.title": "Mon tableau de bord",
  "vakanty.welcomeWithName": "Bienvenue, {name}. Vous voyez ci-dessous le nombre de Vacants sur votre compte.",
  "vakanty.welcomeNoName": "Bienvenue. Vous voyez ci-dessous le nombre de Vacants sur votre compte.",
  "vakanty.purchasedLabel": "Vacants achetés",
  "vakanty.emptyHint": "Vous n'avez pas encore de Vacants. Demandez à l'administrateur d'en ajouter.",
  "vakanty.featuresTitle": "Fonctions et informations",
  "vakanty.kindFeature": "Fonction",
  "vakanty.kindInfo": "Information",
  "vakanty.open": "Ouvrir",
 "vakanty.loading": "Chargement…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Document interne réservé exclusivement aux propriétaires de Vacants.",
 "vakanty.bp.strictHeader": "Strictement confidentiel (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Ce document est strictement confidentiel et constitue un secret d'affaires de For Direct s.r.o.. Le document ne peut pas être téléchargé, transféré, copié, partagé, publié ni distribué de quelque autre manière à des tiers — y compris ses parties, captures d'écran, photographies ou transcriptions. Le document n'est disponible qu'en consultation en ligne sur cette page et exclusivement pour le propriétaire de Vacant connecté. Toute utilisation, reproduction ou distribution non autorisée constitue une violation des obligations contractuelles et peut entraîner une responsabilité civile et pénale. Toute violation du présent accord de confidentialité est passible d'une pénalité contractuelle d'un montant de 5 000 000 EUR (cinq millions d'euros) pour chaque infraction individuelle.",
 "vakanty.bp.paragraph2": "En cochant la case ci-dessous, vous confirmez avoir lu ces règles, les comprendre et vous engager à les respecter. Votre consentement sera enregistré avec un horodatage.",
 "vakanty.bp.acceptedOn": "NDA accepté le {date}.",
 "vakanty.bp.checkboxLabel": "Je déclare avoir lu les conditions de confidentialité ci-dessus, les accepter et m'engager à ne pas partager ni distribuer ce document.",
 "vakanty.bp.acceptButton": "Accepter le NDA et afficher",
 "vakanty.bp.showButton": "Afficher le Business Plan",
 "vakanty.bp.hideButton": "Masquer le document",
 "vakanty.bp.viewerNotice": "Strictement confidentiel — ne pas copier, partager ni télécharger. Consultation uniquement sur cette page.",
 "vakanty.bp.mustCheckFirst": "Veuillez d'abord cocher l'acceptation du NDA.",
 "vakanty.bp.acceptedToast": "NDA accepté. Vous pouvez maintenant consulter le document.",
  "common.error": "Erreur",
  "common.tryAgain": "Réessayer",
  "common.goHome": "Retour à l'accueil",
  "common.pageNotFound": "Page introuvable",
  "common.pageNotFoundDesc": "La page que vous cherchez n'existe pas ou a été déplacée.",
  "common.errorTitle": "Cette page ne s'est pas chargée",
  "common.errorDesc": "Une erreur est survenue. Essayez de recharger ou revenez à l'accueil.",
  "auth.enterEmailFirst": "Veuillez d'abord saisir votre e-mail.",
  "auth.resendResult": "Si le compte existe et n'est pas confirmé, nous avons envoyé un nouveau lien. Tentatives restantes : {remaining}.",
  "auth.resendFailed": "Échec de l'envoi de l'e-mail.",
  "auth.emailAlreadyRegistered": "Cet e-mail est déjà enregistré. Vérifiez votre boîte ou connectez-vous.",
  "auth.resending": "Envoi…",
  "auth.resendConfirmation": "Renvoyer l'e-mail de confirmation",
  "auth.signInDesc": "Connectez-vous ou inscrivez-vous à Cerveza.International.",
  "auth.pageTitle": "Connexion — Cerveza.International",
  "authReset.pageTitle": "Définir le mot de passe — Cerveza.International",
  "authReset.title": "Définir un nouveau mot de passe",
  "authReset.needLink": "Ouvrez cette page à partir du lien reçu par e-mail.",
  "authReset.newPassword": "Nouveau mot de passe",
  "authReset.passwordAgain": "Confirmer le mot de passe",
  "authReset.saving": "Enregistrement…",
  "authReset.save": "Enregistrer le mot de passe",
  "authReset.tooShort": "Le mot de passe doit contenir au moins 8 caractères.",
  "authReset.mismatch": "Les mots de passe ne correspondent pas.",
  "authReset.saved": "Mot de passe défini.",
  "unsub.pageTitle": "Désabonnement — Cerveza.International",
  "unsub.title": "Désabonnement",
  "unsub.checking": "Vérification du lien…",
  "unsub.confirmPrompt": "Confirmez que vous souhaitez vous désabonner des e-mails de Cerveza.International.",
  "unsub.confirmBtn": "Confirmer le désabonnement",
  "unsub.processing": "Désabonnement…",
  "unsub.done": "Terminé. Vous avez été désabonné.",
  "unsub.already": "Cette adresse est déjà désabonnée.",
  "unsub.invalid": "Le lien est invalide ou a expiré.",
  "unsub.errorVerify": "Impossible de vérifier le lien.",
  "unsub.errorGeneric": "Une erreur est survenue.",
  "unsub.errorNetwork": "Erreur réseau.",
  "pay.successPageTitle": "Paiement réussi — Cerveza.International",
  "pay.successTitle": "Paiement réussi",
  "pay.successDesc": "Merci. Votre commande est en cours de traitement — le lien ou la photo sera activé quelques secondes après la confirmation.",
  "pay.backToMy": "Retour à Mes établissements",
  "pay.cancelPageTitle": "Paiement annulé — Cerveza.International",
  "pay.cancelTitle": "Paiement annulé",
  "pay.cancelDesc": "Aucun montant n'a été prélevé. Vous pouvez réessayer le paiement depuis votre page Mes établissements.",
  "profile.errorTitle": "Erreur",
  "profile.notFound": "Établissement introuvable",
  "profile.backToMap": "Retour à la carte",
  "profile.noDescription": "Pas encore de description. Ajoutez-la dans les langues dans lesquelles vous souhaitez présenter l'établissement.",
  "profile.editDescription": "Modifier la description",
  "profile.multiLangDesc": "Description multilingue",
  "profile.maxChars": "Max. {n} caractères par langue",
  "profile.descriptionsSaved": "Descriptions enregistrées",
  "profile.saveFailed": "Échec de l'enregistrement",
  "profile.save": "Enregistrer",
  "profile.cancel": "Annuler",
  "profile.linksHeader": "Liens",
  "profile.viewOnOSM": "Voir sur OpenStreetMap",
  "profile.descPlaceholder": "Description ({lang})",
  "reg.pageTitle": "Enregistrer un établissement — Cerveza.International",
  "reg.pageDesc": "Enregistrez votre établissement dans le catalogue global Cerveza.International.",
  "reg.maxTwoKinds": "Maximum 2 catégories sélectionnables.",
  "reg.confirmedKinds": "Catégories confirmées : {list}",
  "share.warnFallback": "Attention : le modèle n'est pas traduit en «{lang}», l'original tchèque est envoyé.",
  "share.copiedMsg": "Message copié — collez-le dans l'application souhaitée.",
  "share.copyFailed": "Échec de la copie.",
  "share.copiedLink": "Lien copié.",
  "share.copyLink": "Copier le lien",
  "share.copyMessage": "Copier le message",
  "my.photoUploadedPending": "Photo téléchargée, en attente de validation par l'admin.",
  "my.photoPendingApproval": "en attente de validation",
  "my.rejectReason": "Motif du refus : {reason}",
  "my.rejectedShort": "refusée",
  "my.freeOutOf": "{free}/3 gratuites · suivante : {price}",
  "my.photoPack": "Pack de photos :",
  "my.pieces": "pcs",
  "majitele.pageTitle": "Propriétaires de Vacants — Cerveza.International",

  // Downloads section
  "downloads.title": "Téléchargements",
  "downloads.subtitle": "Manuels et guides à télécharger.",
  "downloads.vakantOnly": "Réservé aux propriétaires de Vacants",
  "downloads.signInToView": "Connectez-vous pour voir",
  "downloads.downloadPdf": "Télécharger le PDF",
  "downloads.empty": "Aucun manuel disponible pour le moment.",

};
const hr: Dict = {
  "nav.home": "Početna",
  "nav.back": "Natrag",
  "nav.katalog": "Katalog",
  "nav.mapa": "Karta",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Moj panel",
  "nav.proMajitele": "Za vlasnike",
  "nav.admin": "Admin",
  "nav.cenik": "Cjenik",
  "nav.mujUcet": "Moj račun",
  "nav.odhlasit": "Odjava",
  "nav.prihlasit": "Prijava",
  "nav.vakanty": "Vlasnici Vacanta",

  "photos.title": "Fotografije",
  "photos.add": "Dodaj fotografiju",
  "photos.maxSize": "Maks. 3 MB / fotografija. Prve 3 fotografije besplatno, dodatne: {price} jednokratno.",
  "photos.tooLarge": "Fotografija premašuje 3 MB.",
  "photos.invalidType": "Datoteka mora biti slika.",
  "photos.uploadFailed": "Prijenos nije uspio: {msg}",
  "photos.freeAdded": "Fotografija je dodana besplatno.",
  "photos.paidPending": "Fotografija stvorena, čeka plaćanje.",
  "photos.deleted": "Fotografija je obrisana.",
  "photos.priceLabel": "Cijena po dodatnoj fotografiji",

  "home.title.a": "Otkrijte okuse svijeta na",
  "home.title.b": "jednom mjestu",
  "home.subtitle": "Od obiteljskih bistroa u Pragu do skrivenih barova u Barceloni. Globalni vodič za ljubitelje autentične gastronomije.",
  "home.search.what": "Što tražite? (npr. Tapas)",
  "home.search.where": "Gdje? (npr. Zagreb)",
  "home.search.submit": "Traži",

  "cat.restaurace": "Restorani",
  "cat.bary": "Barovi",
  "cat.kavarny": "Kavane",
  "cat.cajovny": "Čajane",
  "cat.stanky": "Štandovi",
  "cat.jidelny": "Menze",
  "cat.mlecnyBar": "Mliječni barovi",
  "cat.foodTruck": "Food truckovi",
  "cat.rychleObcerstveni": "Brza hrana",
  "cat.bistra": "Bistroi",

  "home.mapaCta": "Istraži kartu svijeta",

  "home.featured.title": "Istaknuti lokali",
  "home.featured.sub": "Najbolje ocijenjeni dragulji naše zajednice.",
  "home.featured.all": "Prikaži sve",

  "dc.tag": "Program vjernosti",
  "dc.title": "Postanite član Direct Cluba",
  "dc.text": "Skupljajte bonus bodove pri svakom posjetu, koristite ekskluzivne popuste i otkrijte posebne menije dostupne samo članovima mreže Cerveza.International.",
  "dc.register": "Registriraj se besplatno",
  "dc.how": "Kako to funkcionira?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Broj kartice",
  "dc.points": "Skupljeni bodovi",

  "owner.title": "Posjedujete ugostiteljski objekt?",
  "owner.text": "Pridružite se tisućama lokala i dosegnite goste iz cijelog svijeta. Registracija je brza i besplatna.",
  "owner.cta": "Dodaj svoj lokal",

  "footer.rights": "Sva prava pridržana",
  "footer.about": "O nama",
  "footer.terms": "Uvjeti",
  "footer.privacy": "Privatnost",
  "footer.contact": "Kontakt",

  "auth.signIn": "Prijava",
  "auth.signUp": "Registracija",
  "auth.tabSignIn": "Prijavi se",
  "auth.tabSignUp": "Stvori račun",
  "auth.google": "Nastavi s Googleom",
  "auth.apple": "Nastavi s Appleom",
  "auth.or": "ili",
  "auth.email": "Email",
  "auth.password": "Lozinka",
  "auth.passwordMin": "Lozinka (min. 6 znakova)",
  "auth.name": "Ime i prezime",
  "auth.submitSignIn": "Prijavi se",
  "auth.submitSignInLoading": "Prijavljivanje…",
  "auth.submitSignUp": "Stvori račun",
  "auth.submitSignUpLoading": "Registracija…",
  "auth.firstAdminNote": "Prvi registrirani račun automatski postaje administrator sustava.",
  "auth.back": "← Natrag na stranicu",
  "auth.welcomeBack": "Dobrodošli natrag!",
  "auth.created": "Račun stvoren.",
  "auth.oauthFailed": "Prijava nije uspjela",

  "mapa.tag": "Karta",
  "mapa.loading": "Učitavanje lokala…",
  "mapa.count": "odobrenih lokala",
  "mapa.kindHeader": "Vrsta lokala",
  "mapa.addBusiness": "Dodaj lokal",
  "mapa.showUsers": "Članovi Direct Cluba",
  "mapa.showAnon": "Gosti",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Objekt iz kataloga",
  "mapa.catalog.brewery": "Pivovara iz kataloga",
  "mapa.catalog.imported": "Uvezeno iz XLSX kataloga Cerveza Data Standard",
  "mapa.catalog.detail": "Detalj",
  "mapa.review.discovery": "Neprovjereno",
  "mapa.review.pending": "Za provjeru",
  "mapa.review.verified": "Provjereno",
  "mapa.review.rejected": "Odbijeno",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "Sjeverna Amerika",
  "mapa.continent.southAmerica": "Južna Amerika",
  "mapa.continent.asia": "Azija",
  "mapa.continent.africa": "Afrika",
  "mapa.continent.oceania": "Oceanija",
  "mapa.continent.other": "Ostalo",
  "mapa.memberBadge": "Član Direct Cluba",
  "mapa.partnerHint": "Provjereno partnersko poduzeće registrirano na Cerveza.International",
  "mapa.memberPrivacyNote": "Registrirani korisnik · približna lokacija radi privatnosti",
  "mapa.anonPrivacyNote": "Anonimni posjetitelj · lokacija vidljiva 24 h, zatim se uklanja",
  "mapa.legend.title": "Legenda",
  "mapa.userPin.title": "Registrirani član Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Približna lokacija (bez točne adrese)",
  "mapa.viewProfile": "Profil lokala",
  "mapa.empty.a": "Još nema odobrenih lokala. Budite prvi —",
  "mapa.empty.b": "registrirajte lokal",
  "mapa.anonPin.title": "Anonimni posjetitelj",
  "mapa.anonPin.subtitle": "Aktivan u zadnja 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtri",
  "mapa.filters.search": "Pretraga",
  "mapa.filters.searchPlaceholder": "Naziv, grad, opis…",
  "mapa.filters.location": "Lokacija",
  "mapa.filters.continent": "Kontinent",
  "mapa.filters.allContinents": "Svi kontinenti",
  "mapa.filters.country": "Država",
  "mapa.filters.allCountries": "Sve države",
  "mapa.filters.region": "Regija",
  "mapa.filters.regionEmpty": "Regija (prazno)",
  "mapa.filters.allRegions": "Sve regije",
  "mapa.filters.city": "Grad",
  "mapa.filters.allCities": "Svi gradovi",
  "mapa.filters.postal": "Poštanski broj",
  "mapa.filters.partnership": "Partnerstvo",
  "mapa.filters.reset": "Resetiraj filtre",
  "mapa.empty.filtered": "Nijedan lokal ne odgovara filtru.",
  "mapa.empty.resetFilters": "Resetiraj filtre",
  "mapa.location.missing": "Lokacija nedostaje",
  "mapa.location.stale": "Lokacija zastarjela",
  "mapa.location.fresh": "Lokacija svježa",
  "mapa.location.refresh": "Osvježi lokaciju",
  "mapa.location.lastSeen": "Zadnji put",
  "mapa.location.hidden": "sakriveno na karti",
  "mapa.location.never": "nikad",
  "mapa.location.toastOk": "Lokacija ažurirana",
  "mapa.location.toastCached": "Korištena svježa spremljena vrijednost.",
  "mapa.location.toastSource": "Izvor",
  "mapa.location.toastFail": "Lokaciju nije moguće utvrditi",
  "mapa.location.toastFailDesc": "Pokušajte kasnije (geo nedostupno).",
  "mapa.location.toastError": "Greška pri osvježavanju lokacije",
  "mapa.breweries": "Pivovare",
  "mapa.breweriesHint": "Pivovare in proizvajalci piva",
  "mapa.showMembersNoBusiness": "Članovi bez obrta",
  "mapa.memberNoBusiness.badge": "Član bez obrta",
  "mapa.memberNoBusiness.title": "Član spreman graditi mrežu",
  "mapa.memberNoBusiness.subtitle": "Može preporučiti nove članove, obrte i ocijeniti posjećena mjesta.",
  "mapa.memberNoBusiness.recommendBusiness": "Preporuči obrt",
  "mapa.memberNoBusiness.inviteMember": "Preporuči člana",
  "mapa.memberNoBusiness.rateVisited": "Ocijeni posjećeni obrt",
  "mapa.memberNoBusiness.privacy": "Približna lokacija radi zaštite privatnosti",
  "mapa.memberNoBusiness.hint": "Registrirani članovi koji ne posjeduju obrt — idealni kandidati za preporuke",

  "my.title": "Moji lokali",
  "my.sub": "Upravljajte svojim lokalima i njihovim poveznicama.",
  "my.add": "Dodaj lokal",
  "my.loading": "Učitavanje…",
  "my.empty.a": "Još nemate lokala.",
  "my.empty.b": "Registriraj prvi",
  "my.links": "Poveznice",
  "my.noLinks": "Nema poveznica.",
  "my.free": "Besplatno",
  "my.paid": "Plaćeno",
  "my.active": "Aktivno",
  "my.rejected": "Odbijeno",
  "my.pendingPayment": "Čeka plaćanje",
  "my.pay": "Plati",
  "my.payTooltip": "Online plaćanje uskoro dostupno",
  "my.addLink": "Dodaj poveznicu",
  "my.linkInfo": "Poveznice na <strong>directclub.digital</strong> / <strong>direct.club</strong> su besplatne (maks. 3 po lokalu). Ostale vanjske poveznice: <strong>{price}</strong> jednokratno (trajna poveznica nakon plaćanja).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Oznaka (neobavezno)",
  "my.addFree": "Dodaj besplatno",
  "my.addPaid": "Dodaj za {price}",
  "my.freeAdded": "Besplatna poveznica dodana i objavljena.",
  "my.paidPending": "Poveznica stvorena, čeka plaćanje.",
  "my.freeLimit": "Dosegnuto je ograničenje od 3 besplatne directclub poveznice.",
  "my.willBeFree": "✓ directclub poveznica — bit će besplatna i odmah objavljena.",
  "my.willBePaid": "Vanjska poveznica — potrebno plaćanje {price} (objava nakon plaćanja).",
  "my.invalidUrl": "Nevažeći URL",

  "reg.back": "← Natrag na stranicu",
  "reg.moderation": "Hibridna moderacija",
  "reg.title": "Registracija lokala",
  "reg.desc": "Nakon unosa poštanskog broja automatski ćemo popuniti grad i koordinate iz baze poštanskih brojeva. Provjereni vlasnici odobravaju se automatski, ostali unosi prolaze moderaciju.",
  "reg.name": "Naziv lokala",
  "reg.namePlaceholder": "npr. Café Vinohrady",
  "reg.kind": "Vrsta lokala",
  "reg.kindPlaceholder": "Odaberite vrstu",
  "reg.country": "Država",
  "reg.psc": "Poštanski broj",
  "reg.pscPlaceholder": "npr. 10000",
  "reg.pscInvalid": "Nevažeći format poštanskog broja (CZ: 5 znamenki).",
  "reg.searching": "Tražim grad…",
  "reg.notFound": "Za poštanski broj {postal} nije pronađen nijedan grad u bazi.",
  "reg.foundOne": "Pronađen grad:",
  "reg.foundMany": "Pronađeno {count} gradova — odaberite ispravan:",
  "reg.coords": "Koordinate:",
  "reg.mapPreview": "Pregled lokacije na karti",
  "reg.mapHint": "Povucite pribadaču ili kliknite na kartu za precizniju lokaciju.",
  "reg.street": "Ulica i broj (neobavezno)",
  "reg.streetPlaceholder": "npr. Korunní 12",
  "reg.descLabel": "Kratki opis (neobavezno)",
  "reg.descPlaceholder": "Po čemu je vaš lokal poseban?",
  "reg.cancel": "Odustani",
  "reg.submit": "Pošalji na registraciju",
  "reg.submitted": "Lokal poslan na moderaciju.",
  "reg.notSignedIn": "Niste prijavljeni",
  "reg.pickCity": "Odaberite važeći grad iz rezultata poštanskog broja",
  "reg.invalidData": "Nevažeći podaci",
  "reg.gate.title": "Potvrda vlasništva nad lokalom",
  "reg.gate.desc": "Lokal može registrirati samo njegov vlasnik ili upravitelj. Odaberite 1–2 kategorije kojima pripada vaš lokal.",
  "reg.gate.confirmBefore": "Potvrđujem da sam",
  "reg.gate.confirmStrong": "vlasnik ili upravitelj",
  "reg.gate.confirmAfter": "lokala koji želim registrirati i da pripada jednoj od dolje navedenih kategorija.",
  "reg.gate.kindsLabel": "Kategorija lokala (odaberite 1–2)",
  "reg.gate.selected": "Odabrano: {count} / 2",
  "reg.continue": "Nastavi",

  "kind.restaurace": "Restoran",
  "kind.bar": "Bar",
  "kind.kavarna": "Kavana",
  "kind.cajovna": "Čajana",
  "kind.stanek": "Štand",
  "kind.jidelna": "Menza",
  "kind.mlecny_bar": "Mliječni bar",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Brza hrana",
  "kind.bistro": "Bistro",

  "status.pending": "Čeka odobrenje",
  "status.approved": "Odobreno",
  "status.rejected": "Odbijeno",
  "status.hidden": "Skriveno",

  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} mj. besplatno do {date}",
  "auth.checkEmail": "Molimo provjerite svoj sandučić e-pošte i potvrdite registraciju klikom na poveznicu koju smo vam poslali.",
  "home.latest.title": "Najnoviji lokali",
  "home.latest.sub": "Nedavno dodani odobreni lokali.",
  "home.latest.empty": "Još nema odobrenih lokala.",
  "home.latest.viewAll": "Prikaži na karti →",
  "reg.descMultiHint": "Opis možete ispuniti na jednom ili više jezika. Posjetitelji ga vide na svom jeziku; ako nedostaje, koristi se drugi dostupan.",
  "vakanty.title": "Moja nadzorna ploča",
  "vakanty.welcomeWithName": "Dobrodošli, {name}. Ispod vidite broj Vacanta na vašem računu.",
  "vakanty.welcomeNoName": "Dobrodošli. Ispod vidite broj Vacanta na vašem računu.",
  "vakanty.purchasedLabel": "Kupljeni Vacanti",
  "vakanty.emptyHint": "Još nemate Vacanta. Zatražite od administratora povećanje.",
  "vakanty.featuresTitle": "Funkcije i informacije",
  "vakanty.kindFeature": "Funkcija",
  "vakanty.kindInfo": "Informacija",
  "vakanty.open": "Otvori",
 "vakanty.loading": "Učitavanje…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Interni dokument dostupan isključivo vlasnicima Vacantâ.",
 "vakanty.bp.strictHeader": "Strogo povjerljivo (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Ovaj dokument je strogo povjerljiv i predstavlja poslovnu tajnu tvrtke For Direct s.r.o.. Dokument se ne smije preuzimati, prosljeđivati, kopirati, dijeliti, objavljivati niti na bilo koji drugi način distribuirati trećim stranama — uključujući njegove dijelove, snimke zaslona, fotografije ili transkripte. Dokument je dostupan isključivo za online pregled na ovoj stranici i samo prijavljenom vlasniku Vacanta. Svaka neovlaštena uporaba, reprodukcija ili distribucija predstavlja povredu ugovornih obveza i može dovesti do građanskopravne i kaznene odgovornosti. Kršenje ovog ugovora o povjerljivosti podliježe ugovornoj kazni u iznosu od 5.000.000 EUR (slovima: pet milijuna eura) za svaki pojedini slučaj kršenja.",
 "vakanty.bp.paragraph2": "Označavanjem polja u nastavku potvrđujete da ste pročitali ova pravila, da ih razumijete i da se obvezujete pridržavati ih. Vaša suglasnost bit će zabilježena s vremenskom oznakom.",
 "vakanty.bp.acceptedOn": "NDA prihvaćen dana {date}.",
 "vakanty.bp.checkboxLabel": "Izjavljujem da sam pročitao/pročitala uvjete povjerljivosti navedene iznad, prihvaćam ih i obvezujem se da ovaj dokument neću dijeliti niti distribuirati.",
 "vakanty.bp.acceptButton": "Prihvati NDA i prikaži",
 "vakanty.bp.showButton": "Prikaži Business Plan",
 "vakanty.bp.hideButton": "Sakrij dokument",
 "vakanty.bp.viewerNotice": "Strogo povjerljivo — ne kopirati, ne dijeliti, ne preuzimati. Pregled isključivo na ovoj stranici.",
 "vakanty.bp.mustCheckFirst": "Najprije označite pristanak na NDA.",
 "vakanty.bp.acceptedToast": "NDA prihvaćen. Sada možete pregledati dokument.",
  "common.error": "Greška",
  "common.tryAgain": "Pokušaj ponovno",
  "common.goHome": "Na početnu",
  "common.pageNotFound": "Stranica nije pronađena",
  "common.pageNotFoundDesc": "Stranica koju tražite ne postoji ili je premještena.",
  "common.errorTitle": "Stranica se nije učitala",
  "common.errorDesc": "Nešto je pošlo po zlu. Pokušajte osvježiti ili se vratite na početnu.",
  "auth.enterEmailFirst": "Prvo unesite e-mail.",
  "auth.resendResult": "Ako račun postoji i nije potvrđen, poslali smo novu poveznicu. Preostalo pokušaja: {remaining}.",
  "auth.resendFailed": "Slanje e-maila nije uspjelo.",
  "auth.emailAlreadyRegistered": "Ovaj e-mail je već registriran. Provjerite sandučić ili se prijavite.",
  "auth.resending": "Šaljem…",
  "auth.resendConfirmation": "Ponovno pošalji potvrdni e-mail",
  "auth.signInDesc": "Prijavite se ili registrirajte na Cerveza.International.",
  "auth.pageTitle": "Prijava — Cerveza.International",
  "authReset.pageTitle": "Postavljanje lozinke — Cerveza.International",
  "authReset.title": "Postavi novu lozinku",
  "authReset.needLink": "Otvorite ovu stranicu iz poveznice u e-mailu.",
  "authReset.newPassword": "Nova lozinka",
  "authReset.passwordAgain": "Ponovi lozinku",
  "authReset.saving": "Spremam…",
  "authReset.save": "Spremi lozinku",
  "authReset.tooShort": "Lozinka mora imati najmanje 8 znakova.",
  "authReset.mismatch": "Lozinke se ne podudaraju.",
  "authReset.saved": "Lozinka postavljena.",
  "unsub.pageTitle": "Odjava — Cerveza.International",
  "unsub.title": "Odjava od pretplate",
  "unsub.checking": "Provjera poveznice…",
  "unsub.confirmPrompt": "Potvrdite da se želite odjaviti s e-mailova Cerveza.International.",
  "unsub.confirmBtn": "Potvrdi odjavu",
  "unsub.processing": "Odjavljivanje…",
  "unsub.done": "Gotovo. Uspješno ste odjavljeni.",
  "unsub.already": "Ova adresa je već odjavljena.",
  "unsub.invalid": "Poveznica je nevažeća ili je istekla.",
  "unsub.errorVerify": "Nije moguće provjeriti poveznicu.",
  "unsub.errorGeneric": "Nešto je pošlo po zlu.",
  "unsub.errorNetwork": "Greška mreže.",
  "pay.successPageTitle": "Plaćanje uspješno — Cerveza.International",
  "pay.successTitle": "Plaćanje uspješno",
  "pay.successDesc": "Hvala. Vaša narudžba se obrađuje — poveznica ili fotografija bit će aktivirana nekoliko sekundi nakon potvrde.",
  "pay.backToMy": "Natrag na Moji objekti",
  "pay.cancelPageTitle": "Plaćanje otkazano — Cerveza.International",
  "pay.cancelTitle": "Plaćanje otkazano",
  "pay.cancelDesc": "Nije naplaćen nikakav iznos. Plaćanje možete ponoviti sa stranice Moji objekti.",
  "profile.errorTitle": "Greška",
  "profile.notFound": "Objekt nije pronađen",
  "profile.backToMap": "Natrag na kartu",
  "profile.noDescription": "Još nema opisa. Dodajte ga na jezicima na kojima želite prezentirati objekt.",
  "profile.editDescription": "Uredi opis",
  "profile.multiLangDesc": "Višejezični opis",
  "profile.maxChars": "Maks. {n} znakova po jeziku",
  "profile.descriptionsSaved": "Opisi spremljeni",
  "profile.saveFailed": "Spremanje nije uspjelo",
  "profile.save": "Spremi",
  "profile.cancel": "Odustani",
  "profile.linksHeader": "Poveznice",
  "profile.viewOnOSM": "Prikaži na OpenStreetMap",
  "profile.descPlaceholder": "Opis ({lang})",
  "reg.pageTitle": "Registracija objekta — Cerveza.International",
  "reg.pageDesc": "Registrirajte svoj objekt u globalnom katalogu Cerveza.International.",
  "reg.maxTwoKinds": "Možete odabrati najviše 2 kategorije.",
  "reg.confirmedKinds": "Potvrđene kategorije: {list}",
  "share.warnFallback": "Napomena: predložak nije preveden na „{lang}”, šalje se češki original.",
  "share.copiedMsg": "Poruka kopirana — zalijepite je u željenu aplikaciju.",
  "share.copyFailed": "Kopiranje nije uspjelo.",
  "share.copiedLink": "Poveznica kopirana.",
  "share.copyLink": "Kopiraj poveznicu",
  "share.copyMessage": "Kopiraj poruku",
  "my.photoUploadedPending": "Fotografija učitana, čeka odobrenje administratora.",
  "my.photoPendingApproval": "čeka odobrenje",
  "my.rejectReason": "Razlog odbijanja: {reason}",
  "my.rejectedShort": "odbijeno",
  "my.freeOutOf": "{free}/3 besplatno · sljedeća: {price}",
  "my.photoPack": "Foto paket:",
  "my.pieces": "kom",
  "majitele.pageTitle": "Vlasnici Vacantsa — Cerveza.International",

  // Downloads section
  "downloads.title": "Za preuzimanje",
  "downloads.subtitle": "Priručnici i vodiči za preuzimanje.",
  "downloads.vakantOnly": "Samo za vlasnike Vacantsa",
  "downloads.signInToView": "Prijavite se za prikaz",
  "downloads.downloadPdf": "Preuzmi PDF",
  "downloads.empty": "Još nema priručnika.",

};
const sk: Dict = {
  "nav.home": "Domov",
  "nav.back": "Späť",
  "nav.katalog": "Katalóg",
  "nav.mapa": "Mapa",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Môj panel",
  "nav.proMajitele": "Pre majiteľov",
  "nav.admin": "Admin",
  "nav.cenik": "Cenník",
  "nav.mujUcet": "Môj účet",
  "nav.odhlasit": "Odhlásiť sa",
  "nav.prihlasit": "Prihlásiť sa",
  "nav.vakanty": "Majitelia Vacantov",
  "photos.title": "Fotky",
  "photos.add": "Pridať fotku",
  "photos.maxSize": "Max. 3 MB / fotka. Prvé 3 fotky zdarma, ďalšie: {price} jednorazovo.",
  "photos.tooLarge": "Fotka presahuje 3 MB.",
  "photos.invalidType": "Súbor musí byť obrázok.",
  "photos.uploadFailed": "Nahranie zlyhalo: {msg}",
  "photos.freeAdded": "Fotka bola pridaná zdarma.",
  "photos.paidPending": "Fotka vytvorená, čaká na úhradu.",
  "photos.deleted": "Fotka bola odstránená.",
  "photos.priceLabel": "Cena za ďalšiu fotku",
  "home.title.a": "Objavte chute sveta na",
  "home.title.b": "jednom mieste",
  "home.subtitle": "Od rodinných bistier v Prahe po skryté bary v Barcelone. Globálny sprievodca pre milovníkov autentickej gastronómie.",
  "home.search.what": "Čo hľadáte? (napr. Tapas)",
  "home.search.where": "Kde? (napr. Bratislava)",
  "home.search.submit": "Hľadať",
  "cat.restaurace": "Reštaurácie",
  "cat.bary": "Bary",
  "cat.kavarny": "Kaviarne",
  "cat.cajovny": "Čajovne",
  "cat.stanky": "Stánky",
  "cat.jidelny": "Jedálne",
  "cat.mlecnyBar": "Mliečne bary",
  "cat.foodTruck": "Food trucky",
  "cat.rychleObcerstveni": "Fast Food",
  "cat.bistra": "Bistrá",
  "home.mapaCta": "Preskúmať mapu sveta",
  "home.featured.title": "Odporúčané podniky",
  "home.featured.sub": "Najlepšie hodnotené skvosty našej komunity.",
  "home.featured.all": "Zobraziť všetko",
  "dc.tag": "Vernostný program",
  "dc.title": "Staňte sa členom Direct Clubu",
  "dc.text": "Získavajte bonusové body za každú návštevu, využívajte exkluzívne zľavy a objavujte špeciálne menu dostupné len pre členov siete Cerveza.International.",
  "dc.register": "Registrovať sa zdarma",
  "dc.how": "Ako to funguje?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Číslo karty",
  "dc.points": "Nazbierané body",
  "owner.title": "Vlastníte gastro podnik?",
  "owner.text": "Pridajte sa k tisícom podnikov a oslovte hostí z celého sveta. Registrácia je rýchla a zdarma.",
  "owner.cta": "Pridať svoj podnik",
  "footer.rights": "Všetky práva vyhradené",
  "footer.about": "O nás",
  "footer.terms": "Podmienky",
  "footer.privacy": "Ochrana údajov",
  "footer.contact": "Kontakt",
  "auth.signIn": "Prihlásenie",
  "auth.signUp": "Registrácia",
  "auth.tabSignIn": "Prihlásiť sa",
  "auth.tabSignUp": "Vytvoriť účet",
  "auth.google": "Pokračovať cez Google",
  "auth.apple": "Pokračovať cez Apple",
  "auth.or": "alebo",
  "auth.email": "Email",
  "auth.password": "Heslo",
  "auth.passwordMin": "Heslo (min. 6 znakov)",
  "auth.name": "Meno a priezvisko",
  "auth.submitSignIn": "Prihlásiť sa",
  "auth.submitSignInLoading": "Prihlasujem…",
  "auth.submitSignUp": "Vytvoriť účet",
  "auth.submitSignUpLoading": "Registrujem…",
  "auth.firstAdminNote": "Prvý registrovaný účet sa automaticky stane správcom systému.",
  "auth.back": "← Späť na web",
  "auth.welcomeBack": "Vitajte späť!",
  "auth.created": "Účet vytvorený.",
  "auth.oauthFailed": "Prihlásenie zlyhalo",
  "mapa.tag": "Mapa",
  "mapa.loading": "Načítavam podniky…",
  "mapa.count": "schválených podnikov",
  "mapa.kindHeader": "Typ podniku",
  "mapa.addBusiness": "Pridať podnik",
  "mapa.showUsers": "Členovia Direct Clubu",
  "mapa.showAnon": "Hostia",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Podnik z katalógu",
  "mapa.catalog.brewery": "Pivovar z katalógu",
  "mapa.catalog.imported": "Importované z XLSX katalógu Cerveza Data Standard",
  "mapa.catalog.detail": "Detail",
  "mapa.review.discovery": "Neoverené",
  "mapa.review.pending": "Na overenie",
  "mapa.review.verified": "Overené",
  "mapa.review.rejected": "Zamietnuté",
  "mapa.continent.europe": "Európa",
  "mapa.continent.northAmerica": "Severná Amerika",
  "mapa.continent.southAmerica": "Južná Amerika",
  "mapa.continent.asia": "Ázia",
  "mapa.continent.africa": "Afrika",
  "mapa.continent.oceania": "Oceánia",
  "mapa.continent.other": "Ostatné",
  "mapa.memberBadge": "Člen Direct Clubu",
  "mapa.partnerHint": "Overená partnerská firma registrovaná v Cerveza.International",
  "mapa.memberPrivacyNote": "Registrovaný používateľ · približná poloha na ochranu súkromia",
  "mapa.anonPrivacyNote": "Neprihlásený návštevník · poloha sa zobrazuje 24 h, potom mizne",
  "mapa.legend.title": "Legenda",
  "mapa.userPin.title": "Registrovaný člen Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Približná poloha (bez presnej adresy)",
  "mapa.viewProfile": "Profil podniku",
  "mapa.empty.a": "Zatiaľ žiadny schválený podnik. Buďte prvý —",
  "mapa.empty.b": "registrovať podnik",
  "mapa.anonPin.title": "Anonymný návštevník",
  "mapa.anonPin.subtitle": "Aktívny za posledných 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtre",
  "mapa.filters.search": "Hľadať",
  "mapa.filters.searchPlaceholder": "Názov, mesto, popis…",
  "mapa.filters.location": "Lokalita",
  "mapa.filters.continent": "Kontinent",
  "mapa.filters.allContinents": "Všetky kontinenty",
  "mapa.filters.country": "Štát",
  "mapa.filters.allCountries": "Všetky štáty",
  "mapa.filters.region": "Región",
  "mapa.filters.regionEmpty": "Región (zatiaľ prázdne)",
  "mapa.filters.allRegions": "Všetky regióny",
  "mapa.filters.city": "Mesto",
  "mapa.filters.allCities": "Všetky mestá",
  "mapa.filters.postal": "PSČ",
  "mapa.filters.partnership": "Partnerstvo",
  "mapa.filters.reset": "Resetovať filtre",
  "mapa.empty.filtered": "Filtru nezodpovedá žiadny podnik.",
  "mapa.empty.resetFilters": "Resetovať filtre",
  "mapa.location.missing": "Poloha chýba",
  "mapa.location.stale": "Poloha zastaraná",
  "mapa.location.fresh": "Poloha čerstvá",
  "mapa.location.refresh": "Obnoviť polohu",
  "mapa.location.lastSeen": "Naposledy",
  "mapa.location.hidden": "skrytá na mape",
  "mapa.location.never": "nikdy",
  "mapa.location.toastOk": "Poloha aktualizovaná",
  "mapa.location.toastCached": "Použitá čerstvá uložená hodnota.",
  "mapa.location.toastSource": "Zdroj",
  "mapa.location.toastFail": "Polohu sa nepodarilo zistiť",
  "mapa.location.toastFailDesc": "Skúste to neskôr (geo nedostupné).",
  "mapa.location.toastError": "Chyba pri obnovovaní polohy",
  "mapa.breweries": "Pivovary",
  "mapa.breweriesHint": "Pivovary a výrobcovia piva",
  "mapa.showMembersNoBusiness": "Členovia bez podniku",
  "mapa.memberNoBusiness.badge": "Člen bez podniku",
  "mapa.memberNoBusiness.title": "Člen pripravený budovať sieť",
  "mapa.memberNoBusiness.subtitle": "Môže odporučiť nových členov, podniky a hodnotiť navštívené miesta.",
  "mapa.memberNoBusiness.recommendBusiness": "Odporučiť podnik",
  "mapa.memberNoBusiness.inviteMember": "Odporučiť člena",
  "mapa.memberNoBusiness.rateVisited": "Ohodnotiť navštívený podnik",
  "mapa.memberNoBusiness.privacy": "Približná poloha na ochranu súkromia",
  "mapa.memberNoBusiness.hint": "Registrovaní členovia, ktorí zatiaľ nevlastnia žiadny podnik — ideálni kandidáti na odporúčanie",
  "my.title": "Moje podniky",
  "my.sub": "Spravujte svoje podniky a ich odkazy.",
  "my.add": "Pridať podnik",
  "my.loading": "Načítavam…",
  "my.empty.a": "Zatiaľ nemáte žiadne podniky.",
  "my.empty.b": "Registrovať prvý",
  "my.links": "Odkazy",
  "my.noLinks": "Žiadne odkazy.",
  "my.free": "Zdarma",
  "my.paid": "Platené",
  "my.active": "Aktívne",
  "my.rejected": "Zamietnuté",
  "my.pendingPayment": "Čaká na úhradu",
  "my.pay": "Zaplatiť",
  "my.payTooltip": "Online platba bude čoskoro dostupná",
  "my.addLink": "Pridať odkaz",
  "my.linkInfo": "Odkazy na <strong>directclub.digital</strong> / <strong>direct.club</strong> sú zdarma (max. 3 na podnik). Ostatné externé odkazy: <strong>{price}</strong> jednorazovo (trvalý odkaz po úhrade).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Štítok (voliteľné)",
  "my.addFree": "Pridať zdarma",
  "my.addPaid": "Pridať za {price}",
  "my.freeAdded": "Bezplatný odkaz bol pridaný a zverejnený.",
  "my.paidPending": "Odkaz vytvorený, čaká na úhradu.",
  "my.freeLimit": "Limit 3 bezplatných directclub odkazov bol dosiahnutý.",
  "my.willBeFree": "✓ directclub odkaz — bude zdarma a okamžite zverejnený.",
  "my.willBePaid": "Externý odkaz — vyžaduje úhradu {price} (zverejnený po úhrade).",
  "my.invalidUrl": "Neplatná URL",
  "reg.back": "← Späť na web",
  "reg.moderation": "Hybridná moderácia",
  "reg.title": "Registrácia podniku",
  "reg.desc": "Po zadaní PSČ automaticky doplníme obec a súradnice z databázy poštových smerovacích čísel. Overení majitelia sa schvaľujú automaticky, ostatní prechádzajú moderáciou.",
  "reg.name": "Názov podniku",
  "reg.namePlaceholder": "napr. Káviareň Vinohrady",
  "reg.kind": "Typ podniku",
  "reg.kindPlaceholder": "Vyberte typ",
  "reg.country": "Krajina",
  "reg.psc": "PSČ",
  "reg.pscPlaceholder": "napr. 811 01",
  "reg.pscInvalid": "Neplatný formát PSČ (CZ: 5 číslic).",
  "reg.searching": "Hľadám obec…",
  "reg.notFound": "Pre PSČ {postal} sme v databáze nenašli žiadnu obec.",
  "reg.foundOne": "Nájdená obec:",
  "reg.foundMany": "Nájdených {count} obcí — vyberte správnu:",
  "reg.coords": "Súradnice:",
  "reg.mapPreview": "Náhľad polohy na mape",
  "reg.mapHint": "Potiahnite pin alebo kliknite na mapu pre spresnenie polohy.",
  "reg.street": "Ulica a číslo (voliteľné)",
  "reg.streetPlaceholder": "napr. Korunní 12",
  "reg.descLabel": "Krátky popis (voliteľné)",
  "reg.descMultiHint": "Popis môžete vyplniť v jednom alebo viacerých jazykoch. Návštevníci ho uvidia vo svojom jazyku; ak chýba, použije sa iný dostupný.",
  "reg.descPlaceholder": "Čím je váš podnik výnimočný?",
  "reg.cancel": "Zrušiť",
  "reg.submit": "Odoslať na registráciu",
  "reg.submitted": "Podnik odoslaný na moderáciu.",
  "reg.notSignedIn": "Nie ste prihlásený",
  "reg.pickCity": "Vyberte platnú obec z výsledkov PSČ",
  "reg.invalidData": "Neplatné údaje",
  "reg.gate.title": "Potvrdenie vlastníctva podniku",
  "reg.gate.desc": "Podnik môže registrovať len jeho majiteľ alebo prevádzkovateľ. Vyberte 1–2 kategórie, do ktorých váš podnik patrí.",
  "reg.gate.confirmBefore": "Potvrdzujem, že som",
  "reg.gate.confirmStrong": "majiteľ alebo prevádzkovateľ",
  "reg.gate.confirmAfter": "podniku, ktorý chcem registrovať, a že patrí do jednej z nižšie uvedených kategórií.",
  "reg.gate.kindsLabel": "Kategória podniku (vyberte 1–2)",
  "reg.gate.selected": "Vybrané: {count} / 2",
  "reg.continue": "Pokračovať",
  "kind.restaurace": "Reštaurácia",
  "kind.bar": "Bar",
  "kind.kavarna": "Kaviareň",
  "kind.cajovna": "Čajovňa",
  "kind.stanek": "Stánok",
  "kind.jidelna": "Jedáleň",
  "kind.mlecny_bar": "Mliečny bar",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistro",
  "status.pending": "Čaká na schválenie",
  "status.approved": "Schválené",
  "status.rejected": "Zamietnuté",
  "status.hidden": "Skryté",
  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} mes. zdarma do {date}",
  "auth.checkEmail": "Skontrolujte si prosím schránku a potvrďte registráciu kliknutím na odkaz, ktorý sme vám poslali.",
  "home.latest.title": "Najnovšie podniky",
  "home.latest.sub": "Nedávno pridané schválené podniky.",
  "home.latest.empty": "Zatiaľ žiadne schválené podniky.",
  "home.latest.viewAll": "Zobraziť na mape →",
  "vakanty.title": "Môj panel",
  "vakanty.welcomeWithName": "Vitajte, {name}. Nižšie vidíte počet Vacantov na vašom konte.",
  "vakanty.welcomeNoName": "Vitajte. Nižšie vidíte počet Vacantov na vašom konte.",
  "vakanty.purchasedLabel": "Zakúpené Vacanty",
  "vakanty.emptyHint": "Zatiaľ nemáte žiadne Vacanty. Požiadajte administrátora o pridanie.",
  "vakanty.featuresTitle": "Funkcie a informácie",
  "vakanty.kindFeature": "Funkcia",
  "vakanty.kindInfo": "Informácia",
  "vakanty.open": "Otvoriť",
 "vakanty.loading": "Načítavam…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Interný dokument dostupný výhradne majiteľom Vacantov.",
 "vakanty.bp.strictHeader": "Prísne dôverné (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Tento dokument je prísne dôverný a predstavuje obchodné tajomstvo spoločnosti For Direct s.r.o.. Dokument sa nesmie sťahovať, preposielať, kopírovať, zdieľať, publikovať ani inak distribuovať tretím stranám — a to ani jeho časti, snímky obrazovky, fotografie alebo prepisy. Dokument je dostupný iba na online prehliadanie na tejto stránke a výhradne prihlásenému majiteľovi Vacantu. Akékoľvek neoprávnené použitie, reprodukcia alebo šírenie je porušením zmluvných povinností a môže viesť k občianskoprávnej aj trestnoprávnej zodpovednosti. Porušenie tejto dohody o mlčanlivosti sa sankcionuje zmluvnou pokutou vo výške 5 000 000 EUR (slovom: päť miliónov eur) za každý jednotlivý prípad porušenia.",
 "vakanty.bp.paragraph2": "Zaškrtnutím políčka nižšie potvrdzujete, že ste si tieto pravidlá prečítali, rozumiete im a zaväzujete sa ich dodržiavať. Váš súhlas bude zaznamenaný s časovou pečiatkou.",
 "vakanty.bp.acceptedOn": "NDA prijaté dňa {date}.",
 "vakanty.bp.checkboxLabel": "Vyhlasujem, že som si prečítal(a) vyššie uvedené podmienky mlčanlivosti, súhlasím s nimi a zaväzujem sa dokument nezdieľať ani nedistribuovať.",
 "vakanty.bp.acceptButton": "Prijať NDA a zobraziť",
 "vakanty.bp.showButton": "Zobraziť Business Plan",
 "vakanty.bp.hideButton": "Skryť dokument",
 "vakanty.bp.viewerNotice": "Prísne dôverné — nekopírovať, nezdieľať, nesťahovať. Prehliadanie iba na tejto stránke.",
 "vakanty.bp.mustCheckFirst": "Najprv zaškrtnite súhlas s NDA.",
 "vakanty.bp.acceptedToast": "NDA prijaté. Teraz si môžete dokument prezrieť.",
  "common.error": "Chyba",
  "common.tryAgain": "Skúsiť znova",
  "common.goHome": "Späť na hlavnú stránku",
  "common.pageNotFound": "Stránka nenájdená",
  "common.pageNotFoundDesc": "Stránku, ktorú hľadáte, sa nepodarilo nájsť alebo bola presunutá.",
  "common.errorTitle": "Táto stránka sa nenačítala",
  "common.errorDesc": "Niečo sa nepodarilo. Skúste obnoviť alebo sa vráťte na úvod.",
  "auth.enterEmailFirst": "Najprv zadajte e-mail.",
  "auth.resendResult": "Ak konto existuje a nie je potvrdené, odoslali sme nový odkaz. Zostáva pokusov: {remaining}.",
  "auth.resendFailed": "Nepodarilo sa odoslať e-mail.",
  "auth.emailAlreadyRegistered": "Tento e-mail je už zaregistrovaný. Skontrolujte schránku alebo sa prihláste.",
  "auth.resending": "Odosielam…",
  "auth.resendConfirmation": "Znova poslať potvrdzovací e-mail",
  "auth.signInDesc": "Prihláste sa alebo zaregistrujte v Cerveza.International.",
  "auth.pageTitle": "Prihlásenie — Cerveza.International",
  "authReset.pageTitle": "Nastavenie hesla — Cerveza.International",
  "authReset.title": "Nastavenie nového hesla",
  "authReset.needLink": "Otvorte túto stránku z odkazu v e-maile.",
  "authReset.newPassword": "Nové heslo",
  "authReset.passwordAgain": "Heslo znova",
  "authReset.saving": "Ukladám…",
  "authReset.save": "Uložiť heslo",
  "authReset.tooShort": "Heslo musí mať aspoň 8 znakov.",
  "authReset.mismatch": "Heslá sa nezhodujú.",
  "authReset.saved": "Heslo nastavené.",
  "unsub.pageTitle": "Odhlásenie — Cerveza.International",
  "unsub.title": "Odhlásenie odberu",
  "unsub.checking": "Overujem odkaz…",
  "unsub.confirmPrompt": "Potvrďte, že sa chcete odhlásiť z e-mailov Cerveza.International.",
  "unsub.confirmBtn": "Potvrdiť odhlásenie",
  "unsub.processing": "Odhlasujem…",
  "unsub.done": "Hotovo. Boli ste úspešne odhlásený.",
  "unsub.already": "Táto adresa je už odhlásená.",
  "unsub.invalid": "Odkaz je neplatný alebo vypršal.",
  "unsub.errorVerify": "Odkaz sa nepodarilo overiť.",
  "unsub.errorGeneric": "Niečo sa nepodarilo.",
  "unsub.errorNetwork": "Chyba siete.",
  "pay.successPageTitle": "Platba úspešná — Cerveza.International",
  "pay.successTitle": "Platba prebehla úspešne",
  "pay.successDesc": "Ďakujeme. Vaša objednávka sa spracúva — odkaz alebo fotografia sa aktivuje niekoľko sekúnd po potvrdení.",
  "pay.backToMy": "Späť na Moje podniky",
  "pay.cancelPageTitle": "Platba zrušená — Cerveza.International",
  "pay.cancelTitle": "Platba zrušená",
  "pay.cancelDesc": "Žiadna suma nebola strhnutá. Platbu môžete kedykoľvek zopakovať zo stránky Moje podniky.",
  "profile.errorTitle": "Chyba",
  "profile.notFound": "Podnik nenájdený",
  "profile.backToMap": "Späť na mapu",
  "profile.noDescription": "Zatiaľ žiadny popis. Pridajte ho v jazykoch, v ktorých chcete podnik prezentovať.",
  "profile.editDescription": "Upraviť popis",
  "profile.multiLangDesc": "Viacjazyčný popis",
  "profile.maxChars": "Max {n} znakov na jazyk",
  "profile.descriptionsSaved": "Popisy uložené",
  "profile.saveFailed": "Uloženie zlyhalo",
  "profile.save": "Uložiť",
  "profile.cancel": "Zrušiť",
  "profile.linksHeader": "Odkazy",
  "profile.viewOnOSM": "Zobraziť na OpenStreetMap",
  "profile.descPlaceholder": "Popis ({lang})",
  "reg.pageTitle": "Registrácia podniku — Cerveza.International",
  "reg.pageDesc": "Zaregistrujte svoj podnik v globálnom katalógu Cerveza.International.",
  "reg.maxTwoKinds": "Môžete vybrať maximálne 2 kategórie.",
  "reg.confirmedKinds": "Potvrdené kategórie: {list}",
  "share.warnFallback": "Pozor: šablóna nie je preložená do „{lang}“, odosiela sa český originál.",
  "share.copiedMsg": "Správa skopírovaná — vložte ju do požadovanej aplikácie.",
  "share.copyFailed": "Kopírovanie zlyhalo.",
  "share.copiedLink": "Odkaz skopírovaný.",
  "share.copyLink": "Kopírovať odkaz",
  "share.copyMessage": "Kopírovať správu",
  "my.photoUploadedPending": "Fotka nahraná, čaká na schválenie adminom.",
  "my.photoPendingApproval": "čaká na schválenie",
  "my.rejectReason": "Dôvod zamietnutia: {reason}",
  "my.rejectedShort": "zamietnuté",
  "my.freeOutOf": "{free}/3 zdarma · ďalšia: {price}",
  "my.photoPack": "Foto balík:",
  "my.pieces": "ks",
  "majitele.pageTitle": "Majitelia Vacantov — Cerveza.International",

  // Downloads section
  "downloads.title": "Na stiahnutie",
  "downloads.subtitle": "Manuály a sprievodcovia na stiahnutie.",
  "downloads.vakantOnly": "Len pre majiteľov Vacantov",
  "downloads.signInToView": "Prihláste sa pre zobrazenie",
  "downloads.downloadPdf": "Stiahnuť PDF",
  "downloads.empty": "Zatiaľ tu nie sú žiadne manuály.",

};
const ro: Dict = {
  "nav.home": "Acasă",
  "nav.back": "Înapoi",
  "nav.katalog": "Catalog",
  "nav.mapa": "Hartă",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Panoul meu",
  "nav.proMajitele": "Pentru proprietari",
  "nav.admin": "Admin",
  "nav.cenik": "Tarife",
  "nav.mujUcet": "Contul meu",
  "nav.odhlasit": "Deconectare",
  "nav.prihlasit": "Conectare",
  "nav.vakanty": "Proprietari Vacant",
  "photos.title": "Fotografii",
  "photos.add": "Adaugă fotografie",
  "photos.maxSize": "Max 3 MB / fotografie. Primele 3 gratuite, suplimentar: {price} o singură dată.",
  "photos.tooLarge": "Fotografia depășește 3 MB.",
  "photos.invalidType": "Fișierul trebuie să fie o imagine.",
  "photos.uploadFailed": "Încărcarea a eșuat: {msg}",
  "photos.freeAdded": "Fotografie adăugată gratuit.",
  "photos.paidPending": "Fotografia a fost creată, în așteptarea plății.",
  "photos.deleted": "Fotografie ștearsă.",
  "photos.priceLabel": "Preț pentru fiecare fotografie suplimentară",
  "home.title.a": "Descoperiți aromele lumii într-",
  "home.title.b": "un singur loc",
  "home.subtitle": "De la bistrouri de familie din Praga la baruri ascunse din Barcelona. Ghidul global pentru iubitorii de gastronomie autentică.",
  "home.search.what": "Ce căutați? (ex. Tapas)",
  "home.search.where": "Unde? (ex. București)",
  "home.search.submit": "Caută",
  "cat.restaurace": "Restaurante",
  "cat.bary": "Baruri",
  "cat.kavarny": "Cafenele",
  "cat.cajovny": "Ceainării",
  "cat.stanky": "Standuri",
  "cat.jidelny": "Cantine",
  "cat.mlecnyBar": "Baruri lactate",
  "cat.foodTruck": "Food trucks",
  "cat.rychleObcerstveni": "Fast Food",
  "cat.bistra": "Bistrouri",
  "home.mapaCta": "Explorează harta lumii",
  "home.featured.title": "Localuri recomandate",
  "home.featured.sub": "Cele mai bine cotate bijuterii din comunitatea noastră.",
  "home.featured.all": "Vezi toate",
  "dc.tag": "Program de loialitate",
  "dc.title": "Deveniți membru Direct Club",
  "dc.text": "Câștigați puncte bonus la fiecare vizită, beneficiați de reduceri exclusive și descoperiți meniuri speciale disponibile doar pentru membrii rețelei Cerveza.International.",
  "dc.register": "Înregistrare gratuită",
  "dc.how": "Cum funcționează?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Număr card",
  "dc.points": "Puncte adunate",
  "owner.title": "Dețineți un local gastronomic?",
  "owner.text": "Alăturați-vă miilor de localuri și ajungeți la oaspeți din întreaga lume. Înregistrarea este rapidă și gratuită.",
  "owner.cta": "Adăugați-vă localul",
  "footer.rights": "Toate drepturile rezervate",
  "footer.about": "Despre",
  "footer.terms": "Termeni",
  "footer.privacy": "Confidențialitate",
  "footer.contact": "Contact",
  "auth.signIn": "Conectare",
  "auth.signUp": "Înregistrare",
  "auth.tabSignIn": "Conectează-te",
  "auth.tabSignUp": "Creează cont",
  "auth.google": "Continuă cu Google",
  "auth.apple": "Continuă cu Apple",
  "auth.or": "sau",
  "auth.email": "Email",
  "auth.password": "Parolă",
  "auth.passwordMin": "Parolă (min. 6 caractere)",
  "auth.name": "Nume complet",
  "auth.submitSignIn": "Conectează-te",
  "auth.submitSignInLoading": "Se conectează…",
  "auth.submitSignUp": "Creează cont",
  "auth.submitSignUpLoading": "Se înregistrează…",
  "auth.firstAdminNote": "Primul cont înregistrat devine automat administratorul sistemului.",
  "auth.back": "← Înapoi la site",
  "auth.welcomeBack": "Bine ați revenit!",
  "auth.created": "Cont creat.",
  "auth.oauthFailed": "Autentificare eșuată",
  "mapa.tag": "Hartă",
  "mapa.loading": "Se încarcă localurile…",
  "mapa.count": "localuri aprobate",
  "mapa.kindHeader": "Tip de local",
  "mapa.addBusiness": "Adaugă local",
  "mapa.showUsers": "Membri Direct Club",
  "mapa.showAnon": "Oaspeți",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Local din catalog",
  "mapa.catalog.brewery": "Berărie din catalog",
  "mapa.catalog.imported": "Importat din catalogul XLSX Cerveza Data Standard",
  "mapa.catalog.detail": "Detaliu",
  "mapa.review.discovery": "Neverificat",
  "mapa.review.pending": "În așteptare",
  "mapa.review.verified": "Verificat",
  "mapa.review.rejected": "Respins",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "America de Nord",
  "mapa.continent.southAmerica": "America de Sud",
  "mapa.continent.asia": "Asia",
  "mapa.continent.africa": "Africa",
  "mapa.continent.oceania": "Oceania",
  "mapa.continent.other": "Altele",
  "mapa.memberBadge": "Membru Direct Club",
  "mapa.partnerHint": "Companie parteneră verificată înregistrată pe Cerveza.International",
  "mapa.memberPrivacyNote": "Utilizator înregistrat · locație aproximativă pentru confidențialitate",
  "mapa.anonPrivacyNote": "Vizitator anonim · locație vizibilă 24 h, apoi eliminată",
  "mapa.legend.title": "Legendă",
  "mapa.userPin.title": "Membru înregistrat Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Locație aproximativă (fără adresă exactă)",
  "mapa.viewProfile": "Profil local",
  "mapa.empty.a": "Încă nu există localuri aprobate. Fiți primul —",
  "mapa.empty.b": "înregistrați un local",
  "mapa.anonPin.title": "Vizitator anonim",
  "mapa.anonPin.subtitle": "Activ în ultimele 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtre",
  "mapa.filters.search": "Căutare",
  "mapa.filters.searchPlaceholder": "Nume, oraș, descriere…",
  "mapa.filters.location": "Locație",
  "mapa.filters.continent": "Continent",
  "mapa.filters.allContinents": "Toate continentele",
  "mapa.filters.country": "Țară",
  "mapa.filters.allCountries": "Toate țările",
  "mapa.filters.region": "Regiune",
  "mapa.filters.regionEmpty": "Regiune (gol)",
  "mapa.filters.allRegions": "Toate regiunile",
  "mapa.filters.city": "Oraș",
  "mapa.filters.allCities": "Toate orașele",
  "mapa.filters.postal": "Cod poștal",
  "mapa.filters.partnership": "Parteneriat",
  "mapa.filters.reset": "Resetează filtre",
  "mapa.empty.filtered": "Niciun local nu corespunde filtrului.",
  "mapa.empty.resetFilters": "Resetează filtre",
  "mapa.location.missing": "Locație lipsă",
  "mapa.location.stale": "Locație veche",
  "mapa.location.fresh": "Locație recentă",
  "mapa.location.refresh": "Actualizează locația",
  "mapa.location.lastSeen": "Ultima dată",
  "mapa.location.hidden": "ascunsă pe hartă",
  "mapa.location.never": "niciodată",
  "mapa.location.toastOk": "Locație actualizată",
  "mapa.location.toastCached": "Valoare recentă din cache.",
  "mapa.location.toastSource": "Sursă",
  "mapa.location.toastFail": "Locația nu a putut fi determinată",
  "mapa.location.toastFailDesc": "Încercați mai târziu (geo indisponibil).",
  "mapa.location.toastError": "Eroare la actualizarea locației",
  "mapa.breweries": "Berării",
  "mapa.breweriesHint": "Berării și producători de bere",
  "mapa.showMembersNoBusiness": "Membri fără afacere",
  "mapa.memberNoBusiness.badge": "Membru fără afacere",
  "mapa.memberNoBusiness.title": "Membru gata să construiască rețeaua",
  "mapa.memberNoBusiness.subtitle": "Poate recomanda noi membri, afaceri și evalua locurile vizitate.",
  "mapa.memberNoBusiness.recommendBusiness": "Recomandă o afacere",
  "mapa.memberNoBusiness.inviteMember": "Recomandă un membru",
  "mapa.memberNoBusiness.rateVisited": "Evaluează afacerea vizitată",
  "mapa.memberNoBusiness.privacy": "Locație aproximativă pentru protecția confidențialității",
  "mapa.memberNoBusiness.hint": "Membri înregistrați care nu dețin o afacere — candidați ideali pentru recomandări",
  "my.title": "Localurile mele",
  "my.sub": "Gestionați-vă localurile și linkurile acestora.",
  "my.add": "Adaugă local",
  "my.loading": "Se încarcă…",
  "my.empty.a": "Nu aveți încă niciun local.",
  "my.empty.b": "Înregistrați primul",
  "my.links": "Linkuri",
  "my.noLinks": "Fără linkuri.",
  "my.free": "Gratuit",
  "my.paid": "Plătit",
  "my.active": "Activ",
  "my.rejected": "Respins",
  "my.pendingPayment": "În așteptarea plății",
  "my.pay": "Plătește",
  "my.payTooltip": "Plata online va fi disponibilă în curând",
  "my.addLink": "Adaugă link",
  "my.linkInfo": "Linkurile către <strong>directclub.digital</strong> / <strong>direct.club</strong> sunt gratuite (max 3 per local). Alte linkuri externe: <strong>{price}</strong> o singură dată (link permanent după plată).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Etichetă (opțional)",
  "my.addFree": "Adaugă gratuit",
  "my.addPaid": "Adaugă pentru {price}",
  "my.freeAdded": "Linkul gratuit a fost adăugat și publicat.",
  "my.paidPending": "Link creat, în așteptarea plății.",
  "my.freeLimit": "Limita de 3 linkuri directclub gratuite a fost atinsă.",
  "my.willBeFree": "✓ link directclub — va fi gratuit și publicat imediat.",
  "my.willBePaid": "Link extern — necesită plata de {price} (publicat după plată).",
  "my.invalidUrl": "URL invalid",
  "reg.back": "← Înapoi la site",
  "reg.moderation": "Moderare hibridă",
  "reg.title": "Înregistrare local",
  "reg.desc": "După introducerea codului poștal, vom completa automat orașul și coordonatele din baza de date a codurilor poștale. Proprietarii verificați sunt aprobați automat, restul intrărilor trec prin moderare.",
  "reg.name": "Numele localului",
  "reg.namePlaceholder": "ex. Café Vinohrady",
  "reg.kind": "Tip de local",
  "reg.kindPlaceholder": "Selectați tipul",
  "reg.country": "Țară",
  "reg.psc": "Cod poștal",
  "reg.pscPlaceholder": "ex. 010101",
  "reg.pscInvalid": "Format cod poștal invalid (CZ: 5 cifre).",
  "reg.searching": "Caut orașul…",
  "reg.notFound": "Nu am găsit niciun oraș în baza de date pentru codul poștal {postal}.",
  "reg.foundOne": "Oraș găsit:",
  "reg.foundMany": "S-au găsit {count} orașe — alegeți-l pe cel corect:",
  "reg.coords": "Coordonate:",
  "reg.mapPreview": "Previzualizare locație pe hartă",
  "reg.mapHint": "Trageți pinul sau dați clic pe hartă pentru a rafina locația.",
  "reg.street": "Stradă și număr (opțional)",
  "reg.streetPlaceholder": "ex. Korunní 12",
  "reg.descLabel": "Descriere scurtă (opțional)",
  "reg.descMultiHint": "Puteți completa descrierea în una sau mai multe limbi. Vizitatorii o văd în limba lor; dacă lipsește, se folosește alta disponibilă.",
  "reg.descPlaceholder": "Ce face localul dvs. special?",
  "reg.cancel": "Anulează",
  "reg.submit": "Trimite pentru înregistrare",
  "reg.submitted": "Local trimis pentru moderare.",
  "reg.notSignedIn": "Nu sunteți autentificat",
  "reg.pickCity": "Selectați un oraș valid din rezultatele codului poștal",
  "reg.invalidData": "Date invalide",
  "reg.gate.title": "Confirmarea proprietății localului",
  "reg.gate.desc": "Doar proprietarul sau operatorul poate înregistra un local. Selectați 1–2 categorii din care face parte localul dvs.",
  "reg.gate.confirmBefore": "Confirm că sunt",
  "reg.gate.confirmStrong": "proprietarul sau operatorul",
  "reg.gate.confirmAfter": "localului pe care doresc să îl înregistrez și că aparține uneia dintre categoriile de mai jos.",
  "reg.gate.kindsLabel": "Categoria localului (selectați 1–2)",
  "reg.gate.selected": "Selectate: {count} / 2",
  "reg.continue": "Continuă",
  "kind.restaurace": "Restaurant",
  "kind.bar": "Bar",
  "kind.kavarna": "Cafenea",
  "kind.cajovna": "Ceainărie",
  "kind.stanek": "Stand",
  "kind.jidelna": "Cantină",
  "kind.mlecny_bar": "Bar lactat",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bistro",
  "status.pending": "În așteptarea aprobării",
  "status.approved": "Aprobat",
  "status.rejected": "Respins",
  "status.hidden": "Ascuns",
  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} luni gratis până la {date}",
  "auth.checkEmail": "Verificați-vă căsuța de email și confirmați înregistrarea făcând clic pe linkul trimis.",
  "home.latest.title": "Cele mai noi localuri",
  "home.latest.sub": "Localuri aprobate adăugate recent.",
  "home.latest.empty": "Încă nu există localuri aprobate.",
  "home.latest.viewAll": "Arată pe hartă →",
  "vakanty.title": "Panoul meu",
  "vakanty.welcomeWithName": "Bun venit, {name}. Mai jos vedeți numărul de Vacant din contul dvs.",
  "vakanty.welcomeNoName": "Bun venit. Mai jos vedeți numărul de Vacant din contul dvs.",
  "vakanty.purchasedLabel": "Vacant achiziționate",
  "vakanty.emptyHint": "Nu aveți încă Vacant. Cereți administratorului să adauge mai multe.",
  "vakanty.featuresTitle": "Funcții și informații",
  "vakanty.kindFeature": "Funcție",
  "vakanty.kindInfo": "Informație",
  "vakanty.open": "Deschide",
 "vakanty.loading": "Se încarcă…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Document intern disponibil exclusiv proprietarilor de Vacants.",
 "vakanty.bp.strictHeader": "Strict confidențial (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Acest document este strict confidențial și constituie un secret comercial al For Direct s.r.o.. Documentul nu poate fi descărcat, redirecționat, copiat, partajat, publicat sau distribuit în orice alt mod către terți — inclusiv părți ale acestuia, capturi de ecran, fotografii sau transcrieri. Documentul este disponibil exclusiv pentru vizualizare online pe această pagină și doar proprietarului de Vacant autentificat. Orice utilizare, reproducere sau distribuire neautorizată reprezintă o încălcare a obligațiilor contractuale și poate duce la răspundere civilă și penală. Încălcarea prezentului acord de confidențialitate este sancționată cu o penalitate contractuală în valoare de 5.000.000 EUR (cinci milioane de euro) pentru fiecare încălcare individuală.",
 "vakanty.bp.paragraph2": "Prin bifarea căsuței de mai jos confirmați că ați citit aceste reguli, le înțelegeți și vă angajați să le respectați. Consimțământul va fi înregistrat cu marcaj temporal.",
 "vakanty.bp.acceptedOn": "NDA acceptat la {date}.",
 "vakanty.bp.checkboxLabel": "Declar că am citit termenii de confidențialitate de mai sus, îi accept și mă angajez să nu partajez și să nu distribui acest document.",
 "vakanty.bp.acceptButton": "Acceptă NDA și vizualizează",
 "vakanty.bp.showButton": "Afișează Business Plan",
 "vakanty.bp.hideButton": "Ascunde documentul",
 "vakanty.bp.viewerNotice": "Strict confidențial — nu copiați, nu distribuiți, nu descărcați. Vizualizare exclusiv pe această pagină.",
 "vakanty.bp.mustCheckFirst": "Bifați mai întâi acceptarea NDA.",
 "vakanty.bp.acceptedToast": "NDA acceptat. Puteți vizualiza documentul acum.",
  "common.error": "Eroare",
  "common.tryAgain": "Încearcă din nou",
  "common.goHome": "Înapoi la pagina principală",
  "common.pageNotFound": "Pagină negăsită",
  "common.pageNotFoundDesc": "Pagina căutată nu există sau a fost mutată.",
  "common.errorTitle": "Această pagină nu s-a încărcat",
  "common.errorDesc": "Ceva a mers greșit. Încearcă să reîncarci sau să te întorci acasă.",
  "auth.enterEmailFirst": "Introdu mai întâi e-mailul.",
  "auth.resendResult": "Dacă contul există și nu este confirmat, am trimis un link nou. Încercări rămase: {remaining}.",
  "auth.resendFailed": "Nu s-a putut trimite e-mailul.",
  "auth.emailAlreadyRegistered": "Acest e-mail este deja înregistrat. Verifică inbox-ul sau autentifică-te.",
  "auth.resending": "Se trimite…",
  "auth.resendConfirmation": "Retrimite e-mailul de confirmare",
  "auth.signInDesc": "Autentifică-te sau înregistrează-te la Cerveza.International.",
  "auth.pageTitle": "Autentificare — Cerveza.International",
  "authReset.pageTitle": "Setare parolă — Cerveza.International",
  "authReset.title": "Setează o parolă nouă",
  "authReset.needLink": "Deschide această pagină din linkul primit pe e-mail.",
  "authReset.newPassword": "Parolă nouă",
  "authReset.passwordAgain": "Confirmă parola",
  "authReset.saving": "Se salvează…",
  "authReset.save": "Salvează parola",
  "authReset.tooShort": "Parola trebuie să aibă cel puțin 8 caractere.",
  "authReset.mismatch": "Parolele nu se potrivesc.",
  "authReset.saved": "Parolă setată.",
  "unsub.pageTitle": "Dezabonare — Cerveza.International",
  "unsub.title": "Dezabonare",
  "unsub.checking": "Se verifică linkul…",
  "unsub.confirmPrompt": "Confirmă că dorești dezabonarea de la e-mailurile Cerveza.International.",
  "unsub.confirmBtn": "Confirmă dezabonarea",
  "unsub.processing": "Se dezabonează…",
  "unsub.done": "Gata. Ai fost dezabonat cu succes.",
  "unsub.already": "Această adresă este deja dezabonată.",
  "unsub.invalid": "Linkul este invalid sau a expirat.",
  "unsub.errorVerify": "Nu s-a putut verifica linkul.",
  "unsub.errorGeneric": "Ceva a mers greșit.",
  "unsub.errorNetwork": "Eroare de rețea.",
  "pay.successPageTitle": "Plată reușită — Cerveza.International",
  "pay.successTitle": "Plată efectuată cu succes",
  "pay.successDesc": "Mulțumim. Comanda ta este procesată — linkul sau fotografia se va activa în câteva secunde după confirmare.",
  "pay.backToMy": "Înapoi la Afacerile mele",
  "pay.cancelPageTitle": "Plată anulată — Cerveza.International",
  "pay.cancelTitle": "Plată anulată",
  "pay.cancelDesc": "Nu s-a debitat nicio sumă. Poți repeta plata din pagina Afacerile mele.",
  "profile.errorTitle": "Eroare",
  "profile.notFound": "Afacere negăsită",
  "profile.backToMap": "Înapoi la hartă",
  "profile.noDescription": "Încă nicio descriere. Adaug-o în limbile în care vrei să prezinți afacerea.",
  "profile.editDescription": "Editează descrierea",
  "profile.multiLangDesc": "Descriere multilingvă",
  "profile.maxChars": "Max. {n} caractere pe limbă",
  "profile.descriptionsSaved": "Descrieri salvate",
  "profile.saveFailed": "Salvarea a eșuat",
  "profile.save": "Salvează",
  "profile.cancel": "Anulează",
  "profile.linksHeader": "Linkuri",
  "profile.viewOnOSM": "Vezi pe OpenStreetMap",
  "profile.descPlaceholder": "Descriere ({lang})",
  "reg.pageTitle": "Înregistrează o afacere — Cerveza.International",
  "reg.pageDesc": "Înregistrează-ți afacerea în catalogul global Cerveza.International.",
  "reg.maxTwoKinds": "Se pot selecta maximum 2 categorii.",
  "reg.confirmedKinds": "Categorii confirmate: {list}",
  "share.warnFallback": "Atenție: șablonul nu este tradus în „{lang}”, se trimite originalul în cehă.",
  "share.copiedMsg": "Mesaj copiat — lipește-l în aplicația dorită.",
  "share.copyFailed": "Copierea a eșuat.",
  "share.copiedLink": "Link copiat.",
  "share.copyLink": "Copiază linkul",
  "share.copyMessage": "Copiază mesajul",
  "my.photoUploadedPending": "Fotografie încărcată, așteaptă aprobarea administratorului.",
  "my.photoPendingApproval": "așteaptă aprobare",
  "my.rejectReason": "Motivul respingerii: {reason}",
  "my.rejectedShort": "respins",
  "my.freeOutOf": "{free}/3 gratis · următoarea: {price}",
  "my.photoPack": "Pachet foto:",
  "my.pieces": "buc",
  "majitele.pageTitle": "Proprietari de Vacants — Cerveza.International",

  // Downloads section
  "downloads.title": "Descărcări",
  "downloads.subtitle": "Manuale și ghiduri pentru descărcare.",
  "downloads.vakantOnly": "Doar pentru proprietarii de Vacants",
  "downloads.signInToView": "Autentifică-te pentru a vedea",
  "downloads.downloadPdf": "Descarcă PDF",
  "downloads.empty": "Momentan nu există manuale.",

};
const it: Dict = {
  "nav.home": "Home",
  "nav.back": "Indietro",
  "nav.katalog": "Catalogo",
  "nav.mapa": "Mappa",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Il mio pannello",
  "nav.proMajitele": "Per i proprietari",
  "nav.admin": "Admin",
  "nav.cenik": "Tariffe",
  "nav.mujUcet": "Il mio account",
  "nav.odhlasit": "Esci",
  "nav.prihlasit": "Accedi",
  "nav.vakanty": "Proprietari Vacant",
  "photos.title": "Foto",
  "photos.add": "Aggiungi foto",
  "photos.maxSize": "Max 3 MB / foto. Prime 3 foto gratis, ulteriori: {price} una tantum.",
  "photos.tooLarge": "La foto supera 3 MB.",
  "photos.invalidType": "Il file deve essere un'immagine.",
  "photos.uploadFailed": "Caricamento non riuscito: {msg}",
  "photos.freeAdded": "Foto aggiunta gratuitamente.",
  "photos.paidPending": "Foto creata, in attesa di pagamento.",
  "photos.deleted": "Foto eliminata.",
  "photos.priceLabel": "Prezzo per ogni foto aggiuntiva",
  "home.title.a": "Scopri i sapori del mondo in",
  "home.title.b": "un solo posto",
  "home.subtitle": "Dai bistrot familiari di Praga ai bar nascosti di Barcellona. La guida globale per gli amanti della gastronomia autentica.",
  "home.search.what": "Cosa cerchi? (es. Tapas)",
  "home.search.where": "Dove? (es. Roma)",
  "home.search.submit": "Cerca",
  "cat.restaurace": "Ristoranti",
  "cat.bary": "Bar",
  "cat.kavarny": "Caffetterie",
  "cat.cajovny": "Sale da tè",
  "cat.stanky": "Chioschi",
  "cat.jidelny": "Mense",
  "cat.mlecnyBar": "Latterie",
  "cat.foodTruck": "Food truck",
  "cat.rychleObcerstveni": "Fast food",
  "cat.bistra": "Bistrò",
  "home.mapaCta": "Esplora la mappa del mondo",
  "home.featured.title": "Locali in evidenza",
  "home.featured.sub": "I gioielli più apprezzati della nostra comunità.",
  "home.featured.all": "Vedi tutti",
  "dc.tag": "Programma fedeltà",
  "dc.title": "Diventa membro del Direct Club",
  "dc.text": "Guadagna punti bonus a ogni visita, approfitta di sconti esclusivi e scopri menù speciali disponibili solo per i membri della rete Cerveza.International.",
  "dc.register": "Registrati gratis",
  "dc.how": "Come funziona?",
  "dc.status": "Status",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Numero carta",
  "dc.points": "Punti raccolti",
  "owner.title": "Hai un locale gastronomico?",
  "owner.text": "Unisciti a migliaia di locali e raggiungi ospiti da tutto il mondo. La registrazione è veloce e gratuita.",
  "owner.cta": "Aggiungi il tuo locale",
  "footer.rights": "Tutti i diritti riservati",
  "footer.about": "Chi siamo",
  "footer.terms": "Termini",
  "footer.privacy": "Privacy",
  "footer.contact": "Contatti",
  "auth.signIn": "Accedi",
  "auth.signUp": "Registrati",
  "auth.tabSignIn": "Accedi",
  "auth.tabSignUp": "Crea account",
  "auth.google": "Continua con Google",
  "auth.apple": "Continua con Apple",
  "auth.or": "oppure",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.passwordMin": "Password (min. 6 caratteri)",
  "auth.name": "Nome e cognome",
  "auth.submitSignIn": "Accedi",
  "auth.submitSignInLoading": "Accesso…",
  "auth.submitSignUp": "Crea account",
  "auth.submitSignUpLoading": "Registrazione…",
  "auth.firstAdminNote": "Il primo account registrato diventa automaticamente l'amministratore del sistema.",
  "auth.back": "← Torna al sito",
  "auth.welcomeBack": "Bentornato!",
  "auth.created": "Account creato.",
  "auth.oauthFailed": "Accesso non riuscito",
  "mapa.tag": "Mappa",
  "mapa.loading": "Caricamento locali…",
  "mapa.count": "locali approvati",
  "mapa.kindHeader": "Tipo di locale",
  "mapa.addBusiness": "Aggiungi locale",
  "mapa.showUsers": "Membri del Direct Club",
  "mapa.showAnon": "Ospiti",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Locale dal catalogo",
  "mapa.catalog.brewery": "Birrificio dal catalogo",
  "mapa.catalog.imported": "Importato dal catalogo XLSX Cerveza Data Standard",
  "mapa.catalog.detail": "Dettaglio",
  "mapa.review.discovery": "Non verificato",
  "mapa.review.pending": "In revisione",
  "mapa.review.verified": "Verificato",
  "mapa.review.rejected": "Rifiutato",
  "mapa.continent.europe": "Europa",
  "mapa.continent.northAmerica": "America del Nord",
  "mapa.continent.southAmerica": "America del Sud",
  "mapa.continent.asia": "Asia",
  "mapa.continent.africa": "Africa",
  "mapa.continent.oceania": "Oceania",
  "mapa.continent.other": "Altro",
  "mapa.memberBadge": "Membro Direct Club",
  "mapa.partnerHint": "Azienda partner verificata registrata su Cerveza.International",
  "mapa.memberPrivacyNote": "Utente registrato · posizione approssimativa per la privacy",
  "mapa.anonPrivacyNote": "Visitatore anonimo · posizione visibile 24 h, poi rimossa",
  "mapa.legend.title": "Legenda",
  "mapa.userPin.title": "Membro registrato di Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Posizione approssimativa (senza indirizzo preciso)",
  "mapa.viewProfile": "Profilo del locale",
  "mapa.empty.a": "Ancora nessun locale approvato. Sii il primo —",
  "mapa.empty.b": "registra un locale",
  "mapa.anonPin.title": "Visitatore anonimo",
  "mapa.anonPin.subtitle": "Attivo nelle ultime 24 h",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Filtri",
  "mapa.filters.search": "Cerca",
  "mapa.filters.searchPlaceholder": "Nome, città, descrizione…",
  "mapa.filters.location": "Località",
  "mapa.filters.continent": "Continente",
  "mapa.filters.allContinents": "Tutti i continenti",
  "mapa.filters.country": "Paese",
  "mapa.filters.allCountries": "Tutti i paesi",
  "mapa.filters.region": "Regione",
  "mapa.filters.regionEmpty": "Regione (vuoto)",
  "mapa.filters.allRegions": "Tutte le regioni",
  "mapa.filters.city": "Città",
  "mapa.filters.allCities": "Tutte le città",
  "mapa.filters.postal": "CAP",
  "mapa.filters.partnership": "Partnership",
  "mapa.filters.reset": "Reimposta filtri",
  "mapa.empty.filtered": "Nessun locale corrisponde al filtro.",
  "mapa.empty.resetFilters": "Reimposta filtri",
  "mapa.location.missing": "Posizione mancante",
  "mapa.location.stale": "Posizione obsoleta",
  "mapa.location.fresh": "Posizione aggiornata",
  "mapa.location.refresh": "Aggiorna posizione",
  "mapa.location.lastSeen": "Ultima volta",
  "mapa.location.hidden": "nascosta sulla mappa",
  "mapa.location.never": "mai",
  "mapa.location.toastOk": "Posizione aggiornata",
  "mapa.location.toastCached": "Valore recente in cache.",
  "mapa.location.toastSource": "Fonte",
  "mapa.location.toastFail": "Impossibile determinare la posizione",
  "mapa.location.toastFailDesc": "Riprovare più tardi (geo non disponibile).",
  "mapa.location.toastError": "Errore aggiornamento posizione",
  "mapa.breweries": "Birrifici",
  "mapa.breweriesHint": "Birrifici e produttori di birra",
  "mapa.showMembersNoBusiness": "Membri senza attività",
  "mapa.memberNoBusiness.badge": "Membro senza attività",
  "mapa.memberNoBusiness.title": "Membro pronto a costruire la rete",
  "mapa.memberNoBusiness.subtitle": "Può consigliare nuovi membri, attività e valutare i luoghi visitati.",
  "mapa.memberNoBusiness.recommendBusiness": "Consiglia un'attività",
  "mapa.memberNoBusiness.inviteMember": "Consiglia un membro",
  "mapa.memberNoBusiness.rateVisited": "Valuta attività visitata",
  "mapa.memberNoBusiness.privacy": "Posizione approssimativa per proteggere la privacy",
  "mapa.memberNoBusiness.hint": "Membri registrati che non possiedono un'attività — candidati ideali per consigli",
  "my.title": "I miei locali",
  "my.sub": "Gestisci i tuoi locali e i loro link.",
  "my.add": "Aggiungi locale",
  "my.loading": "Caricamento…",
  "my.empty.a": "Non hai ancora locali.",
  "my.empty.b": "Registra il primo",
  "my.links": "Link",
  "my.noLinks": "Nessun link.",
  "my.free": "Gratuito",
  "my.paid": "A pagamento",
  "my.active": "Attivo",
  "my.rejected": "Rifiutato",
  "my.pendingPayment": "In attesa di pagamento",
  "my.pay": "Paga",
  "my.payTooltip": "Il pagamento online sarà presto disponibile",
  "my.addLink": "Aggiungi link",
  "my.linkInfo": "I link a <strong>directclub.digital</strong> / <strong>direct.club</strong> sono gratuiti (max 3 per locale). Altri link esterni: <strong>{price}</strong> una tantum (link permanente dopo il pagamento).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Etichetta (opzionale)",
  "my.addFree": "Aggiungi gratis",
  "my.addPaid": "Aggiungi per {price}",
  "my.freeAdded": "Link gratuito aggiunto e pubblicato.",
  "my.paidPending": "Link creato, in attesa di pagamento.",
  "my.freeLimit": "Limite di 3 link gratuiti directclub raggiunto.",
  "my.willBeFree": "✓ link directclub — sarà gratuito e pubblicato immediatamente.",
  "my.willBePaid": "Link esterno — richiede pagamento di {price} (pubblicato dopo il pagamento).",
  "my.invalidUrl": "URL non valido",
  "reg.back": "← Torna al sito",
  "reg.moderation": "Moderazione ibrida",
  "reg.title": "Registrazione locale",
  "reg.desc": "Dopo aver inserito il CAP, compileremo automaticamente città e coordinate dal database CAP. I proprietari verificati vengono approvati automaticamente, le altre voci passano per la moderazione.",
  "reg.name": "Nome del locale",
  "reg.namePlaceholder": "es. Café Vinohrady",
  "reg.kind": "Tipo di locale",
  "reg.kindPlaceholder": "Seleziona tipo",
  "reg.country": "Paese",
  "reg.psc": "CAP",
  "reg.pscPlaceholder": "es. 00100",
  "reg.pscInvalid": "Formato CAP non valido (CZ: 5 cifre).",
  "reg.searching": "Ricerca città…",
  "reg.notFound": "Nessuna città trovata nel database per il CAP {postal}.",
  "reg.foundOne": "Città trovata:",
  "reg.foundMany": "Trovate {count} città — scegli quella giusta:",
  "reg.coords": "Coordinate:",
  "reg.mapPreview": "Anteprima della posizione sulla mappa",
  "reg.mapHint": "Trascina il segnaposto o clicca sulla mappa per affinare la posizione.",
  "reg.street": "Via e numero (opzionale)",
  "reg.streetPlaceholder": "es. Korunní 12",
  "reg.descLabel": "Breve descrizione (opzionale)",
  "reg.descMultiHint": "Puoi compilare la descrizione in una o più lingue. I visitatori la vedono nella loro lingua; se manca, viene usata un'altra disponibile.",
  "reg.descPlaceholder": "Cosa rende speciale il tuo locale?",
  "reg.cancel": "Annulla",
  "reg.submit": "Invia per registrazione",
  "reg.submitted": "Locale inviato per moderazione.",
  "reg.notSignedIn": "Non hai effettuato l'accesso",
  "reg.pickCity": "Seleziona una città valida dai risultati del CAP",
  "reg.invalidData": "Dati non validi",
  "reg.gate.title": "Conferma proprietà del locale",
  "reg.gate.desc": "Solo il proprietario o il gestore può registrare un locale. Seleziona 1–2 categorie a cui appartiene il tuo locale.",
  "reg.gate.confirmBefore": "Confermo di essere il",
  "reg.gate.confirmStrong": "proprietario o gestore",
  "reg.gate.confirmAfter": "del locale che voglio registrare e che appartiene a una delle categorie elencate di seguito.",
  "reg.gate.kindsLabel": "Categoria del locale (seleziona 1–2)",
  "reg.gate.selected": "Selezionate: {count} / 2",
  "reg.continue": "Continua",
  "kind.restaurace": "Ristorante",
  "kind.bar": "Bar",
  "kind.kavarna": "Caffetteria",
  "kind.cajovna": "Sala da tè",
  "kind.stanek": "Chiosco",
  "kind.jidelna": "Mensa",
  "kind.mlecny_bar": "Latteria",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast food",
  "kind.bistro": "Bistrò",
  "status.pending": "In attesa di approvazione",
  "status.approved": "Approvato",
  "status.rejected": "Rifiutato",
  "status.hidden": "Nascosto",
  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} mesi gratis fino al {date}",
  "auth.checkEmail": "Controlla la tua casella di posta e conferma la registrazione cliccando sul link che ti abbiamo inviato.",
  "home.latest.title": "Locali più recenti",
  "home.latest.sub": "Locali approvati aggiunti di recente.",
  "home.latest.empty": "Nessun locale approvato ancora.",
  "home.latest.viewAll": "Mostra sulla mappa →",
  "vakanty.title": "Il mio pannello",
  "vakanty.welcomeWithName": "Benvenuto, {name}. Di seguito vedi il numero di Vacant sul tuo account.",
  "vakanty.welcomeNoName": "Benvenuto. Di seguito vedi il numero di Vacant sul tuo account.",
  "vakanty.purchasedLabel": "Vacant acquistati",
  "vakanty.emptyHint": "Non hai ancora Vacant. Chiedi all'amministratore di aggiungerne.",
  "vakanty.featuresTitle": "Funzioni e informazioni",
  "vakanty.kindFeature": "Funzione",
  "vakanty.kindInfo": "Informazione",
  "vakanty.open": "Apri",
 "vakanty.loading": "Caricamento…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Documento interno disponibile esclusivamente ai proprietari di Vacant.",
 "vakanty.bp.strictHeader": "Strettamente riservato (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Questo documento è strettamente riservato e costituisce un segreto commerciale di For Direct s.r.o.. Il documento non può essere scaricato, inoltrato, copiato, condiviso, pubblicato o altrimenti distribuito a terzi — comprese sue parti, screenshot, fotografie o trascrizioni. Il documento è disponibile solo per la consultazione online su questa pagina ed esclusivamente al proprietario di Vacant autenticato. Qualsiasi uso, riproduzione o distribuzione non autorizzata costituisce una violazione degli obblighi contrattuali e può comportare responsabilità civile e penale. Qualsiasi violazione del presente accordo di riservatezza è soggetta a una penale contrattuale pari a 5.000.000 EUR (cinque milioni di euro) per ogni singola violazione.",
 "vakanty.bp.paragraph2": "Selezionando la casella qui sotto confermi di aver letto queste regole, di comprenderle e di impegnarti a rispettarle. Il tuo consenso sarà registrato con marca temporale.",
 "vakanty.bp.acceptedOn": "NDA accettato il {date}.",
 "vakanty.bp.checkboxLabel": "Dichiaro di aver letto le condizioni di riservatezza sopra riportate, di accettarle e di impegnarmi a non condividere né distribuire questo documento.",
 "vakanty.bp.acceptButton": "Accetta NDA e visualizza",
 "vakanty.bp.showButton": "Mostra Business Plan",
 "vakanty.bp.hideButton": "Nascondi documento",
 "vakanty.bp.viewerNotice": "Strettamente riservato — non copiare, condividere o scaricare. Consultazione solo su questa pagina.",
 "vakanty.bp.mustCheckFirst": "Seleziona prima l'accettazione dell'NDA.",
 "vakanty.bp.acceptedToast": "NDA accettato. Ora puoi consultare il documento.",
  "common.error": "Errore",
  "common.tryAgain": "Riprova",
  "common.goHome": "Torna alla home",
  "common.pageNotFound": "Pagina non trovata",
  "common.pageNotFoundDesc": "La pagina che stai cercando non esiste o è stata spostata.",
  "common.errorTitle": "Questa pagina non è stata caricata",
  "common.errorDesc": "Qualcosa è andato storto. Prova a ricaricare o torna alla home.",
  "auth.enterEmailFirst": "Inserisci prima l'e-mail.",
  "auth.resendResult": "Se l'account esiste e non è confermato, abbiamo inviato un nuovo link. Tentativi rimasti: {remaining}.",
  "auth.resendFailed": "Impossibile inviare l'e-mail.",
  "auth.emailAlreadyRegistered": "Questa e-mail è già registrata. Controlla la casella o accedi.",
  "auth.resending": "Invio…",
  "auth.resendConfirmation": "Rinvia e-mail di conferma",
  "auth.signInDesc": "Accedi o registrati a Cerveza.International.",
  "auth.pageTitle": "Accedi — Cerveza.International",
  "authReset.pageTitle": "Imposta password — Cerveza.International",
  "authReset.title": "Imposta una nuova password",
  "authReset.needLink": "Apri questa pagina dal link ricevuto via e-mail.",
  "authReset.newPassword": "Nuova password",
  "authReset.passwordAgain": "Ripeti la password",
  "authReset.saving": "Salvataggio…",
  "authReset.save": "Salva password",
  "authReset.tooShort": "La password deve contenere almeno 8 caratteri.",
  "authReset.mismatch": "Le password non coincidono.",
  "authReset.saved": "Password impostata.",
  "unsub.pageTitle": "Annulla iscrizione — Cerveza.International",
  "unsub.title": "Annulla iscrizione",
  "unsub.checking": "Verifica link…",
  "unsub.confirmPrompt": "Conferma di voler annullare l'iscrizione alle e-mail di Cerveza.International.",
  "unsub.confirmBtn": "Conferma disiscrizione",
  "unsub.processing": "Disiscrizione…",
  "unsub.done": "Fatto. Sei stato disiscritto.",
  "unsub.already": "Questo indirizzo è già disiscritto.",
  "unsub.invalid": "Il link non è valido o è scaduto.",
  "unsub.errorVerify": "Impossibile verificare il link.",
  "unsub.errorGeneric": "Qualcosa è andato storto.",
  "unsub.errorNetwork": "Errore di rete.",
  "pay.successPageTitle": "Pagamento riuscito — Cerveza.International",
  "pay.successTitle": "Pagamento riuscito",
  "pay.successDesc": "Grazie. Il tuo ordine è in elaborazione — il link o la foto sarà attivo entro pochi secondi dalla conferma.",
  "pay.backToMy": "Torna a I miei locali",
  "pay.cancelPageTitle": "Pagamento annullato — Cerveza.International",
  "pay.cancelTitle": "Pagamento annullato",
  "pay.cancelDesc": "Nessun importo addebitato. Puoi ripetere il pagamento dalla pagina I miei locali.",
  "profile.errorTitle": "Errore",
  "profile.notFound": "Locale non trovato",
  "profile.backToMap": "Torna alla mappa",
  "profile.noDescription": "Nessuna descrizione. Aggiungila nelle lingue in cui vuoi presentare il locale.",
  "profile.editDescription": "Modifica descrizione",
  "profile.multiLangDesc": "Descrizione multilingue",
  "profile.maxChars": "Max {n} caratteri per lingua",
  "profile.descriptionsSaved": "Descrizioni salvate",
  "profile.saveFailed": "Salvataggio non riuscito",
  "profile.save": "Salva",
  "profile.cancel": "Annulla",
  "profile.linksHeader": "Link",
  "profile.viewOnOSM": "Vedi su OpenStreetMap",
  "profile.descPlaceholder": "Descrizione ({lang})",
  "reg.pageTitle": "Registra un locale — Cerveza.International",
  "reg.pageDesc": "Registra il tuo locale nel catalogo globale Cerveza.International.",
  "reg.maxTwoKinds": "Puoi selezionare al massimo 2 categorie.",
  "reg.confirmedKinds": "Categorie confermate: {list}",
  "share.warnFallback": "Attenzione: il modello non è tradotto in «{lang}», viene inviato l'originale in ceco.",
  "share.copiedMsg": "Messaggio copiato — incollalo nell'app desiderata.",
  "share.copyFailed": "Copia non riuscita.",
  "share.copiedLink": "Link copiato.",
  "share.copyLink": "Copia link",
  "share.copyMessage": "Copia messaggio",
  "my.photoUploadedPending": "Foto caricata, in attesa di approvazione dell'admin.",
  "my.photoPendingApproval": "in attesa di approvazione",
  "my.rejectReason": "Motivo del rifiuto: {reason}",
  "my.rejectedShort": "rifiutata",
  "my.freeOutOf": "{free}/3 gratis · prossima: {price}",
  "my.photoPack": "Pacchetto foto:",
  "my.pieces": "pz",
  "majitele.pageTitle": "Proprietari di Vacants — Cerveza.International",

  // Downloads section
  "downloads.title": "Download",
  "downloads.subtitle": "Manuali e guide da scaricare.",
  "downloads.vakantOnly": "Solo per proprietari di Vacants",
  "downloads.signInToView": "Accedi per visualizzare",
  "downloads.downloadPdf": "Scarica PDF",
  "downloads.empty": "Nessun manuale disponibile.",

};
const hu: Dict = {
  "nav.home": "Főoldal",
  "nav.back": "Vissza",
  "nav.katalog": "Katalógus",
  "nav.mapa": "Térkép",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Saját panel",
  "nav.proMajitele": "Tulajdonosoknak",
  "nav.admin": "Admin",
  "nav.cenik": "Árlista",
  "nav.mujUcet": "Fiókom",
  "nav.odhlasit": "Kijelentkezés",
  "nav.prihlasit": "Bejelentkezés",
  "nav.vakanty": "Vacant tulajdonosok",
  "photos.title": "Fotók",
  "photos.add": "Fotó hozzáadása",
  "photos.maxSize": "Max. 3 MB / fotó. Az első 3 fotó ingyenes, további: {price} egyszeri díj.",
  "photos.tooLarge": "A fotó meghaladja a 3 MB-ot.",
  "photos.invalidType": "A fájlnak képnek kell lennie.",
  "photos.uploadFailed": "Feltöltés sikertelen: {msg}",
  "photos.freeAdded": "Fotó ingyenesen hozzáadva.",
  "photos.paidPending": "Fotó létrehozva, fizetésre vár.",
  "photos.deleted": "Fotó törölve.",
  "photos.priceLabel": "Ár minden további fotóért",
  "home.title.a": "Fedezd fel a világ ízeit",
  "home.title.b": "egy helyen",
  "home.subtitle": "A prágai családi bisztróktól a barcelonai eldugott bárokig. Globális kalauz az autentikus gasztronómia szerelmeseinek.",
  "home.search.what": "Mit keresel? (pl. Tapas)",
  "home.search.where": "Hol? (pl. Budapest)",
  "home.search.submit": "Keresés",
  "cat.restaurace": "Éttermek",
  "cat.bary": "Bárok",
  "cat.kavarny": "Kávézók",
  "cat.cajovny": "Teaházak",
  "cat.stanky": "Standok",
  "cat.jidelny": "Étkezdék",
  "cat.mlecnyBar": "Tejivók",
  "cat.foodTruck": "Food truckok",
  "cat.rychleObcerstveni": "Fast Food",
  "cat.bistra": "Bisztrók",
  "home.mapaCta": "Fedezd fel a világtérképet",
  "home.featured.title": "Ajánlott helyek",
  "home.featured.sub": "Közösségünk legjobban értékelt gyöngyszemei.",
  "home.featured.all": "Összes megtekintése",
  "dc.tag": "Hűségprogram",
  "dc.title": "Legyél Direct Club tag",
  "dc.text": "Szerezz bónuszpontokat minden látogatás után, élvezd az exkluzív kedvezményeket és fedezd fel a csak tagoknak elérhető különleges menüket a Cerveza.International hálózatában.",
  "dc.register": "Ingyenes regisztráció",
  "dc.how": "Hogyan működik?",
  "dc.status": "Státusz",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Kártyaszám",
  "dc.points": "Gyűjtött pontok",
  "owner.title": "Saját vendéglátóhelyed van?",
  "owner.text": "Csatlakozz vendéglátóhelyek ezreihez és érd el a világ minden tájáról érkező vendégeket. A regisztráció gyors és ingyenes.",
  "owner.cta": "Add hozzá a helyed",
  "footer.rights": "Minden jog fenntartva",
  "footer.about": "Rólunk",
  "footer.terms": "Feltételek",
  "footer.privacy": "Adatvédelem",
  "footer.contact": "Kapcsolat",
  "auth.signIn": "Bejelentkezés",
  "auth.signUp": "Regisztráció",
  "auth.tabSignIn": "Bejelentkezés",
  "auth.tabSignUp": "Fiók létrehozása",
  "auth.google": "Folytatás Google-fiókkal",
  "auth.apple": "Folytatás Apple-fiókkal",
  "auth.or": "vagy",
  "auth.email": "Email",
  "auth.password": "Jelszó",
  "auth.passwordMin": "Jelszó (min. 6 karakter)",
  "auth.name": "Teljes név",
  "auth.submitSignIn": "Bejelentkezés",
  "auth.submitSignInLoading": "Bejelentkezés…",
  "auth.submitSignUp": "Fiók létrehozása",
  "auth.submitSignUpLoading": "Regisztráció…",
  "auth.firstAdminNote": "Az első regisztrált fiók automatikusan a rendszer adminisztrátora lesz.",
  "auth.back": "← Vissza az oldalra",
  "auth.welcomeBack": "Üdv újra!",
  "auth.created": "Fiók létrehozva.",
  "auth.oauthFailed": "Bejelentkezés sikertelen",
  "mapa.tag": "Térkép",
  "mapa.loading": "Helyek betöltése…",
  "mapa.count": "jóváhagyott hely",
  "mapa.kindHeader": "Hely típusa",
  "mapa.addBusiness": "Hely hozzáadása",
  "mapa.showUsers": "Direct Club tagok",
  "mapa.showAnon": "Vendégek",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Hely a katalógusból",
  "mapa.catalog.brewery": "Sörfőzde a katalógusból",
  "mapa.catalog.imported": "Importálva a Cerveza Data Standard XLSX katalógusból",
  "mapa.catalog.detail": "Részletek",
  "mapa.review.discovery": "Nem ellenőrzött",
  "mapa.review.pending": "Ellenőrzésre vár",
  "mapa.review.verified": "Ellenőrzött",
  "mapa.review.rejected": "Elutasítva",
  "mapa.continent.europe": "Európa",
  "mapa.continent.northAmerica": "Észak-Amerika",
  "mapa.continent.southAmerica": "Dél-Amerika",
  "mapa.continent.asia": "Ázsia",
  "mapa.continent.africa": "Afrika",
  "mapa.continent.oceania": "Óceánia",
  "mapa.continent.other": "Egyéb",
  "mapa.memberBadge": "Direct Club tag",
  "mapa.partnerHint": "Ellenőrzött partnercég a Cerveza.International oldalon",
  "mapa.memberPrivacyNote": "Regisztrált felhasználó · hozzávetőleges hely az adatvédelem érdekében",
  "mapa.anonPrivacyNote": "Névtelen látogató · pozíció 24 órán át látható, majd eltűnik",
  "mapa.legend.title": "Jelmagyarázat",
  "mapa.userPin.title": "Regisztrált Cerveza.International tag — Direct Club",
  "mapa.userPin.subtitle": "Hozzávetőleges hely (pontos cím nélkül)",
  "mapa.viewProfile": "Hely profilja",
  "mapa.empty.a": "Még nincs jóváhagyott hely. Legyél te az első —",
  "mapa.empty.b": "regisztrálj helyet",
  "mapa.anonPin.title": "Névtelen látogató",
  "mapa.anonPin.subtitle": "Aktív az elmúlt 24 órában",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Szűrők",
  "mapa.filters.search": "Keresés",
  "mapa.filters.searchPlaceholder": "Név, város, leírás…",
  "mapa.filters.location": "Helyszín",
  "mapa.filters.continent": "Kontinens",
  "mapa.filters.allContinents": "Összes kontinens",
  "mapa.filters.country": "Ország",
  "mapa.filters.allCountries": "Összes ország",
  "mapa.filters.region": "Régió",
  "mapa.filters.regionEmpty": "Régió (üres)",
  "mapa.filters.allRegions": "Összes régió",
  "mapa.filters.city": "Város",
  "mapa.filters.allCities": "Összes város",
  "mapa.filters.postal": "Irányítószám",
  "mapa.filters.partnership": "Partnerség",
  "mapa.filters.reset": "Szűrők törlése",
  "mapa.empty.filtered": "Nincs a szűrőnek megfelelő hely.",
  "mapa.empty.resetFilters": "Szűrők törlése",
  "mapa.location.missing": "Hiányzó helyszín",
  "mapa.location.stale": "Elavult helyszín",
  "mapa.location.fresh": "Friss helyszín",
  "mapa.location.refresh": "Helyszín frissítése",
  "mapa.location.lastSeen": "Utoljára",
  "mapa.location.hidden": "elrejtve a térképen",
  "mapa.location.never": "soha",
  "mapa.location.toastOk": "Helyszín frissítve",
  "mapa.location.toastCached": "Friss gyorsítótárazott érték.",
  "mapa.location.toastSource": "Forrás",
  "mapa.location.toastFail": "A helyszín nem határozható meg",
  "mapa.location.toastFailDesc": "Próbálja később (geo nem elérhető).",
  "mapa.location.toastError": "Hiba a helyszín frissítésekor",
  "mapa.breweries": "Sörfőzdék",
  "mapa.breweriesHint": "Sörfőzdék és sörgyártók",
  "mapa.showMembersNoBusiness": "Vállalkozás nélküli tagok",
  "mapa.memberNoBusiness.badge": "Tag vállalkozás nélkül",
  "mapa.memberNoBusiness.title": "Tag, aki készen áll a hálózat építésére",
  "mapa.memberNoBusiness.subtitle": "Új tagokat, vállalkozásokat ajánlhat, és értékelheti a meglátogatott helyeket.",
  "mapa.memberNoBusiness.recommendBusiness": "Vállalkozás ajánlása",
  "mapa.memberNoBusiness.inviteMember": "Tag ajánlása",
  "mapa.memberNoBusiness.rateVisited": "Meglévő vállalkozás értékelése",
  "mapa.memberNoBusiness.privacy": "Hozzávetőleges helyzet az adatvédelemért",
  "mapa.memberNoBusiness.hint": "Regisztrált tagok, akiknek nincs saját vállalkozásuk — ideális ajánlási jelöltek",
  "my.title": "Saját helyeim",
  "my.sub": "Kezeld a helyeidet és linkjeiket.",
  "my.add": "Hely hozzáadása",
  "my.loading": "Betöltés…",
  "my.empty.a": "Még nincs egyetlen helyed sem.",
  "my.empty.b": "Regisztráld az elsőt",
  "my.links": "Linkek",
  "my.noLinks": "Nincsenek linkek.",
  "my.free": "Ingyenes",
  "my.paid": "Fizetős",
  "my.active": "Aktív",
  "my.rejected": "Elutasítva",
  "my.pendingPayment": "Fizetésre vár",
  "my.pay": "Fizetés",
  "my.payTooltip": "Az online fizetés hamarosan elérhető lesz",
  "my.addLink": "Link hozzáadása",
  "my.linkInfo": "A <strong>directclub.digital</strong> / <strong>direct.club</strong> linkek ingyenesek (max. 3 helyenként). Egyéb külső linkek: <strong>{price}</strong> egyszeri díj (állandó link fizetés után).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Címke (opcionális)",
  "my.addFree": "Hozzáadás ingyen",
  "my.addPaid": "Hozzáadás {price} áron",
  "my.freeAdded": "Az ingyenes link hozzáadva és közzétéve.",
  "my.paidPending": "Link létrehozva, fizetésre vár.",
  "my.freeLimit": "Elérted a 3 ingyenes directclub link korlátját.",
  "my.willBeFree": "✓ directclub link — ingyenes lesz és azonnal közzétéve.",
  "my.willBePaid": "Külső link — {price} fizetést igényel (fizetés után közzétéve).",
  "my.invalidUrl": "Érvénytelen URL",
  "reg.back": "← Vissza az oldalra",
  "reg.moderation": "Hibrid moderáció",
  "reg.title": "Hely regisztrációja",
  "reg.desc": "Az irányítószám megadása után automatikusan kitöltjük a várost és a koordinátákat az irányítószám-adatbázisból. A hitelesített tulajdonosok automatikusan jóváhagyásra kerülnek, a többi bejegyzés moderáláson megy keresztül.",
  "reg.name": "Hely neve",
  "reg.namePlaceholder": "pl. Café Vinohrady",
  "reg.kind": "Hely típusa",
  "reg.kindPlaceholder": "Válassz típust",
  "reg.country": "Ország",
  "reg.psc": "Irányítószám",
  "reg.pscPlaceholder": "pl. 1011",
  "reg.pscInvalid": "Érvénytelen irányítószám formátum (CZ: 5 számjegy).",
  "reg.searching": "Város keresése…",
  "reg.notFound": "Nem találtunk várost az adatbázisban a {postal} irányítószámhoz.",
  "reg.foundOne": "Talált város:",
  "reg.foundMany": "{count} város található — válaszd ki a megfelelőt:",
  "reg.coords": "Koordináták:",
  "reg.mapPreview": "Hely előnézete a térképen",
  "reg.mapHint": "Húzd a tűt vagy kattints a térképre a hely pontosításához.",
  "reg.street": "Utca és házszám (opcionális)",
  "reg.streetPlaceholder": "pl. Korunní 12",
  "reg.descLabel": "Rövid leírás (opcionális)",
  "reg.descMultiHint": "A leírást egy vagy több nyelven is kitöltheted. A látogatók a saját nyelvükön látják; ha hiányzik, egy másik elérhető nyelvet használunk.",
  "reg.descPlaceholder": "Mitől különleges a helyed?",
  "reg.cancel": "Mégse",
  "reg.submit": "Beküldés regisztrációra",
  "reg.submitted": "Hely moderálásra elküldve.",
  "reg.notSignedIn": "Nem vagy bejelentkezve",
  "reg.pickCity": "Válassz egy érvényes várost az irányítószám eredményei közül",
  "reg.invalidData": "Érvénytelen adatok",
  "reg.gate.title": "Hely tulajdonjogának megerősítése",
  "reg.gate.desc": "Helyet csak a tulajdonos vagy üzemeltető regisztrálhat. Válassz 1–2 kategóriát, amelyhez a helyed tartozik.",
  "reg.gate.confirmBefore": "Megerősítem, hogy én vagyok a",
  "reg.gate.confirmStrong": "tulajdonos vagy üzemeltető",
  "reg.gate.confirmAfter": "annak a helynek, amelyet regisztrálni szeretnék, és hogy az alább felsorolt kategóriák egyikébe tartozik.",
  "reg.gate.kindsLabel": "Hely kategória (válassz 1–2-t)",
  "reg.gate.selected": "Kiválasztva: {count} / 2",
  "reg.continue": "Folytatás",
  "kind.restaurace": "Étterem",
  "kind.bar": "Bár",
  "kind.kavarna": "Kávézó",
  "kind.cajovna": "Teaház",
  "kind.stanek": "Stand",
  "kind.jidelna": "Étkezde",
  "kind.mlecny_bar": "Tejivó",
  "kind.food_truck": "Food truck",
  "kind.rychle_obcerstveni": "Fast Food",
  "kind.bistro": "Bisztró",
  "status.pending": "Jóváhagyásra vár",
  "status.approved": "Jóváhagyva",
  "status.rejected": "Elutasítva",
  "status.hidden": "Rejtett",
  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} hónap ingyen eddig: {date}",
  "auth.checkEmail": "Kérjük, ellenőrizd a postaládádat és erősítsd meg a regisztrációt az elküldött linkre kattintva.",
  "home.latest.title": "Legújabb helyek",
  "home.latest.sub": "Nemrég hozzáadott jóváhagyott helyek.",
  "home.latest.empty": "Még nincsenek jóváhagyott helyek.",
  "home.latest.viewAll": "Mutasd a térképen →",
  "vakanty.title": "Saját panel",
  "vakanty.welcomeWithName": "Üdvözlünk, {name}. Lent láthatod a fiókodban lévő Vacantok számát.",
  "vakanty.welcomeNoName": "Üdvözlünk. Lent láthatod a fiókodban lévő Vacantok számát.",
  "vakanty.purchasedLabel": "Megvásárolt Vacantok",
  "vakanty.emptyHint": "Még nincsenek Vacantjaid. Kérd az adminisztrátort, hogy adjon hozzá.",
  "vakanty.featuresTitle": "Funkciók és információk",
  "vakanty.kindFeature": "Funkció",
  "vakanty.kindInfo": "Információ",
  "vakanty.open": "Megnyitás",
 "vakanty.loading": "Betöltés…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Belső dokumentum, kizárólag a Vacant tulajdonosok számára elérhető.",
 "vakanty.bp.strictHeader": "Szigorúan bizalmas (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Ez a dokumentum szigorúan bizalmas, és a For Direct s.r.o. üzleti titkát képezi. A dokumentumot tilos letölteni, továbbítani, másolni, megosztani, közzétenni vagy bármilyen más módon harmadik fél számára terjeszteni — beleértve annak részeit, képernyőképeit, fényképeit vagy átiratait is. A dokumentum kizárólag ezen az oldalon, online megtekintésre érhető el, és csak a bejelentkezett Vacant-tulajdonos számára. Bármely jogosulatlan felhasználás, sokszorosítás vagy terjesztés a szerződéses kötelezettségek megsértését jelenti, és polgári jogi, valamint büntetőjogi felelősséget vonhat maga után. A jelen titoktartási megállapodás megsértése esetén 5 000 000 EUR (azaz ötmillió euró) összegű szerződéses kötbér fizetendő minden egyes jogsértés után.",
 "vakanty.bp.paragraph2": "Az alábbi jelölőnégyzet bejelölésével megerősíted, hogy elolvastad ezeket a szabályokat, megérted őket, és vállalod, hogy betartod. A hozzájárulásod időbélyeggel kerül rögzítésre.",
 "vakanty.bp.acceptedOn": "NDA elfogadva ekkor: {date}.",
 "vakanty.bp.checkboxLabel": "Kijelentem, hogy a fenti titoktartási feltételeket elolvastam, elfogadom, és vállalom, hogy a dokumentumot nem osztom meg és nem terjesztem.",
 "vakanty.bp.acceptButton": "NDA elfogadása és megtekintés",
 "vakanty.bp.showButton": "Business Plan megjelenítése",
 "vakanty.bp.hideButton": "Dokumentum elrejtése",
 "vakanty.bp.viewerNotice": "Szigorúan bizalmas — ne másold, ne oszd meg, ne töltsd le. Megtekintés kizárólag ezen az oldalon.",
 "vakanty.bp.mustCheckFirst": "Először pipáld be az NDA elfogadását.",
 "vakanty.bp.acceptedToast": "NDA elfogadva. Most már megtekintheted a dokumentumot.",
  "common.error": "Hiba",
  "common.tryAgain": "Újra",
  "common.goHome": "Vissza a főoldalra",
  "common.pageNotFound": "Az oldal nem található",
  "common.pageNotFoundDesc": "A keresett oldal nem létezik vagy át lett helyezve.",
  "common.errorTitle": "Ez az oldal nem töltődött be",
  "common.errorDesc": "Valami hiba történt. Próbáld frissíteni vagy vissza a főoldalra.",
  "auth.enterEmailFirst": "Először add meg az e-mailt.",
  "auth.resendResult": "Ha a fiók létezik és nincs megerősítve, új linket küldtünk. Hátralévő próbálkozások: {remaining}.",
  "auth.resendFailed": "Az e-mail küldése sikertelen.",
  "auth.emailAlreadyRegistered": "Ez az e-mail már regisztrált. Ellenőrizd a postafiókod vagy jelentkezz be.",
  "auth.resending": "Küldés…",
  "auth.resendConfirmation": "Megerősítő e-mail újraküldése",
  "auth.signInDesc": "Jelentkezz be vagy regisztrálj a Cerveza.International oldalra.",
  "auth.pageTitle": "Bejelentkezés — Cerveza.International",
  "authReset.pageTitle": "Jelszó beállítása — Cerveza.International",
  "authReset.title": "Új jelszó beállítása",
  "authReset.needLink": "Nyisd meg ezt az oldalt az e-mailben kapott linkről.",
  "authReset.newPassword": "Új jelszó",
  "authReset.passwordAgain": "Jelszó ismét",
  "authReset.saving": "Mentés…",
  "authReset.save": "Jelszó mentése",
  "authReset.tooShort": "A jelszónak legalább 8 karakteresnek kell lennie.",
  "authReset.mismatch": "A jelszavak nem egyeznek.",
  "authReset.saved": "Jelszó beállítva.",
  "unsub.pageTitle": "Leiratkozás — Cerveza.International",
  "unsub.title": "Leiratkozás",
  "unsub.checking": "Link ellenőrzése…",
  "unsub.confirmPrompt": "Erősítsd meg, hogy le szeretnél iratkozni a Cerveza.International e-mailjeiről.",
  "unsub.confirmBtn": "Leiratkozás megerősítése",
  "unsub.processing": "Leiratkozás…",
  "unsub.done": "Kész. Sikeresen leiratkoztál.",
  "unsub.already": "Ez a cím már le van iratkozva.",
  "unsub.invalid": "A link érvénytelen vagy lejárt.",
  "unsub.errorVerify": "Nem sikerült ellenőrizni a linket.",
  "unsub.errorGeneric": "Valami hiba történt.",
  "unsub.errorNetwork": "Hálózati hiba.",
  "pay.successPageTitle": "Sikeres fizetés — Cerveza.International",
  "pay.successTitle": "Sikeres fizetés",
  "pay.successDesc": "Köszönjük. A rendelésed feldolgozás alatt — a link vagy fotó néhány másodpercen belül aktiválódik a megerősítés után.",
  "pay.backToMy": "Vissza az Üzleteim oldalra",
  "pay.cancelPageTitle": "Fizetés megszakítva — Cerveza.International",
  "pay.cancelTitle": "Fizetés megszakítva",
  "pay.cancelDesc": "Nem történt terhelés. A fizetést az Üzleteim oldalról bármikor megismételheted.",
  "profile.errorTitle": "Hiba",
  "profile.notFound": "Az üzlet nem található",
  "profile.backToMap": "Vissza a térképre",
  "profile.noDescription": "Még nincs leírás. Add hozzá azokon a nyelveken, amelyeken bemutatnád az üzletet.",
  "profile.editDescription": "Leírás szerkesztése",
  "profile.multiLangDesc": "Többnyelvű leírás",
  "profile.maxChars": "Max {n} karakter nyelvenként",
  "profile.descriptionsSaved": "Leírások elmentve",
  "profile.saveFailed": "A mentés sikertelen",
  "profile.save": "Mentés",
  "profile.cancel": "Mégse",
  "profile.linksHeader": "Linkek",
  "profile.viewOnOSM": "Megtekintés az OpenStreetMap-en",
  "profile.descPlaceholder": "Leírás ({lang})",
  "reg.pageTitle": "Üzlet regisztrálása — Cerveza.International",
  "reg.pageDesc": "Regisztráld üzletedet a globális Cerveza.International katalógusban.",
  "reg.maxTwoKinds": "Legfeljebb 2 kategóriát választhatsz.",
  "reg.confirmedKinds": "Megerősített kategóriák: {list}",
  "share.warnFallback": "Figyelem: a sablon nincs lefordítva erre a nyelvre: „{lang}”, a cseh eredetit küldjük.",
  "share.copiedMsg": "Üzenet másolva — illeszd be a kívánt alkalmazásba.",
  "share.copyFailed": "A másolás sikertelen.",
  "share.copiedLink": "Link másolva.",
  "share.copyLink": "Link másolása",
  "share.copyMessage": "Üzenet másolása",
  "my.photoUploadedPending": "Fotó feltöltve, adminisztrátori jóváhagyásra vár.",
  "my.photoPendingApproval": "jóváhagyásra vár",
  "my.rejectReason": "Az elutasítás oka: {reason}",
  "my.rejectedShort": "elutasítva",
  "my.freeOutOf": "{free}/3 ingyenes · következő: {price}",
  "my.photoPack": "Fotó csomag:",
  "my.pieces": "db",
  "majitele.pageTitle": "Vacant tulajdonosok — Cerveza.International",

  // Downloads section
  "downloads.title": "Letöltések",
  "downloads.subtitle": "Kézikönyvek és útmutatók letöltésre.",
  "downloads.vakantOnly": "Csak Vacant tulajdonosoknak",
  "downloads.signInToView": "Jelentkezz be a megtekintéshez",
  "downloads.downloadPdf": "PDF letöltése",
  "downloads.empty": "Még nincsenek elérhető kézikönyvek.",

};
const bg: Dict = {
  "nav.home": "Начало",
  "nav.back": "Назад",
  "nav.katalog": "Каталог",
  "nav.mapa": "Карта",
  "nav.directClub": "Direct Club",
  "nav.mojePodniky": "Моят панел",
  "nav.proMajitele": "За собственици",
  "nav.admin": "Админ",
  "nav.cenik": "Ценоразпис",
  "nav.mujUcet": "Моят акаунт",
  "nav.odhlasit": "Изход",
  "nav.prihlasit": "Вход",
  "nav.vakanty": "Собственици на Vacant",
  "photos.title": "Снимки",
  "photos.add": "Добави снимка",
  "photos.maxSize": "Макс. 3 MB / снимка. Първите 3 снимки са безплатни, следващи: {price} еднократно.",
  "photos.tooLarge": "Снимката надвишава 3 MB.",
  "photos.invalidType": "Файлът трябва да е изображение.",
  "photos.uploadFailed": "Качването е неуспешно: {msg}",
  "photos.freeAdded": "Снимката беше добавена безплатно.",
  "photos.paidPending": "Снимката е създадена, чака плащане.",
  "photos.deleted": "Снимката беше изтрита.",
  "photos.priceLabel": "Цена за всяка допълнителна снимка",
  "home.title.a": "Открийте вкусовете на света на",
  "home.title.b": "едно място",
  "home.subtitle": "От семейни бистра в Прага до скрити барове в Барселона. Глобален пътеводител за любителите на автентичната гастрономия.",
  "home.search.what": "Какво търсите? (напр. Tapas)",
  "home.search.where": "Къде? (напр. София)",
  "home.search.submit": "Търси",
  "cat.restaurace": "Ресторанти",
  "cat.bary": "Барове",
  "cat.kavarny": "Кафенета",
  "cat.cajovny": "Чайни",
  "cat.stanky": "Сергии",
  "cat.jidelny": "Столови",
  "cat.mlecnyBar": "Млечни барове",
  "cat.foodTruck": "Фууд тръкове",
  "cat.rychleObcerstveni": "Фаст фууд",
  "cat.bistra": "Бистра",
  "home.mapaCta": "Разгледай световната карта",
  "home.featured.title": "Препоръчани заведения",
  "home.featured.sub": "Най-високо оценените перли в нашата общност.",
  "home.featured.all": "Виж всички",
  "dc.tag": "Програма за лоялност",
  "dc.title": "Станете член на Direct Club",
  "dc.text": "Печелете бонус точки за всяко посещение, ползвайте ексклузивни отстъпки и открийте специални менюта, достъпни само за членове на мрежата Cerveza.International.",
  "dc.register": "Безплатна регистрация",
  "dc.how": "Как работи?",
  "dc.status": "Статус",
  "dc.platinum": "Platinum Member",
  "dc.cardNo": "Номер на картата",
  "dc.points": "Събрани точки",
  "owner.title": "Имате гастрономическо заведение?",
  "owner.text": "Присъединете се към хиляди заведения и достигнете гости от целия свят. Регистрацията е бърза и безплатна.",
  "owner.cta": "Добавете своето заведение",
  "footer.rights": "Всички права запазени",
  "footer.about": "За нас",
  "footer.terms": "Условия",
  "footer.privacy": "Поверителност",
  "footer.contact": "Контакт",
  "auth.signIn": "Вход",
  "auth.signUp": "Регистрация",
  "auth.tabSignIn": "Вход",
  "auth.tabSignUp": "Създай акаунт",
  "auth.google": "Продължи с Google",
  "auth.apple": "Продължи с Apple",
  "auth.or": "или",
  "auth.email": "Имейл",
  "auth.password": "Парола",
  "auth.passwordMin": "Парола (мин. 6 знака)",
  "auth.name": "Име и фамилия",
  "auth.submitSignIn": "Вход",
  "auth.submitSignInLoading": "Влизане…",
  "auth.submitSignUp": "Създай акаунт",
  "auth.submitSignUpLoading": "Регистриране…",
  "auth.firstAdminNote": "Първият регистриран акаунт автоматично става администратор на системата.",
  "auth.back": "← Обратно към сайта",
  "auth.welcomeBack": "Добре дошли отново!",
  "auth.created": "Акаунтът е създаден.",
  "auth.oauthFailed": "Входът е неуспешен",
  "mapa.tag": "Карта",
  "mapa.loading": "Зареждане на заведения…",
  "mapa.count": "одобрени заведения",
  "mapa.kindHeader": "Тип заведение",
  "mapa.addBusiness": "Добави заведение",
  "mapa.showUsers": "Членове на Direct Club",
  "mapa.showAnon": "Гости",
  "mapa.attribution": "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
  "mapa.partnerFilter": "Direct Club Partner",
  "mapa.results.empty": "No results",
  "mapa.audit.title": "Map audit vs. database",
  "mapa.audit.badge": "Audit",
  "mapa.audit.ok": "All consistent",
  "mapa.audit.mismatch": "Mismatch",
  "mapa.audit.rendered": "Rendered on map (client)",
  "mapa.audit.expected": "Should be shown (DB)",
  "mapa.audit.breweries": "Breweries (sites)",
  "mapa.audit.venues": "Venues (catalog)",
  "mapa.audit.total": "Total",
  "mapa.audit.published": "Published",
  "mapa.audit.publishedGps": "Published + GPS",
  "mapa.audit.unpublished": "Unpublished",
  "mapa.audit.noGps": "Published without GPS",
  "mapa.audit.missingHeader": "Missing from map",
  "mapa.audit.missingNone": "None — all published records with GPS are in the map feed.",
  "mapa.audit.unnamed": "(unnamed)",
  "mapa.audit.rowVenue": "venue",
  "mapa.audit.rowBrewery": "brewery",
  "mapa.audit.showingFirst": "… showing first 200 of {n}",
  "mapa.coverage.title": "Country coverage",
  "mapa.coverage.badge": "Countries",
  "mapa.coverage.published": "Published",
  "mapa.coverage.imported": "Imported, unpublished",
  "mapa.coverage.notImported": "Not imported",
  "mapa.coverage.tabAll": "All",
  "mapa.coverage.tabPublished": "Published",
  "mapa.coverage.tabImported": "Unpublished",
  "mapa.coverage.tabMissing": "Missing",
  "mapa.coverage.thCountry": "Country",
  "mapa.coverage.thPublic": "Publ.",
  "mapa.coverage.thImported": "Imp.",
  "mapa.coverage.thStatus": "Status",
  "mapa.coverage.statusPublished": "Published",
  "mapa.coverage.statusImported": "Unpublished",
  "mapa.coverage.statusMissing": "Not imported",
  "mapa.coverage.empty": "No countries in this category.",
  "mapa.coverage.footer": "Publ. = number of published venues with GPS on the map · Imp. = total catalog records.",
  "mapa.catalog.venue": "Обект от каталога",
  "mapa.catalog.brewery": "Пивоварна от каталога",
  "mapa.catalog.imported": "Импортирано от XLSX каталог Cerveza Data Standard",
  "mapa.catalog.detail": "Детайли",
  "mapa.review.discovery": "Непроверено",
  "mapa.review.pending": "За проверка",
  "mapa.review.verified": "Проверено",
  "mapa.review.rejected": "Отхвърлено",
  "mapa.continent.europe": "Европа",
  "mapa.continent.northAmerica": "Северна Америка",
  "mapa.continent.southAmerica": "Южна Америка",
  "mapa.continent.asia": "Азия",
  "mapa.continent.africa": "Африка",
  "mapa.continent.oceania": "Океания",
  "mapa.continent.other": "Други",
  "mapa.memberBadge": "Член на Direct Club",
  "mapa.partnerHint": "Проверена партньорска фирма, регистрирана в Cerveza.International",
  "mapa.memberPrivacyNote": "Регистриран потребител · приблизително местоположение за поверителност",
  "mapa.anonPrivacyNote": "Анонимен посетител · местоположение видимо 24 ч, след това се премахва",
  "mapa.legend.title": "Легенда",
  "mapa.userPin.title": "Регистриран член на Cerveza.International — Direct Club",
  "mapa.userPin.subtitle": "Приблизително местоположение (без точен адрес)",
  "mapa.viewProfile": "Профил на заведението",
  "mapa.empty.a": "Все още няма одобрени заведения. Бъдете първи —",
  "mapa.empty.b": "регистрирайте заведение",
  "mapa.anonPin.title": "Анонимен посетител",
  "mapa.anonPin.subtitle": "Активен през последните 24 ч.",
  "mapa.partnerBadge": "★ Direct Club Partner",
  "mapa.filters.title": "Филтри",
  "mapa.filters.search": "Търсене",
  "mapa.filters.searchPlaceholder": "Име, град, описание…",
  "mapa.filters.location": "Локация",
  "mapa.filters.continent": "Континент",
  "mapa.filters.allContinents": "Всички континенти",
  "mapa.filters.country": "Държава",
  "mapa.filters.allCountries": "Всички държави",
  "mapa.filters.region": "Регион",
  "mapa.filters.regionEmpty": "Регион (празно)",
  "mapa.filters.allRegions": "Всички региони",
  "mapa.filters.city": "Град",
  "mapa.filters.allCities": "Всички градове",
  "mapa.filters.postal": "Пощенски код",
  "mapa.filters.partnership": "Партньорство",
  "mapa.filters.reset": "Нулиране на филтри",
  "mapa.empty.filtered": "Няма заведение, което да отговаря на филтъра.",
  "mapa.empty.resetFilters": "Нулиране на филтри",
  "mapa.location.missing": "Липсва локация",
  "mapa.location.stale": "Локацията е остаряла",
  "mapa.location.fresh": "Локацията е актуална",
  "mapa.location.refresh": "Обнови локация",
  "mapa.location.lastSeen": "Последно",
  "mapa.location.hidden": "скрита на картата",
  "mapa.location.never": "никога",
  "mapa.location.toastOk": "Локацията е обновена",
  "mapa.location.toastCached": "Използвана свежа кеширана стойност.",
  "mapa.location.toastSource": "Източник",
  "mapa.location.toastFail": "Локацията не може да бъде определена",
  "mapa.location.toastFailDesc": "Опитайте по-късно (geo недостъпно).",
  "mapa.location.toastError": "Грешка при обновяване на локацията",
  "mapa.breweries": "Пивоварни",
  "mapa.breweriesHint": "Пивоварни и производители пива",
  "mapa.showMembersNoBusiness": "Членове без бизнес",
  "mapa.memberNoBusiness.badge": "Член без бизнес",
  "mapa.memberNoBusiness.title": "Член, готов да изгражда мрежа",
  "mapa.memberNoBusiness.subtitle": "Може да препоръчва нови членове, бизнеси и да оценява посетени места.",
  "mapa.memberNoBusiness.recommendBusiness": "Препоръчай бизнес",
  "mapa.memberNoBusiness.inviteMember": "Препоръчай член",
  "mapa.memberNoBusiness.rateVisited": "Оцени посетен бизнес",
  "mapa.memberNoBusiness.privacy": "Приблизително местоположение за защита на личните данни",
  "mapa.memberNoBusiness.hint": "Регистрирани членове, които нямат собствен бизнес — идеални кандидати за препоръки",
  "my.title": "Моите заведения",
  "my.sub": "Управлявайте заведенията и техните връзки.",
  "my.add": "Добави заведение",
  "my.loading": "Зареждане…",
  "my.empty.a": "Все още нямате заведения.",
  "my.empty.b": "Регистрирайте първото",
  "my.links": "Връзки",
  "my.noLinks": "Няма връзки.",
  "my.free": "Безплатно",
  "my.paid": "Платено",
  "my.active": "Активно",
  "my.rejected": "Отхвърлено",
  "my.pendingPayment": "Чака плащане",
  "my.pay": "Плати",
  "my.payTooltip": "Онлайн плащането ще е достъпно скоро",
  "my.addLink": "Добави връзка",
  "my.linkInfo": "Връзки към <strong>directclub.digital</strong> / <strong>direct.club</strong> са безплатни (макс. 3 на заведение). Други външни връзки: <strong>{price}</strong> еднократно (постоянна връзка след плащане).",
  "my.urlPlaceholder": "https://…",
  "my.labelPlaceholder": "Етикет (по избор)",
  "my.addFree": "Добави безплатно",
  "my.addPaid": "Добави за {price}",
  "my.freeAdded": "Безплатната връзка е добавена и публикувана.",
  "my.paidPending": "Връзката е създадена, чака плащане.",
  "my.freeLimit": "Достигнахте лимита от 3 безплатни directclub връзки.",
  "my.willBeFree": "✓ directclub връзка — ще бъде безплатна и публикувана веднага.",
  "my.willBePaid": "Външна връзка — изисква плащане {price} (публикация след плащане).",
  "my.invalidUrl": "Невалиден URL адрес",
  "reg.back": "← Обратно към сайта",
  "reg.moderation": "Хибридна модерация",
  "reg.title": "Регистрация на заведение",
  "reg.desc": "След въвеждане на пощенския код автоматично попълваме града и координатите от базата данни. Верифицираните собственици се одобряват автоматично, останалите записи минават през модерация.",
  "reg.name": "Име на заведението",
  "reg.namePlaceholder": "напр. Café Vinohrady",
  "reg.kind": "Тип заведение",
  "reg.kindPlaceholder": "Изберете тип",
  "reg.country": "Държава",
  "reg.psc": "Пощенски код",
  "reg.pscPlaceholder": "напр. 1000",
  "reg.pscInvalid": "Невалиден формат на пощенски код (CZ: 5 цифри).",
  "reg.searching": "Търсене на град…",
  "reg.notFound": "Не намерихме град за пощенски код {postal}.",
  "reg.foundOne": "Намерен град:",
  "reg.foundMany": "Намерени {count} града — изберете правилния:",
  "reg.coords": "Координати:",
  "reg.mapPreview": "Преглед на местоположението на картата",
  "reg.mapHint": "Плъзнете иглата или кликнете върху картата, за да уточните мястото.",
  "reg.street": "Улица и номер (по избор)",
  "reg.streetPlaceholder": "напр. Korunní 12",
  "reg.descLabel": "Кратко описание (по избор)",
  "reg.descMultiHint": "Можете да попълните описание на един или повече езици. Посетителите го виждат на своя език; ако липсва, се използва друг наличен.",
  "reg.descPlaceholder": "С какво вашето заведение е специално?",
  "reg.cancel": "Отказ",
  "reg.submit": "Изпрати за регистрация",
  "reg.submitted": "Заведението е изпратено за модерация.",
  "reg.notSignedIn": "Не сте влезли в акаунта си",
  "reg.pickCity": "Изберете валиден град от резултатите за пощенски код",
  "reg.invalidData": "Невалидни данни",
  "reg.gate.title": "Потвърждение на собственост на заведението",
  "reg.gate.desc": "Само собственикът или управителят може да регистрира заведение. Изберете 1–2 категории, към които заведението ви принадлежи.",
  "reg.gate.confirmBefore": "Потвърждавам, че съм",
  "reg.gate.confirmStrong": "собственик или управител",
  "reg.gate.confirmAfter": "на заведението, което искам да регистрирам, и то попада в някоя от изброените категории.",
  "reg.gate.kindsLabel": "Категория на заведението (изберете 1–2)",
  "reg.gate.selected": "Избрани: {count} / 2",
  "reg.continue": "Продължи",
  "kind.restaurace": "Ресторант",
  "kind.bar": "Бар",
  "kind.kavarna": "Кафене",
  "kind.cajovna": "Чайна",
  "kind.stanek": "Сергия",
  "kind.jidelna": "Столова",
  "kind.mlecny_bar": "Млечен бар",
  "kind.food_truck": "Фууд трък",
  "kind.rychle_obcerstveni": "Фаст фууд",
  "kind.bistro": "Бистро",
  "status.pending": "Чака одобрение",
  "status.approved": "Одобрено",
  "status.rejected": "Отхвърлено",
  "status.hidden": "Скрито",
  "lang.cs": "Čeština",
  "lang.pl": "Polski",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",
  "lang.pt": "Português",
  "lang.fr": "Français",
  "lang.hr": "Hrvatski",
  "link.promo": "{months} месеца безплатно до {date}",
  "auth.checkEmail": "Моля, проверете имейла си и потвърдете регистрацията, като кликнете върху линка, който изпратихме.",
  "home.latest.title": "Най-нови заведения",
  "home.latest.sub": "Наскоро добавени одобрени заведения.",
  "home.latest.empty": "Все още няма одобрени заведения.",
  "home.latest.viewAll": "Виж на картата →",
  "vakanty.title": "Моят панел",
  "vakanty.welcomeWithName": "Добре дошли, {name}. По-долу виждате броя Vacant в акаунта си.",
  "vakanty.welcomeNoName": "Добре дошли. По-долу виждате броя Vacant в акаунта си.",
  "vakanty.purchasedLabel": "Закупени Vacant",
  "vakanty.emptyHint": "Все още нямате Vacant. Помолете администратора да добави.",
  "vakanty.featuresTitle": "Функции и информация",
  "vakanty.kindFeature": "Функция",
  "vakanty.kindInfo": "Информация",
  "vakanty.open": "Отвори",
 "vakanty.loading": "Зареждане…",
 "vakanty.bp.title": "For Direct s.r.o. — Business Plan",
 "vakanty.bp.subtitle": "Вътрешен документ, достъпен изключително за собствениците на Vacants.",
 "vakanty.bp.strictHeader": "Строго поверително (STRICTLY CONFIDENTIAL)",
 "vakanty.bp.paragraph1": "Този документ е строго поверителен и представлява търговска тайна на For Direct s.r.o.. Документът не може да бъде изтеглян, препращан, копиран, споделян, публикуван или разпространяван по какъвто и да е друг начин на трети страни — включително негови части, екранни снимки, снимки или преписи. Документът е достъпен единствено за онлайн преглед на тази страница и само за влезлия в системата собственик на Vacant. Всяко неоторизирано използване, възпроизвеждане или разпространение представлява нарушение на договорните задължения и може да доведе до гражданска и наказателна отговорност. Нарушението на настоящото споразумение за поверителност се санкционира с договорна неустойка в размер на 5 000 000 EUR (пет милиона евро) за всеки отделен случай на нарушение.",
 "vakanty.bp.paragraph2": "С отмятането на полето по-долу потвърждавате, че сте прочели тези правила, разбирате ги и се задължавате да ги спазвате. Съгласието Ви ще бъде записано с времеви печат.",
 "vakanty.bp.acceptedOn": "NDA прието на {date}.",
 "vakanty.bp.checkboxLabel": "Декларирам, че съм прочел(а) горните условия за поверителност, приемам ги и се задължавам да не споделям и да не разпространявам този документ.",
 "vakanty.bp.acceptButton": "Приемам NDA и преглеждам",
 "vakanty.bp.showButton": "Покажи Business Plan",
 "vakanty.bp.hideButton": "Скрий документа",
 "vakanty.bp.viewerNotice": "Строго поверително — не копирайте, не споделяйте, не изтегляйте. Преглед само на тази страница.",
 "vakanty.bp.mustCheckFirst": "Първо отбележете съгласието с NDA.",
 "vakanty.bp.acceptedToast": "NDA прието. Вече можете да прегледате документа.",
  "common.error": "Грешка",
  "common.tryAgain": "Опитай отново",
  "common.goHome": "Към началната страница",
  "common.pageNotFound": "Страницата не е намерена",
  "common.pageNotFoundDesc": "Страницата, която търсите, не съществува или е преместена.",
  "common.errorTitle": "Тази страница не се зареди",
  "common.errorDesc": "Нещо се обърка. Опитайте да презаредите или се върнете към началото.",
  "auth.enterEmailFirst": "Първо въведете имейл.",
  "auth.resendResult": "Ако акаунтът съществува и не е потвърден, изпратихме нов линк. Оставащи опити: {remaining}.",
  "auth.resendFailed": "Неуспешно изпращане на имейл.",
  "auth.emailAlreadyRegistered": "Този имейл вече е регистриран. Проверете пощата си или влезте.",
  "auth.resending": "Изпращане…",
  "auth.resendConfirmation": "Изпрати отново потвърдителен имейл",
  "auth.signInDesc": "Влезте или се регистрирайте в Cerveza.International.",
  "auth.pageTitle": "Вход — Cerveza.International",
  "authReset.pageTitle": "Задаване на парола — Cerveza.International",
  "authReset.title": "Задаване на нова парола",
  "authReset.needLink": "Отворете тази страница от линка в получения имейл.",
  "authReset.newPassword": "Нова парола",
  "authReset.passwordAgain": "Парола отново",
  "authReset.saving": "Запазване…",
  "authReset.save": "Запази паролата",
  "authReset.tooShort": "Паролата трябва да е поне 8 знака.",
  "authReset.mismatch": "Паролите не съвпадат.",
  "authReset.saved": "Паролата е зададена.",
  "unsub.pageTitle": "Отписване — Cerveza.International",
  "unsub.title": "Отписване от бюлетина",
  "unsub.checking": "Проверка на линка…",
  "unsub.confirmPrompt": "Потвърдете, че искате да се отпишете от имейлите на Cerveza.International.",
  "unsub.confirmBtn": "Потвърди отписването",
  "unsub.processing": "Отписване…",
  "unsub.done": "Готово. Успешно се отписахте.",
  "unsub.already": "Този адрес вече е отписан.",
  "unsub.invalid": "Линкът е невалиден или е изтекъл.",
  "unsub.errorVerify": "Линкът не може да бъде проверен.",
  "unsub.errorGeneric": "Нещо се обърка.",
  "unsub.errorNetwork": "Мрежова грешка.",
  "pay.successPageTitle": "Успешно плащане — Cerveza.International",
  "pay.successTitle": "Плащането беше успешно",
  "pay.successDesc": "Благодарим. Поръчката ви се обработва — линкът или снимката ще се активира секунди след потвърждението.",
  "pay.backToMy": "Обратно към Моите обекти",
  "pay.cancelPageTitle": "Плащането е отменено — Cerveza.International",
  "pay.cancelTitle": "Плащането е отменено",
  "pay.cancelDesc": "Не е удържана сума. Можете да повторите плащането от страницата Моите обекти.",
  "profile.errorTitle": "Грешка",
  "profile.notFound": "Обектът не е намерен",
  "profile.backToMap": "Обратно към картата",
  "profile.noDescription": "Все още няма описание. Добавете го на езиците, на които искате да представите обекта.",
  "profile.editDescription": "Редактирай описанието",
  "profile.multiLangDesc": "Многоезично описание",
  "profile.maxChars": "Макс. {n} знака на език",
  "profile.descriptionsSaved": "Описанията са запазени",
  "profile.saveFailed": "Запазването е неуспешно",
  "profile.save": "Запази",
  "profile.cancel": "Отказ",
  "profile.linksHeader": "Връзки",
  "profile.viewOnOSM": "Виж в OpenStreetMap",
  "profile.descPlaceholder": "Описание ({lang})",
  "reg.pageTitle": "Регистрация на обект — Cerveza.International",
  "reg.pageDesc": "Регистрирайте вашия обект в глобалния каталог Cerveza.International.",
  "reg.maxTwoKinds": "Може да изберете максимум 2 категории.",
  "reg.confirmedKinds": "Потвърдени категории: {list}",
  "share.warnFallback": "Внимание: шаблонът не е преведен на „{lang}“, изпраща се чешкият оригинал.",
  "share.copiedMsg": "Съобщението е копирано — поставете го в желаното приложение.",
  "share.copyFailed": "Копирането е неуспешно.",
  "share.copiedLink": "Линкът е копиран.",
  "share.copyLink": "Копирай линка",
  "share.copyMessage": "Копирай съобщението",
  "my.photoUploadedPending": "Снимката е качена, чака одобрение от админ.",
  "my.photoPendingApproval": "чака одобрение",
  "my.rejectReason": "Причина за отказ: {reason}",
  "my.rejectedShort": "отказано",
  "my.freeOutOf": "{free}/3 безплатни · следваща: {price}",
  "my.photoPack": "Фото пакет:",
  "my.pieces": "бр",
  "majitele.pageTitle": "Собственици на Vacants — Cerveza.International",
  // Downloads section
  "downloads.title": "Изтегляния",
  "downloads.subtitle": "Ръководства и инструкции за изтегляне.",
  "downloads.vakantOnly": "Само за собственици на Vacants",
  "downloads.signInToView": "Влезте, за да видите",
  "downloads.downloadPdf": "Изтегли PDF",
  "downloads.empty": "Все още няма ръководства.",

};

const dicts: Record<Lang, Dict> = { cs, pl, en, es, de, pt, fr, hr, sk, ro, it, hu, bg };

/** Test/introspection helper — read a full dictionary for a language. */
export function getDict(lang: Lang): Dict {
  return dicts[lang];
}

/**
 * Humanize a missing i18n key into a readable fallback so raw dotted keys
 * (e.g. `mapa.legend.title`) never reach the UI. Takes the last dotted
 * segment and converts camelCase / snake_case / kebab-case into a sentence.
 */
export function humanizeKey(key: string): string {
  const last = key.includes(".") ? key.split(".").pop()! : key;
  const spaced = last
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function warnMissingKey(lang: Lang, key: string) {
  if (typeof console === "undefined") return;
  // Dedupe across the session to avoid log spam.
  const g = (typeof globalThis !== "undefined" ? (globalThis as any) : {}) as {
    __i18nMissWarned?: Set<string>;
  };
  const seen = (g.__i18nMissWarned ||= new Set<string>());
  const sig = `${lang}::${key}`;
  if (seen.has(sig)) return;
  seen.add(sig);
  console.warn(`[i18n] missing translation key "${key}" (lang=${lang}) — using humanized fallback`);
}

/** Test/introspection helper — resolve a translation key exactly like the runtime `t()` (with CS fallback), without React context. */
export type I18nSource = "direct" | "fallback-cs" | "missing";
export type I18nUsageEntry = {
  ts: number;
  key: string;
  lang: Lang;
  source: I18nSource;
  value: string;
  via: "context" | "static";
};

const USAGE_LIMIT = 500;
function recordUsage(entry: I18nUsageEntry) {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __i18nUsage?: I18nUsageEntry[] };
  const buf = (w.__i18nUsage ||= []);
  buf.push(entry);
  if (buf.length > USAGE_LIMIT) buf.splice(0, buf.length - USAGE_LIMIT);
  try {
    window.dispatchEvent(new CustomEvent("i18n:usage", { detail: entry }));
  } catch {}
}

export function translateStatic(lang: Lang, key: string): string {
  const direct = dicts[lang]?.[key];
  const fb = dicts.cs[key];
  const resolved = direct ?? fb;
  const source: I18nSource = direct !== undefined ? "direct" : fb !== undefined ? "fallback-cs" : "missing";
  const value = resolved ?? humanizeKey(key);
  if (resolved === undefined) warnMissingKey(lang, key);
  recordUsage({ ts: Date.now(), key, lang, source, value, via: "static" });
  return value;
}


type QaMissEntry = { key: string; lang: Lang; kind: "fallback" | "missing" };
type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  qa: {
    enabled: boolean;
    setEnabled: (v: boolean) => void;
    misses: QaMissEntry[];
    reset: () => void;
  };
};

const I18nCtx = createContext<Ctx | null>(null);

function interpolate(s: string, vars?: Record<string, string | number>) {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

const SUPPORTED: Lang[] = ["cs","pl","en","es","de","pt","fr","hr","sk","ro","it","hu","bg"];
const QA_STORAGE_KEY = "lc_qa";

function detectDeviceLang(): Lang {
  if (typeof navigator === "undefined") return "en";
  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split("-")[0] as Lang;
    if ((SUPPORTED as string[]).includes(base)) return base;
  }
  return "en";
}

async function syncPreferredLanguage(code: string) {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getUser();
    const uid = data.user?.id;
    if (!uid) return;
    await supabase.from("profiles").update({ preferred_language: code }).eq("id", uid);
  } catch {}
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");
  const [qaEnabled, setQaEnabledState] = useState(false);
  const [misses, setMisses] = useState<QaMissEntry[]>([]);
  const missSeenRef = (typeof window !== "undefined") ? ((window as any).__qaSeen ||= new Set<string>()) : new Set<string>();

  useEffect(() => {
    let next: Lang = detectDeviceLang();
    try {
      // New deploy detected → the bundled dictionaries are the source of truth.
      // Drop any i18n-adjacent caches a returning visitor may still hold so the
      // fresh JS shell isn't shadowed by stale copies (extensions, older SW
      // installs, page-cache prefetches, etc.). Keep the user's language pick.
      const storedBuild = localStorage.getItem(BUILD_ID_KEY);
      if (storedBuild !== CURRENT_BUILD_ID) {
        try {
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (k.startsWith("lc_i18n_cache") || k.startsWith("i18n:")) {
              localStorage.removeItem(k);
            }
          }
          if ("caches" in window) {
            void caches.keys().then((names) => {
              names
                .filter((n) => /i18n|translations?|locale/i.test(n))
                .forEach((n) => void caches.delete(n));
            });
          }
        } catch {}
        try { localStorage.setItem(BUILD_ID_KEY, CURRENT_BUILD_ID); } catch {}
      }

      // Returning-visitor preference (localStorage) wins over URL params so
      // that a saved language survives across pages/reloads. `?lang=xx` is
      // only used as a first-visit hint (or when the user has never picked
      // a language) — e.g. hreflang alternates from crawlers/TV catalogs.
      const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
      if (stored && (SUPPORTED as string[]).includes(stored)) {
        next = stored;
      } else {
        const urlLang = new URLSearchParams(window.location.search).get("lang") as Lang | null;
        if (urlLang && (SUPPORTED as string[]).includes(urlLang)) {
          next = urlLang;
          try { localStorage.setItem(STORAGE_KEY, urlLang); } catch {}
        }
      }
    } catch {}
    setLangState(next);
    if (typeof document !== "undefined") document.documentElement.lang = next;
    try {
      const qa = localStorage.getItem(QA_STORAGE_KEY) === "1"
        || new URLSearchParams(window.location.search).get("qa") === "1";
      if (qa) setQaEnabledState(true);
    } catch {}
  }, []);


  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch {}
    if (typeof document !== "undefined") document.documentElement.lang = l;
    // Always reflect the active language in the URL so a shared / copied
    // deep link opens in the same language for the recipient.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("lang") !== l) {
        url.searchParams.set("lang", l);
        window.history.replaceState(window.history.state, "", url.toString());
      }
    } catch {}
    void syncPreferredLanguage(l);
  }, []);


  const setQaEnabled = useCallback((v: boolean) => {
    setQaEnabledState(v);
    try { localStorage.setItem(QA_STORAGE_KEY, v ? "1" : "0"); } catch {}
  }, []);

  const reset = useCallback(() => {
    missSeenRef.clear();
    setMisses([]);
  }, [missSeenRef]);

  // Memoized so `t` reference is stable across renders where `lang` / `qaEnabled`
  // did not change — prevents downstream effects that depend on `t` from re-firing.
  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const direct = dicts[lang][key];
    const fb = dicts.cs[key];
    const resolved = direct ?? fb;
    const source: I18nSource = direct !== undefined ? "direct" : fb !== undefined ? "fallback-cs" : "missing";
    const v = resolved ?? humanizeKey(key);
    if (resolved === undefined) warnMissingKey(lang, key);
    const finalValue = interpolate(v, vars);
    recordUsage({ ts: Date.now(), key, lang, source, value: finalValue, via: "context" });
    if (qaEnabled && lang !== "cs" && direct === undefined) {
      const kind: "fallback" | "missing" = fb === undefined ? "missing" : "fallback";
      const sig = `${lang}::${key}::${kind}`;
      if (!missSeenRef.has(sig)) {
        missSeenRef.add(sig);
        setMisses((m) => [...m, { key, lang, kind }]);
      }
      return `⚑${finalValue}`;
    }
    return finalValue;
  }, [lang, qaEnabled, missSeenRef]);


  const qa = useMemo(
    () => ({ enabled: qaEnabled, setEnabled: setQaEnabled, misses, reset }),
    [qaEnabled, setQaEnabled, misses, reset],
  );

  const value = useMemo<Ctx>(() => ({ lang, setLang, t, qa }), [lang, setLang, t, qa]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}


export function useT() {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error("useT must be used inside LanguageProvider");
  return ctx;
}

export function tKind(t: Ctx["t"], k: BusinessKind) {
  return t(`kind.${k}`);
}
const KIND_PLURAL_KEY: Record<BusinessKind, string> = {
  restaurace: "cat.restaurace",
  bar: "cat.bary",
  kavarna: "cat.kavarny",
  cajovna: "cat.cajovny",
  stanek: "cat.stanky",
  jidelna: "cat.jidelny",
  mlecny_bar: "cat.mlecnyBar",
  food_truck: "cat.foodTruck",
  rychle_obcerstveni: "cat.rychleObcerstveni",
  bistro: "cat.bistra",
};
export function tKindPlural(t: Ctx["t"], k: BusinessKind) {
  return t(KIND_PLURAL_KEY[k]);
}
export function tStatus(t: Ctx["t"], s: BusinessStatus) {
  return t(`status.${s}`);
}

/**
 * Pick a localized description from a per-language map (jsonb on businesses.descriptions).
 * Falls back: current lang → cs → en → first non-empty → fallback string → "".
 */
export function pickDescription(
  lang: Lang,
  descriptions: unknown,
  fallback?: string | null,
): string {
  const map = (descriptions && typeof descriptions === "object" ? (descriptions as Record<string, unknown>) : {}) as Record<string, unknown>;
  const get = (k: string) => {
    const v = map[k];
    return typeof v === "string" && v.trim().length > 0 ? v : null;
  };
  return (
    get(lang) ??
    get("cs") ??
    get("en") ??
    (Object.values(map).find((v) => typeof v === "string" && (v as string).trim().length > 0) as string | undefined) ??
    (fallback && fallback.trim().length > 0 ? fallback : "")
  );
}


export const LANGS: { code: Lang; label: string }[] = [
  { code: "cs", label: "CS" },
  { code: "pl", label: "PL" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "de", label: "DE" },
  { code: "pt", label: "PT" },
  { code: "fr", label: "FR" },
  { code: "hr", label: "HR" },
  { code: "sk", label: "SK" },
  { code: "ro", label: "RO" },
  { code: "it", label: "IT" },
  { code: "hu", label: "HU" },
  { code: "bg", label: "BG" },
];

import {
  DropdownMenu as _DropdownMenu,
  DropdownMenuContent as _DropdownMenuContent,
  DropdownMenuItem as _DropdownMenuItem,
  DropdownMenuTrigger as _DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { lang, setLang } = useT();
  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];
  return (
    <_DropdownMenu modal={false}>
      <_DropdownMenuTrigger
        aria-label="Language"
        className={`relative inline-flex shrink-0 cursor-pointer items-center rounded-full border border-brand-earth/15 bg-brand-cream/60 px-3 py-1 text-xs font-semibold text-brand-earth outline-none focus:ring-2 focus:ring-brand-terracotta/30 ${className}`}
      >
        <span aria-hidden="true">{current.label}</span>
        <svg className="ml-1.5 h-3 w-3 opacity-60" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M3 5l3 3 3-3" />
        </svg>
      </_DropdownMenuTrigger>
      <_DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {LANGS.map((l) => (
          <_DropdownMenuItem
            key={l.code}
            onSelect={() => setLang(l.code as typeof lang)}
            className={l.code === lang ? "font-semibold" : ""}
          >
            {l.label}
          </_DropdownMenuItem>
        ))}
      </_DropdownMenuContent>
    </_DropdownMenu>
  );
}

